import React, { useState, useCallback, useMemo, useEffect, useLayoutEffect, useRef } from 'react';
import clsx from 'clsx';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import DescriptionIcon from '@mui/icons-material/Description';
import IrisFloatingPanel from './components/HelperChat';
// (EmbeddedHelperChat removed — Iris is now a floating panel)

// Material UI Icons for custom nodes
import BuildIcon from '@mui/icons-material/Build';
import SettingsIcon from '@mui/icons-material/Settings';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PaletteIcon from '@mui/icons-material/Palette';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import BarChartIcon from '@mui/icons-material/BarChart';
import SearchIcon from '@mui/icons-material/Search';
import StarIcon from '@mui/icons-material/Star';
import BoltIcon from '@mui/icons-material/Bolt';
import TheaterComedyIcon from '@mui/icons-material/TheaterComedy';
import MovieIcon from '@mui/icons-material/Movie';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import ImageIcon from '@mui/icons-material/Image';
import VideocamIcon from '@mui/icons-material/Videocam';
import PsychologyIcon from '@mui/icons-material/Psychology';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CodeIcon from '@mui/icons-material/Code';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';

import {
  ReactFlow,
  ReactFlowProvider,
  MiniMap,
  Background,
  Panel,
  useNodesState,
  useEdgesState,
  useReactFlow,
  addEdge,
  Node,
  Edge,
  Connection,
  OnConnect,
  NodeTypes,
  EdgeTypes,
  SelectionMode,
  NodeChange,
} from '@xyflow/react';

import '@xyflow/react/dist/style.css';

// Import Universal Node Adapters (new architecture)
import {
  UniversalStartingPromptNode,
  UniversalAgentPromptNode,
  UniversalChatGPTNode,
  UniversalClaudeNode,
  UniversalKimiNode,
  UniversalQwenNode,
  UniversalMinimaxNode,
  UniversalGeminiTextNode,
  UniversalGrokTextNode,
  UniversalImagePromptNode,
  UniversalGPTImageNode,
  UniversalFluxImageNode,
  // fal.ai Image Nodes
  UniversalZImageTurboNode,
  UniversalQwenImageNode,
  UniversalQwenImageEditNode,
  UniversalSeedreamNode,
  UniversalRecraftV3Node,
  UniversalGrokImageNode,
  // Video Nodes
  // fal.ai Video Nodes
  UniversalKlingVideoNode,
  UniversalKling26VideoNode,
  UniversalKling25VideoNode,
  UniversalKlingO3VideoNode,
  UniversalWanVideoNode,
  UniversalWan26VideoNode,
  UniversalWanAnimateVideoNode,
  UniversalLTXVideoNode,
  UniversalLTXFastVideoNode,
  UniversalMinimaxVideoNode,
  UniversalPixVerseVideoNode,
  UniversalHunyuanVideoNode,
  UniversalKlingAvatarNode,
  UniversalVeoVideoNode,
  UniversalGeminiOmniNode,
  UniversalGLMImageNode,
  UniversalKlingImageNode,
  UniversalGrokVideoNode,
  UniversalSeedance2VideoNode,
  UniversalSeedance25VideoNode,
  UniversalMinimaxH3VideoNode,
  UniversalVoiceTTSNode,
  UniversalSoundEffectsNode,
  UniversalMusicNode,
  UniversalHeyGenAvatarV3Node,
  UniversalHeyGenAvatarV4Node,
  UniversalHeyGenAvatarVNode,
  UniversalHeyGenVideoAgentNode,
  UniversalHeyGenTranslatePrecisionNode,
  UniversalHeyGenTranslateSpeedNode,
  UniversalHeyGenPhotoVideoNode,
  UniversalHeyGenLipsyncNode,
  UniversalOutputNode,
  UniversalImagePanelNode,
  UniversalInpaintNode,
  UniversalImageScaleNode,
  UniversalVideoTrimNode,
  UniversalVideoSplitNode,
  UniversalVideoScaleNode,
  UniversalVideoStitchNode,
  UniversalDictationNode,
} from './components/NodeAdapters';

// Import node definitions
import { NODE_DEFINITIONS, OUTPUT_NODE_DEF } from './config/nodeDefinitions';
import { buildAssetStageNodeAt, buildAssetStageGrid, computeAssetPlacementFrame, isNodeMeasured, nodeBox, obstacleBoxes, relayoutAssetBatch, revealShift, type AssetAnchorMode, type FlowPoint } from './utils/assetStageNodes';
import { findCompatibleInputHandle, getNodeByReactFlowType, getNodeRegistration, getInputHandlesForNodeType, resolveTargetHandle, getInitialNodeData, getNodeDimensions, getOutputNodeDimensions, NODE_REGISTRY, isGeneratorNode, isPassiveNode, type NodeRegistration } from './config/nodeRegistry';
import { useSnapGrid } from './utils/canvasSnapGrid';
import { repairNodeDimensionsAll } from './utils/nodeDimensionRepair';
import { migrateLegacyVeoNode } from '../server/config/falModels';
import { createNodeAdapter } from './components/NodeAdapters';

// Import migration utilities
import { getUniversalDefinition, migrateCustomNodesInStorage, convertCustomNodeToUniversal } from './utils/customNodeMigration';

// Import other components
import CustomNodeBuilder from './components/CustomNodeBuilder';
import CustomEdge from './components/CustomEdge';
import FilePanel from './components/FilePanel';
import { NodePalette, NodePaletteRef, PaletteSection } from './components/NodePalette';
import SaveFlowDialog from './components/SaveFlowDialog';
import PublishModal from './components/PublishModal';
import { inspectFlowForPublish, buildAutoAddPatch, type InspectionResult } from './utils/publishSchemaInspector';
import LoadFlowDialog from './components/LoadFlowDialog';
import BrandVoiceManager from './components/BrandVoiceManager';
import SnappingConnectionLine from './components/SnappingConnectionLine';
import { UpdateRequiredModal } from './components/UpdateRequiredModal';
import SendToDeviceModal from './components/ui/SendToDeviceModal';
import Toast, { toastService } from './components/ui/Toast';
import ConfirmDialog, { confirmDialogService } from './components/ui/ConfirmDialog';
import PromptLimitModal, { promptLimitService } from './components/ui/PromptLimitModal';
import type { PromptLimitCheckOptions } from './components/ui/PromptLimitModal';
import { alertService } from './components/Alert';
import { useAutoSave } from './hooks/useAutoSave';
import { useAddNodeWithAnimation } from './hooks/useAddNodeWithAnimation';
import { useFlowHistory } from './hooks/useFlowHistory';
import { recordUserAction, releaseUserAction } from './services/FlowHistoryRef';
import { generationLifecycleService } from './services/GenerationLifecycleService';
import { executionController } from './services/ExecutionController';
import { stripRunState } from './utils/nodeClone';
import { isJobPolling } from './utils/jobState';
import { getHistoryShortcut } from './utils/flowHistory';
import { FlowSaveSessions } from './utils/flowSaveSessions';
import { trackFlowSave } from './services/pendingFlowSaves';
import { useIsMobile } from './hooks/useIsMobile';
import { AutoSaveProvider } from './contexts/AutoSaveContext';
import { useJobResumption } from './hooks/useJobResumption';
import { useChunkLoadingError } from './hooks/useChunkLoadingError';
import { useMediaPolling } from './hooks/useMediaPolling';
import { friendlyErrors } from './utils/friendlyErrorMessages';
import { reportFlowLimitRefusal } from './utils/flowLimit';
import { useCredits } from './contexts/CreditsContext';
import { useAuth } from './contexts/AuthContext';
import { getApiBase } from './config/api';
import { UI_DIMENSIONS } from './constants/app';
import logger from './utils/logger';
import * as promptTemplateUtils from './utils/promptTemplateUtils';
import { getTotalCreditCost } from './utils/selectionCostUtils';
import fileStorageService from './services/FileStorageService';
import { registerFlowStore, unregisterFlowStore } from './services/FlowStoreRef';
import flowSerializationService from './services/FlowSerializationService';
import supabaseFlowStorageService from './services/SupabaseFlowStorageService';
import brandVoiceStorageService from './services/BrandVoiceStorageService';
import CustomNodeStorage from './services/CustomNodeStorage';
import settingsService from './services/SettingsService';
import knodExportService from './services/knodExportService';
import irisService from './services/IrisService';

// Flow storage service - uses Supabase for authenticated users
const flowStorageService = supabaseFlowStorageService;

// Import types
import type { FileContext, OutputData, ConversationContext } from './types/api';
import type { ConnectorType } from './types/nodeConfig';
import { EDGE_COLOR_MAP } from './types/nodeConfig';
import { ExecutionProvider, useExecution } from './contexts/ExecutionContext';
import { TutorialProvider, useTutorial } from './contexts/TutorialContext';
import { ExecutionProgress } from './components/ui/ExecutionProgress';
import { FloatingToolbarLeft } from './components/ui/FloatingToolbarLeft';
import { FloatingToolbarCenter } from './components/ui/FloatingToolbarCenter';
import { FloatingToolbarRight } from './components/ui/FloatingToolbarRight';
import { TutorialOverlay } from './components/tutorial';
import type { CustomNodeDefinition } from './types/customNode';

// Icon mapping for custom nodes
// @ts-expect-error TS6133 - Kept for future custom node icon support
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _CUSTOM_NODE_ICON_MAP: Record<string, React.ComponentType<any>> = {
  'Build': BuildIcon,
  'Settings': SettingsIcon,
  'AutoAwesome': AutoAwesomeIcon,
  'Palette': PaletteIcon,
  'Description': DescriptionIcon,
  'Lightbulb': LightbulbIcon,
  'RocketLaunch': RocketLaunchIcon,
  'GpsFixed': GpsFixedIcon,
  'BarChart': BarChartIcon,
  'Search': SearchIcon,
  'Star': StarIcon,
  'Bolt': BoltIcon,
  'TheaterComedy': TheaterComedyIcon,
  'Movie': MovieIcon,
  'CameraAlt': CameraAltIcon,
  'Image': ImageIcon,
  'Videocam': VideocamIcon,
  'Psychology': PsychologyIcon,
  'SmartToy': SmartToyIcon,
  'Code': CodeIcon
};

// ============================================================================
// Type Definitions
// ============================================================================

interface ContextMenuState {
  x: number;
  y: number;
  flowX: number;
  flowY: number;
  mode?: 'canvas' | 'multi-select' | 'node';
  selectedNodeIds?: string[];
  nodeId?: string;
  handleType?: 'source' | 'target';
  handleId?: string;
}

type SubmenuKey = 'text' | 'image' | 'video' | 'audio' | 'utility' | 'switch' | null;

interface HandleContextState {
  nodeId: string;
  handleType: 'source' | 'target';
  handleId: string;
  isSource: boolean;
}

type CustomNodeType = 'startingPrompt' | 'agentPrompt' | 'documentPanel' | 'imagePrompt' | 'gptImage' | 'fluxImage' | 'customOutput' | 'imagePanel' | 'inpaint' | 'klingVideo' | 'kling26Video' | 'kling25Video' | 'klingO3Video' | 'wanVideo' | 'wan26Video' | 'wanAnimateVideo' | 'ltxVideo' | 'ltxFastVideo' | 'minimaxVideo' | 'pixverseVideo' | 'hunyuanVideo' | 'klingAvatar' | 'veoVideo' | 'grokImage' | 'grokVideo' | 'seedance2Video' | 'seedance25Video' | 'minimaxH3Video' | 'heyGenAvatarV3' | 'heyGenAvatarV4' | 'heyGenVideoAgent' | 'heyGenTranslatePrecision' | 'heyGenTranslateSpeed' | 'heyGenPhotoVideo';

type InputHandlerCallback = (data: { content: string; context?: ConversationContext; type: 'text' | 'image' | 'video'; nodeId?: string; videoUrl?: string }) => void;

// ============================================================================
// Node Type Migration
// ============================================================================
// Maps legacy node types to their current equivalents
const LEGACY_NODE_TYPE_MAP: Record<string, string> = {
  // 'klingVideo' was previously mapped to 'kling26Video' as an old legacy alias
  // for a removed unified Kling node. As of 2026-06 we have re-introduced a
  // unified Kling node under the same 'klingVideo' type, so this mapping is now
  // an identity and intentionally omitted (no-op).
  'veo3FalAI': 'veoVideo',       // Renamed misnomer (routes Google, not fal.ai) → Veo 3.1
  'veo3Video': 'veoVideo',       // Legacy VEO 3 name → Veo 3.1
  'veo31Video': 'veoVideo',      // Removed Google-direct VEO 3.1 → Veo 3.1
  'videoPrompt': 'veoVideo',     // Removed Google-direct video node → Veo 3.1
  'output': 'customOutput',      // Legacy output
  'universalOutput': 'customOutput', // Alias
};

// Monotonic counter to ensure unique node IDs even when Date.now() returns the same ms
let _nodeIdCounter = 0;
function uniqueNodeId(prefix: string): string {
  return `${prefix}-${Date.now()}-${_nodeIdCounter++}`;
}

/**
 * Migrate legacy node types to current types and reset transient states
 * @param nodes - Array of nodes that may have legacy types
 * @returns Nodes with migrated types and cleaned transient states
 */
function migrateNodeTypes(nodes: Node[]): Node[] {
  let migrated = false;
  const result = nodes.map(original => {
    // Legacy veo3FalAI nodes pinning fal's retired bare 'veo-3.1-fast' /
    // 'veo-3.1' become the Google ids (and Google-valid settings) BEFORE the
    // invalid-model repair below, which would otherwise reset 'veo-3.1'
    // (Standard) to the definition default (Fast). Rule: server/config/falModels.ts.
    const node = migrateLegacyVeoNode(original);
    if (node !== original) {
      migrated = true;
      console.log(`[Migration] Node ${node.id}: legacy Veo model "${(original.data as any)?.selectedModel ?? (original.data as any)?.parameters?.selectedModel}" → "${(node.data as any).selectedModel}"`);
    }
    const newType = LEGACY_NODE_TYPE_MAP[node.type || ''];
    let updatedNode = node;
    
    if (newType) {
      migrated = true;
      console.log(`[Migration] Node ${node.id}: ${node.type} → ${newType}`);
      updatedNode = { ...node, type: newType };
    }
    
    // Reset transient processing states that should not persist across saves/loads
    const nodeData = updatedNode.data as Record<string, unknown>;
    let needsDataUpdate = !!(nodeData && (nodeData.streamingInProgress || nodeData.isProcessing));
    const dataUpdates: Record<string, unknown> = {};

    if (nodeData?.streamingInProgress) dataUpdates.streamingInProgress = false;
    if (nodeData?.isProcessing) dataUpdates.isProcessing = false;

    // Fix corrupted selectedModel: validate it belongs to this node type's allowed models
    if (nodeData?.selectedModel && updatedNode.type) {
      const reg = getNodeRegistration(updatedNode.type) || getNodeByReactFlowType(updatedNode.type);
      const definition = reg?.definition;
      const modelSetting = definition?.settings?.find((s: any) => s.id === 'selectedModel');
      if (modelSetting) {
        const validModels = [
          ...(modelSetting.options || []).map((o: any) => o.value),
          ...(modelSetting.models || []).map((m: any) => m.id),
        ];
        if (validModels.length > 0 && !validModels.includes(nodeData.selectedModel as string)) {
          const correctDefault = modelSetting.default || reg?.initialData?.selectedModel || validModels[0];
          console.log(`[Migration] Node ${updatedNode.id} (${updatedNode.type}): invalid model "${nodeData.selectedModel}" → "${correctDefault}"`);
          dataUpdates.selectedModel = correctDefault;
          needsDataUpdate = true;
          migrated = true;
        }
      }
    }

    if (needsDataUpdate) {
      return {
        ...updatedNode,
        data: { ...nodeData, ...dataUpdates }
      };
    }

    return updatedNode;
  });
  if (migrated) {
    console.log('[Migration] Legacy node types migrated');
  }

  // Deduplicate nodes with the same ID (keeps first occurrence)
  const seen = new Set<string>();
  const deduped = result.filter(node => {
    if (seen.has(node.id)) {
      console.log(`[Migration] Removing duplicate node: ${node.id}`);
      return false;
    }
    seen.add(node.id);
    return true;
  });

  // Backstop: give every node a width/height before it reaches React Flow, and
  // undo a width that is a baked-in screen measurement rather than a resize.
  // Runs LAST so legacy types have already collapsed onto their current names
  // and resolve to the right registry default. See utils/nodeDimensionRepair.ts
  // for the rule and the thresholds.
  return repairNodeDimensionsAll(deduped);
}

/**
 * Edge color map for type-based styling
 */
// EDGE_COLOR_MAP is imported from ./types/nodeConfig (single source of truth).

/**
 * Migrate edges to ensure they have correct type-based colors
 * Fixes edges that were created before color styling was added
 * @param edges - Array of edges that may have missing/incorrect colors
 * @param nodes - Array of nodes to determine edge types from source nodes
 * @returns Edges with correct type and style
 */
function migrateEdgeColors(edges: Edge[], nodes: Node[]): Edge[] {
  // Map node types to their output connector types
  const nodeOutputTypeMap: Record<string, string> = {
    startingPrompt: 'text',
    agentPrompt: 'text',
    imagePrompt: 'image',
    gptImage: 'image',
    fluxImage: 'image',
    zImageTurbo: 'image',
    qwenImage: 'image',
    qwenImageEdit: 'image',
    seedream: 'image',
    recraftV3: 'image',
    grokImage: 'image',
    glmImage: 'image',
    klingImage: 'image',
    klingVideo: 'video',
    kling26Video: 'video',
    kling25Video: 'video',
    klingO3Video: 'video',
    wanVideo: 'video',
    wan26Video: 'video',
    wanAnimateVideo: 'video',
    ltxVideo: 'video',
    ltxFastVideo: 'video',
    minimaxVideo: 'video',
    pixverseVideo: 'video',
    hunyuanVideo: 'video',
    klingAvatar: 'video',
    veoVideo: 'video',
    geminiOmni: 'video',
    grokVideo: 'video',
    seedance2Video: 'video',
    seedance25Video: 'video',
    minimaxH3Video: 'video',
    heyGenAvatarV3: 'video',
    heyGenAvatarV4: 'video',
    heyGenVideoAgent: 'video',
    heyGenTranslatePrecision: 'video',
    heyGenTranslateSpeed: 'video',
    heyGenPhotoVideo: 'video',
    imagePanel: 'image',
    inpaint: 'image',
    imageScale: 'image',
    documentPanel: 'text',
    dictation: 'text',
    grokText: 'text',
  };

  let migrated = false;

  // STEP 1 — reconcile each saved targetHandle against the handles its target
  // node renders TODAY. This has to happen BEFORE any existence check: a flow
  // saved while a node advertised per-type handles stores e.g. "input-text",
  // and a bare existence filter would delete that edge rather than re-point it
  // to the node's current handle. Order matters — reversed, loading an old flow
  // silently drops the user's connections.
  const resolvedEdges: Array<{ edge: Edge; previousHandle: string | null | undefined }> = [];
  for (const edge of edges) {
    const targetNode = nodes.find(n => n.id === edge.target);
    if (!targetNode) {
      resolvedEdges.push({ edge, previousHandle: edge.targetHandle }); // can't validate, keep it
      continue;
    }

    const { keep, targetHandle } = resolveTargetHandle(
      targetNode.type || '',
      targetNode.data,
      edge.targetHandle,
    );

    if (!keep) {
      console.log(`[Migration] Removing edge ${edge.id}: target handle "${edge.targetHandle}" has no counterpart on ${targetNode.type}`);
      migrated = true;
      continue;
    }

    if (targetHandle !== (edge.targetHandle ?? null)) {
      console.log(`[Migration] Re-pointing edge ${edge.id}: "${edge.targetHandle}" → ${targetHandle ?? 'default'} on ${targetNode.type}`);
      migrated = true;
      resolvedEdges.push({ edge: { ...edge, targetHandle }, previousHandle: edge.targetHandle });
    } else {
      resolvedEdges.push({ edge, previousHandle: edge.targetHandle });
    }
  }

  // STEP 2 — deduplicate on the RESOLVED handle. Re-pointing can collapse two
  // edges onto the same anchor (e.g. a node leaving first-last-frame mode).
  const connectionSeen = new Set<string>();
  const dedupedEdges = resolvedEdges.filter(({ edge }) => {
    const connectionKey = `${edge.source}→${edge.target}→${edge.targetHandle ?? 'default'}`;
    if (connectionSeen.has(connectionKey)) {
      console.log(`[Migration] Removing duplicate edge: ${edge.id} (${connectionKey})`);
      migrated = true;
      return false; // drop true duplicate
    }
    connectionSeen.add(connectionKey);
    return true;
  });

  // STEP 3 — ids, types and colors.
  const result = dedupedEdges.map(({ edge, previousHandle }) => {
    // Fix edge IDs that don't include handle info (prevents future duplicates).
    // Also covers ids that still carry the pre-resolution handle.
    const expectedId = edge.targetHandle
      ? `${edge.source}-${edge.target}-${edge.targetHandle}`
      : `${edge.source}-${edge.target}`;
    const derivedIds = new Set([
      `${edge.source}-${edge.target}`,
      ...(previousHandle ? [`${edge.source}-${edge.target}-${previousHandle}`] : []),
    ]);
    const nextId = edge.id !== expectedId && derivedIds.has(edge.id) ? expectedId : edge.id;

    // Find source node to determine edge type
    const sourceNode = nodes.find(n => n.id === edge.source);
    let edgeType = edge.type || 'any';
    
    if (sourceNode) {
      const nodeType = sourceNode.type || '';
      // For output nodes, check their content type
      if (nodeType === 'customOutput' || nodeType === 'universalOutput') {
        const contentType = (sourceNode.data as any)?.type;
        if (contentType && ['text', 'image', 'video'].includes(contentType)) {
          edgeType = contentType;
        } else {
          edgeType = 'any';
        }
      } else {
        edgeType = nodeOutputTypeMap[nodeType] || 'any';
      }
    }

    const edgeColor = EDGE_COLOR_MAP[edgeType] || EDGE_COLOR_MAP['any'];
    const currentStroke = (edge.style as React.CSSProperties)?.stroke;
    const currentStrokeWidth = (edge.style as React.CSSProperties)?.strokeWidth;
    const typeChanged = edge.type !== edgeType;
    const colorChanged = currentStroke !== edgeColor;
    const widthChanged = currentStrokeWidth !== 3;
    const idChanged = nextId !== edge.id;

    if (!typeChanged && !colorChanged && !widthChanged && !idChanged) {
      return edge;
    }

    migrated = true;

    return {
      ...edge,
      id: nextId,
      type: edgeType,
      style: { ...edge.style, stroke: edgeColor, strokeWidth: 3 }
    };
  });

  if (migrated) {
    console.log('[Migration] Edge colors migrated');
  }
  return result;
}

// ============================================================================
// Initial State
// ============================================================================

// Start with a Starting Prompt node for new users
const initialNodes: Node[] = [];

const initialEdges: Edge[] = [];

// ============================================================================
// Main Component
// ============================================================================

// Define node and edge types outside component to prevent recreation on hot reload
const edgeTypes: EdgeTypes = {
  default: CustomEdge,
  image: CustomEdge,
  text: CustomEdge,
  video: CustomEdge,
  any: CustomEdge,
};

// Base node types defined at module level for stable identity
const baseNodeTypes: NodeTypes = {
  startingPrompt: UniversalStartingPromptNode,
  agentPrompt: UniversalAgentPromptNode,
  chatGPT: UniversalChatGPTNode,
  claude: UniversalClaudeNode,
  kimi: UniversalKimiNode,
  qwen: UniversalQwenNode,
  minimax: UniversalMinimaxNode,
  geminiText: UniversalGeminiTextNode,
  grokText: UniversalGrokTextNode,
  imagePrompt: UniversalImagePromptNode,
  fluxImage: UniversalFluxImageNode,
  gptImage: UniversalGPTImageNode,
  zImageTurbo: UniversalZImageTurboNode,
  qwenImage: UniversalQwenImageNode,
  qwenImageEdit: UniversalQwenImageEditNode,
  seedream: UniversalSeedreamNode,
  recraftV3: UniversalRecraftV3Node,
  grokImage: UniversalGrokImageNode,
  glmImage: UniversalGLMImageNode,
  klingImage: UniversalKlingImageNode,
  klingVideo: UniversalKlingVideoNode,
  kling26Video: UniversalKling26VideoNode,
  kling25Video: UniversalKling25VideoNode,
  klingO3Video: UniversalKlingO3VideoNode,
  wanVideo: UniversalWanVideoNode,
  wan26Video: UniversalWan26VideoNode,
  wanAnimateVideo: UniversalWanAnimateVideoNode,
  ltxVideo: UniversalLTXVideoNode,
  ltxFastVideo: UniversalLTXFastVideoNode,
  minimaxVideo: UniversalMinimaxVideoNode,
  pixverseVideo: UniversalPixVerseVideoNode,
  hunyuanVideo: UniversalHunyuanVideoNode,
  klingAvatar: UniversalKlingAvatarNode,
  veoVideo: UniversalVeoVideoNode,
  geminiOmni: UniversalGeminiOmniNode,
  grokVideo: UniversalGrokVideoNode,
  seedance2Video: UniversalSeedance2VideoNode,
  seedance25Video: UniversalSeedance25VideoNode,
  minimaxH3Video: UniversalMinimaxH3VideoNode,
  voiceTts: UniversalVoiceTTSNode,
  soundEffects: UniversalSoundEffectsNode,
  music: UniversalMusicNode,
  heyGenAvatarV3: UniversalHeyGenAvatarV3Node,
  heyGenAvatarV4: UniversalHeyGenAvatarV4Node,
  heyGenAvatarV: UniversalHeyGenAvatarVNode,
  heyGenVideoAgent: UniversalHeyGenVideoAgentNode,
  heyGenTranslatePrecision: UniversalHeyGenTranslatePrecisionNode,
  heyGenTranslateSpeed: UniversalHeyGenTranslateSpeedNode,
  heyGenPhotoVideo: UniversalHeyGenPhotoVideoNode,
  heyGenLipsync: UniversalHeyGenLipsyncNode,
  customOutput: UniversalOutputNode,
  universalOutput: UniversalOutputNode,
  imagePanel: UniversalImagePanelNode,
  inpaint: UniversalInpaintNode,
  imageScale: UniversalImageScaleNode,
  videoTrim: UniversalVideoTrimNode,
  videoSplit: UniversalVideoSplitNode,
  videoScale: UniversalVideoScaleNode,
  videoStitch: UniversalVideoStitchNode,
  documentPanel: createNodeAdapter('DocumentPanel'),
  dictation: UniversalDictationNode,
};

// Module-level flag: prevents the /flow creation path from firing twice when React
// Strict Mode double-invokes effects. Both runs call saveFlow before their own
// `cancelled` flag can stop them. This mutex is set before the async call and cleared
// when the component receives a real flowId (meaning navigation succeeded).
let _newFlowCreationInFlight = false;

function CreativeNodeFlowInner() {
  // URL routing hooks
  const { flowId: urlFlowId } = useParams<{ flowId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Query params
  const templateId = searchParams.get('template');
  const tutorialId = searchParams.get('tutorial');
  
  // Execution state
  const { runFullFlow, runSelectedNodes, cancel: cancelExecution, isExecuting } = useExecution();
  
  // Tutorial state
  const { startTutorial, completeAction, currentStep: tutorialStep } = useTutorial();
  // Ref so stale closures (handleNodePaletteSelect) always call the latest completeAction
  const completeActionRef = useRef(completeAction);
  completeActionRef.current = completeAction;

  // Track previous node count to detect new nodes (used to advance tutorial step 7 → 8)
  const prevNodeCountRef = useRef<number | null>(null);

  // Mobile detection
  const isMobile = useIsMobile();
  const [minimapExpanded, setMinimapExpanded] = useState(false);
  // Snap-to-grid preference (toggled/sized from the user-profile menu). Stable
  // tuple via useMemo — a new array each render would churn the canvas.
  const { enabled: snapToGridEnabled, size: snapGridSize } = useSnapGrid();
  const snapGrid = useMemo<[number, number]>(() => [snapGridSize, snapGridSize], [snapGridSize]);
  
  // Chunk loading error detection (for stale deployments)
  const { updateRequired } = useChunkLoadingError();
  
  const [nodes, setNodes, onNodesChangeInternal] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChangeInternal] = useEdgesState(initialEdges);

  // Mark the flow dirty from anywhere in this component.
  //
  // `onNodesChange`/`onEdgesChange` are the ONLY hooks React Flow calls for canvas
  // interactions, and every persistence trigger (interval, visibilitychange,
  // beforeunload) gates on `isDirty`. Any code path that mutates the graph with a
  // direct `setNodes`/`setEdges` therefore bypasses auto-save entirely — that is how
  // a node dropped on the canvas could vanish on reload until an unrelated drag
  // happened to mark the flow dirty. Every such path must call this.
  //
  // It goes through a ref because `autoSave` is created further down (it depends on
  // handlers defined below); callbacks declared above it would hit a TDZ error if
  // they closed over `autoSave` directly or listed it as a dependency.
  const markDirtyRef = useRef<() => void>(() => {});
  const markFlowDirty = useCallback(() => {
    markDirtyRef.current();
  }, []);

  // Advance tutorial step 7 → 8 when a new node appears (output node created after generation)
  useEffect(() => {
    if (prevNodeCountRef.current !== null && nodes.length > prevNodeCountRef.current) {
      completeActionRef.current('generate-complete');
    }
    prevNodeCountRef.current = nodes.length;
  }, [nodes.length]);
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const [activeSubmenu, setActiveSubmenu] = useState<SubmenuKey>(null);
  const [customNodeDefinitions, setCustomNodeDefinitions] = useState<CustomNodeDefinition[]>([]);
  const [customNodeBuilderOpen, setCustomNodeBuilderOpen] = useState(false);
  const [editingCustomNode, setEditingCustomNode] = useState<CustomNodeDefinition | undefined>(undefined);
  // Edges are part of the cost input: ElevenLabs TTS is billed per character of
  // its CONNECTED text, so a Voice node fed by a text node costs far more than
  // its own (empty) prompt suggests.
  const totalFlowCredits = useMemo(() => getTotalCreditCost(nodes, { edges, nodes }), [nodes, edges]);

  // Count selected edges (for mobile action bar)
  const selectedEdgeCount = useMemo(() => {
    return edges.filter(e => e.selected).length;
  }, [edges]);

  // Copy selected node (mobile action bar — single node only)
  const handleCopySelectedNode = useCallback(() => {
    const selected = nodes.filter(n => n.selected);
    if (selected.length !== 1) return;
    const node = selected[0];
    const newId = `${node.type}_${Date.now()}`;
    const clone = {
      ...node,
      id: newId,
      position: { x: node.position.x + 40, y: node.position.y + 40 },
      selected: false,
      // A copy is a new node: it inherits no run state (spinner, job id, error).
      data: stripRunState(node.data as Record<string, unknown>),
    };
    recordUserAction('Duplicate node');
    setNodes(prev => [...prev, clone]);
    markFlowDirty(); // direct setNodes bypasses onNodesChange
  }, [nodes, setNodes, markFlowDirty]);

  // Deselect all nodes and edges (mobile action bar)
  const handleDeselectAll = useCallback(() => {
    setNodes(prev => prev.map(n => n.selected ? { ...n, selected: false } : n));
    setEdges(prev => prev.map(e => e.selected ? { ...e, selected: false } : e));
  }, [setNodes, setEdges]);

  // Dynamic nodeTypes with custom nodes registered
  const nodeTypes = useMemo(() => {
    if (customNodeDefinitions.length === 0) return baseNodeTypes;

    // Add custom node types dynamically
    const customTypes = customNodeDefinitions.reduce((acc, def) => {
      // Check if migrated to UniversalNode
      let universalDef = getUniversalDefinition(def);
      
      // If not migrated, convert it now (but don't save to storage yet)
      if (!universalDef) {
        logger.warn('[CreativeNodeFlow] Custom node not migrated, converting for registration:', def.name);
        universalDef = convertCustomNodeToUniversal(def);
      }
      
      if (universalDef) {
        // Register the definition in NODE_DEFINITIONS
        const nodeTypeKey = `Custom_${def.id}`;
        NODE_DEFINITIONS[nodeTypeKey] = universalDef;
        
        // Use createNodeAdapter for UniversalNode
        // Note: def.id already contains 'custom-' prefix, so we use it directly
        acc[def.id] = createNodeAdapter(nodeTypeKey);
        
        logger.debug('[CreativeNodeFlow] Registered custom node:', {
          id: def.id,
          name: def.name,
          type: def.id, // def.id already contains 'custom-' prefix
          migrated: !!getUniversalDefinition(def) // Check if originally migrated
        });
      }
      
      return acc;
    }, {} as NodeTypes);

    logger.info('[CreativeNodeFlow] NodeTypes registered:', {
      baseTypes: Object.keys(baseNodeTypes).length,
      customTypes: Object.keys(customTypes).length,
      total: Object.keys({ ...baseNodeTypes, ...customTypes }).length
    });

    return { ...baseNodeTypes, ...customTypes };
  }, [customNodeDefinitions]);
  
  const [handleContext, setHandleContext] = useState<HandleContextState | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [, setSelectedFileContexts] = useState<FileContext[]>([]);
  
  // Save/Load Flow state
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveDialogAsTemplate, setSaveDialogAsTemplate] = useState(false);
  const [loadDialogOpen, setLoadDialogOpen] = useState(false);
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [brandVoiceManagerOpen, setBrandVoiceManagerOpen] = useState(false);
  const [irisOpen, setIrisOpen] = useState(false);
  const [irisMinimized, setIrisMinimized] = useState(false);

  // Register Iris service so any component can call irisService.open()
  useEffect(() => {
    irisService.register(() => { setIrisOpen(true); setIrisMinimized(false); });
  }, []);
  const [sendToDeviceAsset, setSendToDeviceAsset] = useState<{ id: string; name: string; type: string; size: number; url: string; previewUrl?: string } | null>(null);
  
  // Welcome popup removed
  
  const [currentFlowId, setCurrentFlowId] = useState<string | null>(null);
  const [currentFlowName, setCurrentFlowName] = useState<string>('');
  const currentFlowNameRef = useRef<string>(''); // Ref for auto-save to access latest value
  const [currentFlowDescription, setCurrentFlowDescription] = useState<string>('');
  const [currentFlowTags, setCurrentFlowTags] = useState<string[]>([]);
  const [currentFlowThumbnail, setCurrentFlowThumbnail] = useState<string | undefined>();
  const [currentTemplateId, setCurrentTemplateId] = useState<string | null>(null);
  const [currentTemplateCategory, setCurrentTemplateCategory] = useState<string | undefined>();
  const [currentBrandVoiceId, setCurrentBrandVoiceId] = useState<string | undefined>();
  // Project association for the open knod. Round-tripped on load/save so
  // editing a knod never strips its project (reverts to General/Inbox).
  const [currentFlowProjectId, setCurrentFlowProjectId] = useState<string | null>(null);
  const [, setCurrentBrandVoiceName] = useState<string>('');
  const [, setCurrentBrandVoiceColor] = useState<string>('#3b82f6');
  const [autoSaveEnabled] = useState(() => {
    const enabled = settingsService.getAutoSaveEnabled();
    console.log('[CreativeNodeFlow] Auto-save enabled from settings:', enabled);
    return enabled;
  });
  const [pendingViewport, setPendingViewport] = useState<{ x: number; y: number; zoom: number } | null>(null);

  // Canvas undo/redo. User-action entry points mark a step with
  // recordUserAction() (FlowHistoryRef) BEFORE they mutate; generations never
  // do. Undo is a three-way merge that keeps anything changed since the action
  // (generation results included) — see src/utils/flowHistory.ts.
  const flowHistory = useFlowHistory({
    nodes,
    edges,
    setNodes,
    setEdges,
    markDirty: markFlowDirty,
    resetKey: currentFlowId,
    isGenerator: isGeneratorNode,
    // Undo/redo never removes a node whose generation is in flight.
    isRunning: (id) => {
      if (generationLifecycleService.getRun(id)) return true;
      if (isJobPolling(id)) return true; // queued jobs polled after the handler returned (Wan Animate, resumed Veo)
      const state = executionController.getState();
      const status = state.isExecuting ? state.nodeStates.get(id)?.status : undefined;
      return status === 'running' || status === 'pending';
    },
    onApplied: (direction, entry) => {
      toastService.info(`${direction === 'undo' ? 'Undone' : 'Redone'}: ${entry.label}`, 1500);
    },
    // A step that would remove a node mid-generation is not skipped: say so.
    onBlocked: (direction) => {
      toastService.warning(`Can't ${direction} while a node is generating`, 2500);
    },
  });

  // Toast and Confirm Dialog state
  const [toastData, setToastData] = useState<{ message: string; type: 'info' | 'success' | 'warning' | 'error'; duration?: number; persona?: 'default' | 'iris'; speakerName?: string } | null>(null);
  const [confirmData, setConfirmData] = useState<{ title?: string; message: string; confirmText?: string; cancelText?: string; type?: 'warning' | 'danger' | 'info'; callback: (confirmed: boolean) => void } | null>(null);
  const [promptLimitData, setPromptLimitData] = useState<(PromptLimitCheckOptions & { callback: (result: any) => void }) | null>(null);

  // Generate date-based flow name for new flows
  const generateFlowName = useCallback(() => {
    const now = new Date();
    const date = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const time = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    return `Knod - ${date} at ${time}`;
  }, []);

  // ── Flow-limit refusals ───────────────────────────────────────────
  // Every save — first autosave on an unsaved canvas, the /flow bootstrap,
  // quick save, the save dialog — passes through the same BEFORE INSERT trigger
  // and can be turned down by the plan's knod cap.
  //
  // Saving an existing knod is NOT exempt by virtue of being an update. The
  // client upserts (`INSERT ... ON CONFLICT (id) DO UPDATE`, see
  // SupabaseFlowStorageService.saveFlow) and Postgres fires row-level BEFORE
  // INSERT triggers on the proposed row *before* it resolves the conflict, so
  // the trigger runs on re-saves too. What makes those safe is the early return
  // added in migration 115: the trigger returns NEW immediately when a row with
  // this id already exists and belongs to this user, because the primary key
  // means such a statement cannot add a row. Without it a user at their cap
  // could not save a knod they already owned — data loss, not a refusal.
  //
  // So in practice this fires on creation. It returns true when it explained the refusal, letting
  // the caller skip its generic "trouble saving your work" message, which would
  // otherwise hide the one detail that makes this actionable: the number.
  const explainIfFlowLimit = useCallback(
    (error: unknown): boolean =>
      reportFlowLimitRefusal(error, {
        onUpgrade: () => navigate('/pricing'),
        onManageKnods: () => navigate('/'),
      }),
    [navigate],
  );

  // Keep ref in sync with state for auto-save access
  useEffect(() => {
    currentFlowNameRef.current = currentFlowName;
  }, [currentFlowName]);

  // Auto-save sessions: which document a save belongs to and which row it
  // writes. The rules (and their tests) live in src/utils/flowSaveSessions.ts.
  // Every content-replacing load calls saveSessionsRef.current.replaceContent()
  // next to flowHistory.clear().
  const saveSessionsRef = useRef<FlowSaveSessions | null>(null);
  if (!saveSessionsRef.current) saveSessionsRef.current = new FlowSaveSessions(currentFlowId);
  const saveSession = saveSessionsRef.current.observe(currentFlowId);

  // Auto-save hook - now also handles new (unsaved) flows
  const autoSave = useAutoSave({
    enabled: autoSaveEnabled && (nodes.length > 0 || edges.length > 0), // Auto-save when there's content and setting is enabled
    saveInterval: 120000, // 2 minutes
    debounceDelay: 5000,  // 5 seconds after last change
    flowKey: saveSession.key,
    // A flush of a left session is registered under that session's row at
    // once, so reopening the flow waits for it (settleFlowSaves).
    onFlushScheduled: (flush) => {
      const id = FlowSaveSessions.idForSave(saveSession, currentFlowId);
      if (id) trackFlowSave(id, flush);
    },
    onSave: async ({ isCurrent }) => {
      // Skip if canvas is empty
      if (nodes.length === 0 && edges.length === 0) return;

      const viewport = getViewport();
      // A save flushed for a flow that has since been left (see useAutoSave)
      // must use THAT flow's name: the ref already holds the new flow's.
      const current = isCurrent();

      // For new flows, generate a date-based name
      // Use ref to get the latest value (state may be stale in callback)
      const flowName = (current ? currentFlowNameRef.current : currentFlowName) || generateFlowName();
      // Row choice and id bookkeeping: src/utils/flowSaveSessions.ts. The row is
      // the editor's id, else one an earlier save of THIS session minted (not yet
      // rendered, or the session was left) — never a second knod.
      const { id: savedId, minted, adopt } = await saveSessionsRef.current!.runSave(
        saveSession,
        currentFlowId,
        isCurrent,
        async (rowId) => {
          const flow = flowSerializationService.serializeFlow(
            nodes,
            edges,
            viewport,
            {
              id: rowId, // reserved on the session (FlowSaveSessions.reserveId)
              name: flowName,
              description: currentFlowDescription,
              tags: currentFlowTags,
              brandVoiceId: currentBrandVoiceId,
              thumbnail: currentFlowThumbnail,
              projectId: currentFlowProjectId
            },
            false // Not a template
          );
          try {
            await flowStorageService.saveFlow(flow);
          } catch (error) {
            // The autosave indicator alone turns amber and says nothing. That is
            // exactly wrong here: the canvas has real work on it that will never
            // persist, and the user has no idea why. Say so, then rethrow so
            // useAutoSave still records the failure and doesn't mark this clean.
            explainIfFlowLimit(error);
            throw error;
          }
          return flow.metadata.id;
        },
      );

      // A new canvas's first save: adopt the id — unless the editor has moved
      // to another document meanwhile (its next save would overwrite this row).
      if (minted && !adopt) {
        logger.info('Auto-save stored a left, unsaved canvas as new knod:', savedId, flowName);
      } else if (minted) {
        setCurrentFlowId(savedId);
        setCurrentFlowName(flowName);
        flowStorageService.setCurrentFlowId(savedId);
        logger.info('Auto-save created new knod:', savedId, flowName);
        toastService.success(`Knod auto-saved as "${flowName}"`);
      } else {
        logger.debug('Auto-save completed for knod:', savedId);
      }
    }
  });

  // Wire the forward-declared dirty marker to the real auto-save hook.
  markDirtyRef.current = autoSave.markDirty;

  // Migrate legacy brand voice on component mount
  useEffect(() => {
    brandVoiceStorageService.migrateFromLegacy().catch(err => {
      logger.error('Failed to migrate legacy brand voice:', err);
    });
  }, []);

  // Migrate custom nodes on component mount
  useEffect(() => {
    const migrateCustomNodes = async () => {
      try {
        const stats = await migrateCustomNodesInStorage();
        const total = stats.migrated + stats.skipped + stats.failed;
        
        if (stats.migrated > 0) {
          logger.info('[CreativeNodeFlow] Custom node migration completed:', stats);
          toastService.success(
            `Migrated ${stats.migrated} custom node${stats.migrated === 1 ? '' : 's'} to new format`,
            5000
          );
          
          // Reload custom node definitions after migration
          setCustomNodeDefinitions(CustomNodeStorage.getAll());
        } else if (total > 0) {
          logger.debug('[CreativeNodeFlow] All custom nodes already migrated');
        }
        
        if (stats.failed > 0) {
          logger.error('[CreativeNodeFlow] Failed to migrate some custom nodes:', stats);
          toastService.warning(
            `Warning: ${stats.failed} custom node${stats.failed === 1 ? '' : 's'} failed to migrate`,
            8000
          );
        }
      } catch (error) {
        logger.error('[CreativeNodeFlow] Custom node migration failed:', error);
        toastService.error(
          'Failed to migrate custom nodes',
          5000
        );
      }
    };
    
    migrateCustomNodes();
  }, []);

  // Subscribe to toast service
  useEffect(() => {
    const unsubscribe = toastService.subscribe((data) => {
      setToastData(data);
    });
    return unsubscribe;
  }, []);

  // Subscribe to confirm dialog service
  useEffect(() => {
    const unsubscribe = confirmDialogService.subscribe((request) => {
      setConfirmData(request);
    });
    return unsubscribe;
  }, []);

  // Subscribe to prompt limit service
  useEffect(() => {
    const unsubscribe = promptLimitService.subscribe((request) => {
      setPromptLimitData(request);
    });
    return unsubscribe;
  }, []);

  // Load brand voice name when ID changes & sync to flow storage service
  useEffect(() => {
    // Sync brand voice ID to storage service for cross-component access
    flowStorageService.setCurrentBrandVoiceId(currentBrandVoiceId || null);
    
    const loadBrandVoiceName = async () => {
      if (currentBrandVoiceId) {
        try {
          const voice = await brandVoiceStorageService.getBrandVoice(currentBrandVoiceId);
          setCurrentBrandVoiceName(voice?.name || '');
          setCurrentBrandVoiceColor(voice?.color || '#3b82f6');
        } catch (error) {
          logger.error('Failed to load brand voice name:', error);
          setCurrentBrandVoiceName('');
          setCurrentBrandVoiceColor('#3b82f6');
        }
      } else {
        setCurrentBrandVoiceName('');
        setCurrentBrandVoiceColor('#3b82f6');
      }
    };
    loadBrandVoiceName();
  }, [currentBrandVoiceId]);

  // Load flow from URL or restore last opened flow on mount
  useEffect(() => {
    let cancelled = false;

    const loadFlowFromUrl = async () => {
      try {
        // Priority 0: Load from template query param
        if (templateId) {
          logger.info('[CreativeNodeFlow] Loading template:', templateId);
          const template = await flowStorageService.loadTemplate(templateId);
          if (cancelled) return;
          
          if (template) {
            // Load template nodes and edges (as a copy, not the template itself)
            const migratedNodes = migrateNodeTypes(template.state.nodes);
            setNodes(migratedNodes);
            flowHistory.clear(); // a different graph: old steps must not apply to it
            saveSessionsRef.current?.replaceContent(); // ...and its saves must not write this one
            setEdges(migrateEdgeColors(template.state.edges, migratedNodes));
            // Don't set flow ID - this is a new flow based on template
            setCurrentFlowId(null);
            setCurrentFlowName(`${template.metadata.name} (Copy)`);
            setCurrentFlowDescription(template.metadata.description || '');
            setCurrentFlowTags(template.metadata.tags || []);
            
            // Clear the template query param
            setSearchParams({}, { replace: true });
            
            logger.info('[CreativeNodeFlow] Template loaded:', template.metadata.name);
            toastService.success(`Created flow from template: ${template.metadata.name}`);
          } else {
            logger.warn('[CreativeNodeFlow] Template not found:', templateId);
            toastService.error(friendlyErrors.templateNotFound);
            setSearchParams({}, { replace: true });
          }
          return;
        }
        
        // Priority 1: Load flow from URL parameter
        if (urlFlowId) {
          // A real flow ID is in the URL — we are no longer on the /flow creation path.
          // Reset the mutex so future visits to /flow can create a new knod.
          _newFlowCreationInFlight = false;
          logger.info('[CreativeNodeFlow] Loading flow from URL:', urlFlowId);
          const flow = await flowStorageService.loadFlow(urlFlowId);
          if (cancelled) return;
          
          if (flow) {
            // Restore flow state with legacy node type migration and edge color fix
            const migratedNodes = migrateNodeTypes(flow.state.nodes);
            setNodes(migratedNodes);
            flowHistory.clear(); // a different graph: old steps must not apply to it
            saveSessionsRef.current?.replaceContent(); // ...and its saves must not write this one
            setEdges(migrateEdgeColors(flow.state.edges, migratedNodes));
            setCurrentFlowId(flow.metadata.id);
            setCurrentFlowName(flow.metadata.name);
            setCurrentFlowDescription(flow.metadata.description || '');
            setCurrentFlowTags(flow.metadata.tags || []);
            setCurrentFlowThumbnail(flow.metadata.thumbnail);
            setCurrentBrandVoiceId(flow.metadata.brandVoiceId);
            setCurrentFlowProjectId(flow.metadata.projectId ?? null);
            
            // Queue viewport restoration (will be applied after ReactFlow initializes)
            if (flow.state.viewport) {
              setPendingViewport(flow.state.viewport);
            }
            
            // Update localStorage to remember this flow
            flowStorageService.setCurrentFlowId(flow.metadata.id);
            
            logger.info('[CreativeNodeFlow] Flow loaded from URL:', flow.metadata.name);
            toastService.info(`Loaded knod: ${flow.metadata.name}`);
          } else {
            // Flow not found - redirect to home
            logger.warn('[CreativeNodeFlow] Flow not found:', urlFlowId);
            toastService.error(friendlyErrors.loadFailed);
            navigate('/', { replace: true });
          }
          return;
        }
        
        // Priority 2: If at /flow (new flow), create and save immediately
        // This ensures the flow has an ID for features that require it (phone upload, etc.)
        if (window.location.pathname === '/flow') {
          // Guard against React Strict Mode double-invocation: both async runs call
          // saveFlow before their own `cancelled` flag fires. The module-level mutex
          // ensures only ONE run proceeds to create a document.
          if (_newFlowCreationInFlight) return;
          _newFlowCreationInFlight = true;

          logger.info('[CreativeNodeFlow] Creating new flow');
          
          // Generate a date-based name for the new flow
          const now = new Date();
          const date = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
          const time = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
          const flowName = `Knod - ${date} at ${time}`;
          
          // Create and save the new flow immediately
          const newFlow = flowSerializationService.serializeFlow(
            [], // Empty nodes
            [], // Empty edges
            { x: 0, y: 0, zoom: 1 }, // Default viewport
            { name: flowName },
            false
          );
          
          await flowStorageService.saveFlow(newFlow);
          if (cancelled) return; // Abort if effect was re-run (prevents duplicate creation)
          
          // Update state with the new flow
          setCurrentFlowId(newFlow.metadata.id);
          setCurrentFlowName(flowName);
          setCurrentFlowDescription('');
          setCurrentFlowTags([]);
          setCurrentFlowProjectId(null);
          setNodes([]);
          setEdges([]);
          
          // Update localStorage and URL
          flowStorageService.setCurrentFlowId(newFlow.metadata.id);
          navigate(`/knod/${newFlow.metadata.id}`, { replace: true });
          
          logger.info('[CreativeNodeFlow] New knod created:', newFlow.metadata.id, flowName);
          return;
        }
        
        // Priority 3: Restore last opened flow from localStorage (only when no explicit path)
        const savedFlowId = flowStorageService.getCurrentFlowId();
        if (savedFlowId) {
          logger.info('[CreativeNodeFlow] Restoring last opened flow:', savedFlowId);
          const flow = await flowStorageService.loadFlow(savedFlowId);
          if (cancelled) return;
          
          if (flow) {
            // Restore flow state with legacy node type migration and edge color fix
            const migratedNodes = migrateNodeTypes(flow.state.nodes);
            setNodes(migratedNodes);
            flowHistory.clear(); // a different graph: old steps must not apply to it
            saveSessionsRef.current?.replaceContent(); // ...and its saves must not write this one
            setEdges(migrateEdgeColors(flow.state.edges, migratedNodes));
            setCurrentFlowId(flow.metadata.id);
            setCurrentFlowName(flow.metadata.name);
            setCurrentFlowDescription(flow.metadata.description || '');
            setCurrentFlowTags(flow.metadata.tags || []);
            setCurrentFlowThumbnail(flow.metadata.thumbnail);
            setCurrentBrandVoiceId(flow.metadata.brandVoiceId);
            setCurrentFlowProjectId(flow.metadata.projectId ?? null);
            
            // Queue viewport restoration (will be applied after ReactFlow initializes)
            if (flow.state.viewport) {
              setPendingViewport(flow.state.viewport);
            }
            
            // Update URL to reflect the loaded knod
            navigate(`/knod/${flow.metadata.id}`, { replace: true });
            
            logger.info('[CreativeNodeFlow] Knod restored:', flow.metadata.name);
            toastService.info(`Restored knod: ${flow.metadata.name}`);
          } else {
            // Knod was deleted or not found - clear the reference
            logger.warn('[CreativeNodeFlow] Saved flow not found, clearing reference');
            flowStorageService.clearCurrentFlowId();
          }
        }
      } catch (error) {
        if (cancelled) return;
        logger.error('[CreativeNodeFlow] Failed to load flow:', error);
        flowStorageService.clearCurrentFlowId();
        _newFlowCreationInFlight = false; // Unblock future /flow creation attempts

        // Arriving at /flow creates a knod immediately, so the cap can refuse
        // it here. Without this the user lands on a blank canvas with no flow
        // id and nothing on screen ever explains why. Send them back to the
        // dashboard, where the counter shows the state they just hit.
        if (explainIfFlowLimit(error)) {
          navigate('/', { replace: true });
          return;
        }

        if (urlFlowId) {
          navigate('/', { replace: true });
        }
      }
    };
    
    loadFlowFromUrl();
    return () => { cancelled = true; };
  }, [urlFlowId, templateId]); // Re-run when URL flow ID or template changes

  // Handle tutorial query param - start tutorial after flow is ready
  useEffect(() => {
    if (tutorialId) {
      // Small delay to ensure the flow UI is ready
      const timer = setTimeout(() => {
        logger.info('[CreativeNodeFlow] Starting tutorial from query param:', tutorialId);
        startTutorial(tutorialId);
        // Clear the tutorial query param
        setSearchParams({}, { replace: true });
      }, 500);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [tutorialId, startTutorial, setSearchParams]);

  // Get credits refresh function
  const { refreshBalance } = useCredits();
  const { user } = useAuth();

  // Handle Stripe checkout success/cancel query params
  useEffect(() => {
    const checkoutStatus = searchParams.get('checkout');
    const sessionId = searchParams.get('session_id');
    
    if (checkoutStatus === 'success' && sessionId && user?.id) {
      // Verify the checkout session and fulfill the order (add credits)
      // This is a fallback in case the Stripe webhook didn't fire
      const verifyAndFulfill = async () => {
        try {
          logger.info('[CreativeNodeFlow] Verifying checkout session:', sessionId);
          
          const response = await fetch(`${getApiBase()}/api/checkout/verify/${sessionId}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'x-user-id': user.id,
            },
          });
          
          if (response.ok) {
            const result = await response.json();
            if (result.alreadyFulfilled) {
              logger.info('[CreativeNodeFlow] Session already fulfilled');
            } else {
              logger.info('[CreativeNodeFlow] Session verified and fulfilled:', result.credits, 'credits');
            }
            toastService.success(`🎉 Payment successful! ${result.credits} credits have been added to your account.`);
          } else {
            // Verification failed, but payment may have still succeeded
            // Just show a generic success and refresh balance
            logger.warn('[CreativeNodeFlow] Could not verify session, refreshing balance');
            toastService.success('🎉 Payment successful! Credits have been added to your account.');
          }
        } catch (error) {
          logger.error('[CreativeNodeFlow] Error verifying checkout:', error);
          // Still show success - payment went through, credits will arrive via webhook
          toastService.success('🎉 Payment successful! Credits have been added to your account.');
        }
        
        // Refresh the credits balance
        refreshBalance();
      };
      
      verifyAndFulfill();
      
      // Clear the query params
      setSearchParams({}, { replace: true });
      
      logger.info('[CreativeNodeFlow] Checkout completed successfully, session:', sessionId);
    } else if (checkoutStatus === 'cancelled') {
      // User cancelled the checkout
      toastService.info('Checkout was cancelled. No charges were made.');
      
      // Clear the query params
      setSearchParams({}, { replace: true });
      
      logger.info('[CreativeNodeFlow] Checkout was cancelled');
    }
  }, [searchParams, setSearchParams, refreshBalance, user]);

  // Track if a flow has been loaded (for job resumption)
  const isFlowLoaded = currentFlowId !== null;

  // ReactFlow instance for proper coordinate transformations
  const { screenToFlowPosition: reactFlowScreenToFlowPosition, getViewport, setViewport, fitView, getNode, getNodes, getEdges: rfGetEdges, setNodes: rfSetNodes, setEdges: rfSetEdges } = useReactFlow();

  // Expose the store-bound graph functions to module-level services
  // (GenerationLifecycleService) so generation completion can spawn/update
  // output nodes even when the originating node component is unmounted by
  // onlyRenderVisibleElements — or when no node component is mounted at all.
  useEffect(() => {
    registerFlowStore({
      setNodes: rfSetNodes,
      setEdges: rfSetEdges,
      getNode,
      getNodes,
      getEdges: rfGetEdges,
    });
    return () => unregisterFlowStore();
  }, [rfSetNodes, rfSetEdges, getNode, getNodes, rfGetEdges]);

  // Resume pending jobs when a flow is loaded
  // This needs to be after useReactFlow() to access getNode
  useJobResumption({
    nodes,
    setNodes,
    edges,
    setEdges,
    getNode,
    isFlowLoaded
  });

  // Poll for new media when resumed background jobs complete
  // Note: We only notify the FilePanel to refresh its asset list.
  // We do NOT reload the flow from database here because that would overwrite
  // any pending local state changes (like newly created output nodes) that
  // haven't been auto-saved yet, causing a race condition.
  const handleMediaUpdated = useCallback(async (count: number) => {
    // Just trigger a FilePanel refresh so new assets appear in the gallery
    window.dispatchEvent(new CustomEvent('fileStorageUpdated'));
    logger.debug('[CreativeNodeFlow] Media poll detected new assets:', count);
  }, []);

  // Start polling for new media when flow is loaded
  useMediaPolling(currentFlowId || '', user?.id || null, handleMediaUpdated);

  // Handle double-click on node to zoom in
  const handleNodeDoubleClick = useCallback((_event: React.MouseEvent, node: Node) => {
    fitView({
      nodes: [node],
      duration: 300,
      padding: 0.3,
      maxZoom: 1.2,
    });
  }, [fitView]);

  // Fit all nodes into view when the tutorial reaches the celebration step
  useEffect(() => {
    if (tutorialStep?.id === 'celebration') {
      setTimeout(() => fitView({ padding: 0.2, duration: 600 }), 300);
    }
  }, [tutorialStep?.id, fitView]);

  // When a tutorial step's target is an element inside a ReactFlow node, the
  // user can only see/click it if the node is on screen. ReactFlow doesn't
  // respond to scrollIntoView (transforms, not scroll), so we have to drive
  // the viewport ourselves. Walk up from the target element to its parent
  // .react-flow__node and fitView on it.
  //
  // This fires whenever the tutorial advances to a new step. We poll briefly
  // because the target node may have just been spawned (the previous step's
  // add-node action) and React hasn't necessarily painted it yet.
  useEffect(() => {
    if (!tutorialStep?.target) return;
    if (tutorialStep.id === 'celebration') return; // celebration handles its own fitView

    let attempts = 0;
    const maxAttempts = 20; // ~1s at 50ms cadence
    const interval = setInterval(() => {
      attempts += 1;
      const el = document.querySelector(tutorialStep.target!);
      const nodeEl = el?.closest('.react-flow__node') as HTMLElement | null;
      if (nodeEl) {
        const nodeId = nodeEl.getAttribute('data-id');
        const node = nodeId ? getNode(nodeId) : null;
        if (node) {
          fitView({
            nodes: [node],
            duration: 500,
            padding: 0.4,
            maxZoom: 1.1,
          });
        }
        clearInterval(interval);
      } else if (attempts >= maxAttempts) {
        clearInterval(interval);
      }
    }, 50);

    return () => clearInterval(interval);
  }, [tutorialStep?.id, tutorialStep?.target, fitView, getNode]);

  // Export/Import handlers for Knod files
  const handleExportWithMedia = useCallback(async () => {
    try {
      await knodExportService.exportWithMedia({
        flowName: currentFlowName || 'Untitled Knod',
        nodes,
        edges,
        viewport: getViewport(),
        flowId: currentFlowId || undefined,
      });
      toastService.success('Knod exported with media');
    } catch (error) {
      logger.error('[CreativeNodeFlow] Export with media failed:', error);
      toastService.error(friendlyErrors.exportFailed);
    }
  }, [currentFlowName, nodes, edges, getViewport, currentFlowId]);

  const handleExportStructure = useCallback(async () => {
    try {
      await knodExportService.exportStructureOnly({
        flowName: currentFlowName || 'Untitled Knod',
        nodes,
        edges,
        viewport: getViewport(),
        flowId: currentFlowId || undefined,
      });
      toastService.success('Knod exported (structure only)');
    } catch (error) {
      logger.error('[CreativeNodeFlow] Export structure failed:', error);
      toastService.error('Failed to export Knod');
    }
  }, [currentFlowName, nodes, edges, getViewport, currentFlowId]);

  // Auto-arrange nodes using Sugiyama-style hierarchical layout (left to right)
  const handleAutoArrange = useCallback(() => {
    if (nodes.length === 0) return;

    // --- Step 0: Selection-aware scoping ---
    // With 2+ nodes selected, arrange ONLY the selected sub-graph and leave
    // every other node untouched; only edges whose BOTH endpoints are selected
    // shape the hierarchy. The Step 4 bounding box is then computed from
    // `layoutNodes`, so the layout starts at the selection's mid-left. With 0 or
    // 1 selected we fall through unchanged (layoutNodes === nodes,
    // layoutEdges === edges) — the full-graph behavior is byte-identical.
    const selectedNodes = nodes.filter(n => n.selected);
    const useSelection = selectedNodes.length >= 2;
    const layoutNodes = useSelection ? selectedNodes : nodes;
    const layoutIds = new Set(layoutNodes.map(n => n.id));
    const layoutEdges = useSelection
      ? edges.filter(e => layoutIds.has(e.source) && layoutIds.has(e.target))
      : edges;

    // --- Step 1: Build directed graph ---
    const childrenOf = new Map<string, string[]>();
    const parentsOf = new Map<string, string[]>();
    const inDegree = new Map<string, number>();

    layoutNodes.forEach(n => {
      childrenOf.set(n.id, []);
      parentsOf.set(n.id, []);
      inDegree.set(n.id, 0);
    });

    layoutEdges.forEach(e => {
      if (!childrenOf.has(e.source) || !parentsOf.has(e.target)) return;
      childrenOf.get(e.source)!.push(e.target);
      parentsOf.get(e.target)!.push(e.source);
      inDegree.set(e.target, (inDegree.get(e.target) || 0) + 1);
    });

    // --- Step 2: Longest-path column assignment ---
    // This places each node as far right as possible, giving cleaner edge routing
    const nodeToCol = new Map<string, number>();
    const queue: string[] = [];

    inDegree.forEach((deg, id) => {
      if (deg === 0) {
        queue.push(id);
        nodeToCol.set(id, 0);
      }
    });

    const tempInDeg = new Map(inDegree);
    while (queue.length > 0) {
      const id = queue.shift()!;
      const col = nodeToCol.get(id) || 0;

      for (const childId of childrenOf.get(id) || []) {
        // Always take the max column (longest path)
        const current = nodeToCol.get(childId) ?? -1;
        nodeToCol.set(childId, Math.max(current, col + 1));

        const newDeg = (tempInDeg.get(childId) || 0) - 1;
        tempInDeg.set(childId, newDeg);
        if (newDeg === 0) queue.push(childId);
      }
    }

    // Handle remaining nodes (cycles or disconnected)
    layoutNodes.forEach(n => {
      if (!nodeToCol.has(n.id)) nodeToCol.set(n.id, 0);
    });

    // Group nodes into columns
    const maxCol = Math.max(...Array.from(nodeToCol.values()), 0);
    const columns: string[][] = Array.from({ length: maxCol + 1 }, () => []);
    nodeToCol.forEach((col, id) => columns[col].push(id));

    // --- Step 2b: Detect fan blocks ---
    // When a hub has many sibling nodes connected on one side that are leaves on
    // the far side, stacking them in one vertical column makes the graph tall
    // and forces scrolling. Lay those siblings as a compact 2-D grid instead.
    // We only do this for leaves (no further connections) so the surrounding
    // column layout isn't disturbed — other shapes fall through unchanged.
    const FAN_BLOCK_MIN = 6;             // "more than 4 or 5"
    const FAN_BLOCK_3COL_THRESHOLD = 11; // >=11 uses 3 sub-columns, else 2
    const SUB_COL_GAP_RATIO = 0.5;       // sub-col gap = GAP_X * this

    interface FanBlock {
      hubId: string;
      blockCol: number;
      side: 'out' | 'in';
      members: string[];
      cols: 2 | 3;
      rows: number;
    }
    const fanBlocks: FanBlock[] = [];
    const memberOf = new Map<string, FanBlock>();
    const fanBlockByCol = new Map<number, FanBlock>();

    const registerBlock = (block: FanBlock) => {
      // If any member is already claimed by a larger block, keep the larger.
      const conflicts = block.members.filter(id => memberOf.has(id));
      if (conflicts.length > 0) {
        const existing = memberOf.get(conflicts[0])!;
        if (existing.members.length >= block.members.length) return;
        existing.members.forEach(id => memberOf.delete(id));
        const idx = fanBlocks.indexOf(existing);
        if (idx >= 0) fanBlocks.splice(idx, 1);
      }
      fanBlocks.push(block);
      block.members.forEach(id => memberOf.set(id, block));
      fanBlockByCol.set(block.blockCol, block);
    };

    const pickCols = (k: number): 2 | 3 => (k >= FAN_BLOCK_3COL_THRESHOLD ? 3 : 2);

    layoutNodes.forEach(hub => {
      const hubCol = nodeToCol.get(hub.id) ?? 0;

      // Fan-out: members are children of hub
      const outKids = (childrenOf.get(hub.id) || []).filter(kId => {
        const pars = parentsOf.get(kId) || [];
        const kids = childrenOf.get(kId) || [];
        return pars.length === 1
          && kids.length === 0
          && nodeToCol.get(kId) === hubCol + 1;
      });
      if (outKids.length >= FAN_BLOCK_MIN) {
        const cols = pickCols(outKids.length);
        registerBlock({
          hubId: hub.id,
          blockCol: hubCol + 1,
          side: 'out',
          members: outKids,
          cols,
          rows: Math.ceil(outKids.length / cols),
        });
      }

      // Fan-in: members are parents of hub
      const inPars = (parentsOf.get(hub.id) || []).filter(pId => {
        const pars = parentsOf.get(pId) || [];
        const kids = childrenOf.get(pId) || [];
        return kids.length === 1
          && pars.length === 0
          && nodeToCol.get(pId) === hubCol - 1;
      });
      if (inPars.length >= FAN_BLOCK_MIN) {
        const cols = pickCols(inPars.length);
        registerBlock({
          hubId: hub.id,
          blockCol: hubCol - 1,
          side: 'in',
          members: inPars,
          cols,
          rows: Math.ceil(inPars.length / cols),
        });
      }
    });

    // --- Step 3: Measure node dimensions ---
    const dim = new Map<string, { w: number; h: number }>();
    layoutNodes.forEach(n => {
      dim.set(n.id, {
        w: n.measured?.width || n.width || 280,
        h: n.measured?.height || n.height || 200,
      });
    });

    // --- Step 4: Calculate starting point from current bounding box ---
    // Anchor at horizontal-left, vertical-center of existing node positions
    const GAP_X = 100; // horizontal gap between columns
    const GAP_Y = 40;  // vertical gap between nodes in same column

    let minX = Infinity, minY = Infinity, maxY = -Infinity;
    layoutNodes.forEach(n => {
      const h = dim.get(n.id)?.h || 200;
      minX = Math.min(minX, n.position.x);
      minY = Math.min(minY, n.position.y);
      maxY = Math.max(maxY, n.position.y + h);
    });
    const START_X = minX;
    const boundsCenterY = (minY + maxY) / 2;

    // Calculate column X positions from actual widths. Columns that host a
    // fan block get widened to fit the grid's sub-columns side by side.
    const colX: number[] = [];
    let runningX = START_X;
    columns.forEach((col, i) => {
      colX[i] = runningX;
      const nonBlockMaxW = col
        .filter(id => !memberOf.has(id))
        .reduce((m, id) => Math.max(m, dim.get(id)?.w || 0), 0);
      const block = fanBlockByCol.get(i);
      let slotW: number;
      if (block) {
        const memW = Math.max(...block.members.map(id => dim.get(id)?.w || 280));
        const subColGap = GAP_X * SUB_COL_GAP_RATIO;
        const blockW = block.cols * memW + (block.cols - 1) * subColGap;
        slotW = Math.max(blockW, nonBlockMaxW);
      } else {
        slotW = nonBlockMaxW || 280;
      }
      runningX += slotW + GAP_X;
    });

    // --- Step 5: Initial vertical positioning + barycenter ordering ---
    const pos = new Map<string, { x: number; y: number }>();

    // Block members are placed in a grid after the rest of the layout settles
    // (step 6b, below). Exclude them from every per-column pass so they don't
    // perturb barycenter ordering or overlap resolution.
    const nonMembersOf = (col: string[]) => col.filter(id => !memberOf.has(id));

    // First column: center around bounding box vertical center
    if (columns[0]) {
      const col0 = nonMembersOf(columns[0]);
      let totalH = 0;
      col0.forEach(id => { totalH += (dim.get(id)?.h || 200); });
      totalH += Math.max(0, col0.length - 1) * GAP_Y;
      let y = boundsCenterY - totalH / 2;
      col0.forEach(id => {
        pos.set(id, { x: colX[0], y });
        y += (dim.get(id)?.h || 200) + GAP_Y;
      });
    }

    // Subsequent columns: order by barycenter of parents, then position
    for (let c = 1; c < columns.length; c++) {
      const col = nonMembersOf(columns[c]);

      // Sort by average center-Y of parents (barycenter)
      col.sort((a, b) => {
        const pA = (parentsOf.get(a) || []).filter(p => pos.has(p));
        const pB = (parentsOf.get(b) || []).filter(p => pos.has(p));
        const centerOf = (id: string) => (pos.get(id)?.y || 0) + (dim.get(id)?.h || 200) / 2;
        const avgA = pA.length > 0 ? pA.reduce((s, p) => s + centerOf(p), 0) / pA.length : 0;
        const avgB = pB.length > 0 ? pB.reduce((s, p) => s + centerOf(p), 0) / pB.length : 0;
        return avgA - avgB;
      });

      // Place each node at barycenter of its parents
      col.forEach(id => {
        const nodeParents = (parentsOf.get(id) || []).filter(p => pos.has(p));
        const h = dim.get(id)?.h || 200;

        let targetY: number;
        if (nodeParents.length > 0) {
          const parentCenters = nodeParents.map(p =>
            (pos.get(p)?.y || 0) + (dim.get(p)?.h || 200) / 2
          );
          targetY = parentCenters.reduce((a, b) => a + b, 0) / parentCenters.length - h / 2;
        } else {
          targetY = boundsCenterY;
        }

        pos.set(id, { x: colX[c], y: targetY });
      });

      // Resolve overlaps in this column (push down)
      resolveOverlaps(col, pos, dim, GAP_Y);
    }

    // --- Step 6: Iterative refinement - center parents ↔ children ---
    for (let iter = 0; iter < 4; iter++) {
      // Backward pass: center each parent relative to its children
      for (let c = columns.length - 2; c >= 0; c--) {
        const col = nonMembersOf(columns[c]);
        col.forEach(id => {
          const kids = (childrenOf.get(id) || []).filter(ch => pos.has(ch));
          if (kids.length === 0) return;
          const h = dim.get(id)?.h || 200;
          const kidCenters = kids.map(k => (pos.get(k)?.y || 0) + (dim.get(k)?.h || 200) / 2);
          const avg = kidCenters.reduce((a, b) => a + b, 0) / kidCenters.length;
          pos.set(id, { x: colX[c], y: avg - h / 2 });
        });
        resolveOverlaps(col, pos, dim, GAP_Y);
      }

      // Forward pass: re-center children relative to parents
      for (let c = 1; c < columns.length; c++) {
        const col = nonMembersOf(columns[c]);
        col.forEach(id => {
          const nodeParents = (parentsOf.get(id) || []).filter(p => pos.has(p));
          if (nodeParents.length === 0) return;
          const h = dim.get(id)?.h || 200;
          const parentCenters = nodeParents.map(p =>
            (pos.get(p)?.y || 0) + (dim.get(p)?.h || 200) / 2
          );
          const avg = parentCenters.reduce((a, b) => a + b, 0) / parentCenters.length;
          pos.set(id, { x: colX[c], y: avg - h / 2 });
        });
        resolveOverlaps(col, pos, dim, GAP_Y);
      }
    }

    // --- Step 6b: Place fan-block grid members ---
    // Grid members had no positions during the main layout passes; we now lay
    // them out anchored to their hub's FINAL Y so refinement drift doesn't leave
    // the block visually off-center.
    const placeFanBlock = (block: FanBlock) => {
      const { hubId, members, rows, blockCol } = block;
      const hubPos = pos.get(hubId);
      if (!hubPos) return;
      const memW = Math.max(...members.map(id => dim.get(id)?.w || 280));
      const memH = Math.max(...members.map(id => dim.get(id)?.h || 200));
      const subColGap = GAP_X * SUB_COL_GAP_RATIO;
      const blockH = rows * memH + (rows - 1) * GAP_Y;
      const hubCenterY = hubPos.y + (dim.get(hubId)?.h || 200) / 2;
      const topY = hubCenterY - blockH / 2;
      const leftX = colX[blockCol];
      members.forEach((id, i) => {
        const subCol = Math.floor(i / rows); // column-major fill
        const row = i % rows;
        const w = dim.get(id)?.w || 280;
        pos.set(id, {
          x: leftX + subCol * (memW + subColGap) + (memW - w) / 2,
          y: topY + row * (memH + GAP_Y),
        });
      });
    };
    fanBlocks.forEach(placeFanBlock);

    // Final safety pass: if any non-block node in a block's column overlaps the
    // block's vertical span, push the non-block node (not the block) out of the
    // way. This is rare (different hubs sharing a column) but keeps the grid
    // coherent when it happens.
    fanBlocks.forEach(block => {
      const memW = Math.max(...block.members.map(id => dim.get(id)?.w || 280));
      const memH = Math.max(...block.members.map(id => dim.get(id)?.h || 200));
      const blockH = block.rows * memH + (block.rows - 1) * GAP_Y;
      const topY = (pos.get(block.hubId)?.y ?? 0) + (dim.get(block.hubId)?.h || 200) / 2 - blockH / 2;
      const botY = topY + blockH;
      const leftX = colX[block.blockCol];
      const rightX = leftX + block.cols * memW + (block.cols - 1) * GAP_X * SUB_COL_GAP_RATIO;
      const neighbors = nonMembersOf(columns[block.blockCol]);
      neighbors.forEach(id => {
        const p = pos.get(id);
        if (!p) return;
        const h = dim.get(id)?.h || 200;
        const w = dim.get(id)?.w || 280;
        const xOverlap = p.x < rightX && p.x + w > leftX;
        const yOverlap = p.y < botY + GAP_Y && p.y + h > topY - GAP_Y;
        if (!xOverlap || !yOverlap) return;
        // Push in whichever direction is shorter.
        const pushDown = botY + GAP_Y - p.y;
        const pushUp = p.y + h - (topY - GAP_Y);
        if (pushDown <= pushUp) {
          pos.set(id, { x: p.x, y: p.y + pushDown });
        } else {
          pos.set(id, { x: p.x, y: p.y - pushUp });
        }
      });
    });

    // --- Step 7: Animate nodes sequentially ---
    // One undo step for the whole staggered animation: held until the last
    // position lands (released in the cleanup timeout below).
    recordUserAction('Auto-arrange', { coalesceKey: 'auto-arrange', hold: true });
    let delay = 0;
    const ANIM_STEP = 40;
    const ANIM_DURATION = 200;
    
    // First pass: add animating class to the nodes being arranged
    setNodes(curr =>
      curr.map(n =>
        layoutIds.has(n.id)
          ? { ...n, className: `${n.className || ''} animating`.trim() }
          : n
      )
    );

    columns.forEach(col => {
      // Sort by (x, y) so the animation flows left-to-right then top-to-bottom.
      // For non-block columns all x's are equal so this reduces to the old
      // top-to-bottom pass; for fan-block columns it animates each grid
      // sub-column as a waterfall rather than interleaving rows across them.
      const sorted = [...col].sort((a, b) => {
        const pa = pos.get(a);
        const pb = pos.get(b);
        const dx = (pa?.x || 0) - (pb?.x || 0);
        if (Math.abs(dx) > 0.5) return dx;
        return (pa?.y || 0) - (pb?.y || 0);
      });
      sorted.forEach(nodeId => {
        const target = pos.get(nodeId);
        if (!target) return;
        setTimeout(() => {
          setNodes(curr =>
            curr.map(n => (n.id === nodeId ? { ...n, position: target } : n))
          );
        }, delay);
        delay += ANIM_STEP;
      });
    });

    // Remove animation class after all animations complete
    const totalDelay = delay + ANIM_DURATION + 50;
    setTimeout(() => {
      setNodes(curr =>
        curr.map(n =>
          layoutIds.has(n.id)
            ? { ...n, className: (n.className || '').replace(/\s*animating\s*/g, ' ').trim() }
            : n
        )
      );
      // The positions above are written by a staggered setTimeout per node, so the
      // last one lands here, not when handleAutoArrange returns. Marking only at
      // return would race the stagger (40ms x node count) against the 5s save
      // debounce and persist a half-arranged layout on a large graph.
      markFlowDirty();
      releaseUserAction('auto-arrange');
    }, totalDelay);

    // Fit to the arranged set: the full graph normally, or just the selection
    // when in selection mode (0/1 selected keeps the original full-graph fit).
    setTimeout(() => fitView(
      useSelection
        ? { padding: 0.15, duration: 500, nodes: layoutNodes.map(n => ({ id: n.id })) }
        : { padding: 0.15, duration: 500 }
    ), delay + 150);
    // Marked immediately as well as after the stagger, so closing the tab
    // mid-animation still saves the arrangement that has landed so far.
    markFlowDirty();
    toastService.success('Nodes arranged');
  }, [nodes, edges, setNodes, fitView, markFlowDirty]);

  // Helper: resolve vertical overlaps within a column
  function resolveOverlaps(
    col: string[],
    pos: Map<string, { x: number; y: number }>,
    dim: Map<string, { w: number; h: number }>,
    gap: number
  ) {
    const sorted = [...col].sort((a, b) => (pos.get(a)?.y || 0) - (pos.get(b)?.y || 0));
    if (sorted.length === 0) return;

    // Preserve column center to avoid cumulative downward drift.
    const firstId = sorted[0];
    const lastId = sorted[sorted.length - 1];
    const originalTop = pos.get(firstId)?.y || 0;
    const originalBottom = (pos.get(lastId)?.y || 0) + (dim.get(lastId)?.h || 200);
    const originalCenter = (originalTop + originalBottom) / 2;

    for (let i = 1; i < sorted.length; i++) {
      const prevId = sorted[i - 1];
      const currId = sorted[i];
      const prevBottom = (pos.get(prevId)?.y || 0) + (dim.get(prevId)?.h || 200) + gap;
      const currY = pos.get(currId)?.y || 0;
      if (currY < prevBottom) {
        pos.set(currId, { x: pos.get(currId)!.x, y: prevBottom });
      }
    }

    const adjustedTop = pos.get(firstId)?.y || 0;
    const adjustedBottom = (pos.get(lastId)?.y || 0) + (dim.get(lastId)?.h || 200);
    const adjustedCenter = (adjustedTop + adjustedBottom) / 2;
    const offset = originalCenter - adjustedCenter;

    if (Math.abs(offset) > 0.1) {
      sorted.forEach((nodeId) => {
        const currentPos = pos.get(nodeId);
        if (!currentPos) return;
        pos.set(nodeId, { x: currentPos.x, y: currentPos.y + offset });
      });
    }
  }

  // Apply pending viewport after ReactFlow initializes
  useEffect(() => {
    if (pendingViewport && setViewport) {
      // Small delay to ensure ReactFlow is fully initialized
      const timer = setTimeout(() => {
        setViewport(pendingViewport, { duration: 0 });
        setPendingViewport(null);
        logger.info('[CreativeNodeFlow] Viewport restored:', pendingViewport);
      }, 100);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [pendingViewport, setViewport]);

  // Using useRef for nodeInputHandlers to prevent state mutation
  const nodeInputHandlers = useRef<Map<string, InputHandlerCallback>>(new Map());

  // Ref for ReactFlow wrapper to access viewport conversion
  const reactFlowWrapperRef = useRef<HTMLDivElement>(null);

  // Reset zoom to 100% while keeping the current viewport center stable.
  const resetZoomToOneCentered = useCallback(() => {
    const wrapper = reactFlowWrapperRef.current;
    if (!wrapper) {
      const viewport = getViewport();
      setViewport({ x: viewport.x, y: viewport.y, zoom: 1 }, { duration: 300 });
      return;
    }

    const bounds = wrapper.getBoundingClientRect();
    const screenCenter = {
      x: bounds.left + bounds.width / 2,
      y: bounds.top + bounds.height / 2,
    };

    const flowCenter = reactFlowScreenToFlowPosition(screenCenter);
    const nextZoom = 1;

    setViewport(
      {
        x: screenCenter.x - flowCenter.x * nextZoom,
        y: screenCenter.y - flowCenter.y * nextZoom,
        zoom: nextZoom,
      },
      { duration: 300 }
    );
  }, [getViewport, setViewport, reactFlowScreenToFlowPosition]);

  // ── Double-click zoom guard ────────────────────────────────────────────────
  // React Flow enables `zoomOnDoubleClick` (default true). Internally it binds
  // d3-zoom's `dblclick.zoom` directly on the pane element (`.react-flow__renderer`)
  // during the BUBBLE phase, and — per xyflow's own note — this handler bypasses the
  // interaction filter, so the usual `nowheel`/`nopan` classes do NOT suppress it.
  // Consequence: double-clicking to select a word inside a text-editing surface
  // (a node's inline <input>/<textarea>, or the maximized TextEditorModal) bubbles
  // up to the pane and zooms the canvas.
  //
  // The listener is installed on `document` in the CAPTURE phase because the editor
  // modal is rendered via createPortal to <body>, OUTSIDE the flow wrapper — a
  // wrapper-scoped guard would never see its events. When a double-click originates
  // inside a text-editing context we stopPropagation() so it can't reach the pane's
  // zoom handler. We deliberately do NOT preventDefault(), so the browser's native
  // double-click word-selection still happens. Non-text targets (e.g. the empty
  // pane) are left untouched, so double-click-to-zoom on the empty canvas is
  // preserved.
  useEffect(() => {
    const isTextEditingTarget = (node: EventTarget | null): boolean => {
      const el = node instanceof Element ? node : null;
      if (!el) return false;
      return !!el.closest(
        'input, textarea, [contenteditable=""], [contenteditable="true"]'
      );
    };

    const onDblClickCapture = (event: MouseEvent) => {
      if (isTextEditingTarget(event.target)) {
        event.stopPropagation();
      }
    };

    document.addEventListener('dblclick', onDblClickCapture, true);
    return () => {
      document.removeEventListener('dblclick', onDblClickCapture, true);
    };
  }, []);


  // Ref for NodePalette to close submenu on flow interactions
  const nodePaletteRef = useRef<NodePaletteRef>(null);
  
  // Clipboard for copy/paste of nodes
  const nodeClipboard = useRef<{ nodes: Node[]; edges: Edge[] }>({ nodes: [], edges: [] });
  
  // Track mouse position for paste-at-cursor functionality
  const mousePositionRef = useRef<{ x: number; y: number } | null>(null);

  // Track sequential placement for Iris-spawned nodes so they don't stack on top of each other
  const irisPlacementRef = useRef<{ x: number; y: number; index: number; expiresAt: number } | null>(null);
  
  // Track nodes that should be animated (newly created)
  const [animatedNodeIds, setAnimatedNodeIds] = useState<Set<string>>(new Set());
  // Track nodes that are being deleted to play exit animation
  const [deletingNodeIds, setDeletingNodeIds] = useState<Set<string>>(new Set());
  // Track whether Shift is currently held — used to make marquee-box selection
  // additive instead of replacing the existing selection. React Flow's Pane
  // unconditionally clears selection when a marquee drag starts, so we filter
  // out the deselect changes in our onNodesChange wrapper when Shift is down.
  const shiftHeldRef = useRef(false);
  // True between React Flow's first dragging:true and its dragging:false, so a
  // whole drag is one undo step.
  const historyDragActiveRef = useRef(false);
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Shift') shiftHeldRef.current = true;
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Shift') shiftHeldRef.current = false;
    };
    const onBlur = () => { shiftHeldRef.current = false; };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    };
  }, []);
  // Intercept node removals to play a CSS exit animation before removing
  const onNodesChange = useCallback((changes: NodeChange[]) => {
    // Make Shift+click and Shift+marquee additive. React Flow's Pane
    // unconditionally calls resetSelectedElements() when a marquee drag starts,
    // and getSelectionChanges() during the drag emits select:false for every
    // node that falls outside the rect — both wipe the prior selection even
    // when Shift is held. While Shift is down we drop every select:false
    // change so existing selection is preserved and new nodes are added on
    // top. Shift is intended as a pure "add" modifier here (no toggle-off).
    let effectiveChanges = changes;
    if (shiftHeldRef.current) {
      effectiveChanges = changes.filter(
        (c) => !(c.type === 'select' && (c as any).selected === false)
      );
    }

    // The Delete key reaches both our own handler (which removes the selected
    // nodes at once) and React Flow, whose remove arrives a render later for
    // nodes that are already gone. Those stale removes are dropped here: they
    // would otherwise schedule the 200ms exit-animation removal below, and if
    // Ctrl+Z restored the node in between, the timeout would delete it again.
    const liveNodeIds = new Set(flowHistory.peekGraph().nodes.map((n) => n.id));
    effectiveChanges = effectiveChanges.filter(
      (c) => c.type !== 'remove' || liveNodeIds.has((c as { id: string }).id)
    );
    const removeIds = new Set(
      effectiveChanges
        .filter((c) => c.type === 'remove')
        .map((c: any) => c.id as string)
    );

    // Undo steps for the interactions React Flow owns. A whole drag (one node
    // or a multi-selection) is ONE step, held until React Flow reports
    // dragging:false; keyboard nudges and resizes coalesce per burst.
    const isResizeBatch = effectiveChanges.some(
      (c) => c.type === 'dimensions' && typeof (c as { resizing?: boolean }).resizing === 'boolean'
    );
    const dragMoves = effectiveChanges.filter(
      (c) => c.type === 'position' && (c as { dragging?: boolean }).dragging === true
    );
    let dragEnded = false;
    // Held: React Flow removes the connected edges now but the nodes only
    // after the 200ms exit animation below — both must land in ONE step.
    if (removeIds.size > 0) {
      recordUserAction(removeIds.size === 1 ? 'Delete node' : `Delete ${removeIds.size} nodes`, { coalesceKey: 'delete-nodes', hold: true });
    }
    if (isResizeBatch) {
      const resizing = effectiveChanges.find(
        (c) => c.type === 'dimensions' && (c as { resizing?: boolean }).resizing === true
      );
      if (resizing) recordUserAction('Resize node', { coalesceKey: `resize:${(resizing as { id: string }).id}` });
    } else if (dragMoves.length > 0) {
      if (!historyDragActiveRef.current) {
        historyDragActiveRef.current = true;
        recordUserAction(dragMoves.length === 1 ? 'Move node' : `Move ${dragMoves.length} nodes`, { coalesceKey: 'drag', hold: true });
      }
    } else if (effectiveChanges.some((c) => c.type === 'position' && (c as { position?: unknown }).position)) {
      if (historyDragActiveRef.current) {
        historyDragActiveRef.current = false;
        dragEnded = true;
      } else {
        recordUserAction('Move node', { coalesceKey: 'nudge' });
      }
    }

    if (removeIds.size > 0) {
      // Mark nodes as deleting (adds CSS class for exit animation)
      setDeletingNodeIds((prev) => {
        const next = new Set(prev);
        removeIds.forEach((id) => next.add(id));
        return next;
      });

      // Pass through all non-remove changes immediately
      const nonRemoveChanges = effectiveChanges.filter((c) => c.type !== 'remove');
      if (nonRemoveChanges.length) {
        onNodesChangeInternal(nonRemoveChanges);
      }

      // After animation duration, remove nodes (and connected edges)
      window.setTimeout(() => {
        setNodes((prev) => prev.filter((n) => !removeIds.has(n.id)));
        setEdges((prev) => prev.filter((e) => !removeIds.has(e.source) && !removeIds.has(e.target)));
        releaseUserAction('delete-nodes');

        // Cleanup deleting flags
        setDeletingNodeIds((prev) => {
          const next = new Set(prev);
          removeIds.forEach((id) => next.delete(id));
          return next;
        });
      }, 200); // Match --duration-normal CSS variable
    } else {
      onNodesChangeInternal(effectiveChanges);
    }
    if (dragEnded) releaseUserAction('drag');

    // Only mark dirty for meaningful changes — never for selection, hover or
    // React Flow's own measurement.
    //
    // `dimensions` is the delicate one. React Flow emits it from the
    // ResizeObserver on every mount, every virtualization remount and every
    // load, so treating the type as an edit would make merely *opening* a flow
    // rewrite it. But NodeResizer — which BaseNode renders on resizable nodes —
    // also reports through `dimensions`, and a user dragging a node's corner is
    // as real an edit as dragging the node itself. The two are told apart by
    // `resizing`, which only the interactive resize sets.
    const meaningfulChanges = effectiveChanges.filter(c =>
      c.type === 'position' ||
      c.type === 'remove' ||
      c.type === 'add' ||
      (c.type === 'dimensions' && (c as { resizing?: boolean }).resizing === true)
    );
    if (meaningfulChanges.length > 0) {
      autoSave.markDirty();
    }
  }, [onNodesChangeInternal, autoSave, flowHistory]);

  // Wrap onEdgesChange to track changes for auto-save
  // Edge changes are always meaningful (add/remove connections)
  const onEdgesChange = useCallback((changes: any) => {
    const removedEdges = changes.filter((c: any) => c.type === 'remove').length;
    if (removedEdges > 0) {
      recordUserAction(removedEdges === 1 ? 'Delete connection' : `Delete ${removedEdges} connections`);
    }
    onEdgesChangeInternal(changes);
    // Only mark dirty for add/remove edge changes, not selection
    const meaningfulChanges = changes.filter((c: any) => 
      c.type === 'add' || c.type === 'remove'
    );
    if (meaningfulChanges.length > 0) {
      autoSave.markDirty();
    }
  }, [onEdgesChangeInternal, autoSave]);

  // Update connection state colors based on edges (mark specific handles)
  useEffect(() => {
    const updateHandleColors = () => {
      // Remove all existing connection state classes
      document.querySelectorAll('.react-flow__handle').forEach(handle => {
        handle.classList.remove('connected-input', 'connected-output');
      });

      // Mark only the handles that are actually connected
      edges.forEach(edge => {
        // ReactFlow uses data-nodeid and data-handleid attributes on handles
        
        // Target handle (input) - use data-nodeid and data-handleid
        const targetSelectors = [
          `.react-flow__handle[data-nodeid="${edge.target}"][data-handleid="${edge.targetHandle}"]`,
          `.react-flow__handle-target[data-nodeid="${edge.target}"]`,
        ];
        
        let targetHandle: HTMLElement | null = null;
        for (const sel of targetSelectors) {
          targetHandle = document.querySelector(sel) as HTMLElement;
          if (targetHandle) {
            targetHandle.classList.add('connected-input');
            break;
          }
        }
        
        // Source handle (output) - use data-nodeid and data-handleid
        const sourceSelectors = [
          `.react-flow__handle[data-nodeid="${edge.source}"][data-handleid="${edge.sourceHandle}"]`,
          `.react-flow__handle-source[data-nodeid="${edge.source}"]`,
        ];
        
        let sourceHandle: HTMLElement | null = null;
        for (const sel of sourceSelectors) {
          sourceHandle = document.querySelector(sel) as HTMLElement;
          if (sourceHandle) {
            sourceHandle.classList.add('connected-output');
            break;
          }
        }
      });
    };

    // Call immediately
    updateHandleColors();

    // Also call after a short delay to catch DOM updates
    const timeoutId = setTimeout(updateHandleColors, 100);

    return () => clearTimeout(timeoutId);
  }, [edges]);

  // Use refs to access current values without causing re-renders
  const edgesRef = useRef<Edge[]>(edges);
  const nodesRef = useRef<Node[]>(nodes);

  useEffect(() => {
    edgesRef.current = edges;
    nodesRef.current = nodes;
  }, [edges, nodes]);

  // Load custom node definitions from storage on mount
  useEffect(() => {
    const loadCustomNodes = () => {
      try {
        const customDefs = CustomNodeStorage.getAll();
        setCustomNodeDefinitions(customDefs);
        logger.info('[CreativeNodeFlow] Loaded custom node definitions:', customDefs.length);
      } catch (error) {
        logger.error('[CreativeNodeFlow] Failed to load custom nodes:', error);
        alertService.error('Failed to load custom nodes');
      }
    };

    loadCustomNodes();
  }, []);

  // Reload custom nodes when builder closes (after save)
  const handleCustomNodeBuilderSave = useCallback(() => {
    const customDefs = CustomNodeStorage.getAll();
    setCustomNodeDefinitions(customDefs);
    
    // Update all existing custom node instances with the latest definitions
    // IMPORTANT: Preserve onOutput and onReceiveInput handlers to maintain connections
    setNodes((currentNodes) => 
      currentNodes.map((node) => {
        // Check if this is a custom node
        if (node.type?.startsWith('custom-')) {
          const customDefId = node.type.replace('custom-', '');
          const updatedDef = customDefs.find(def => def.id === customDefId);
          
          if (updatedDef && node.data?.customNodeDefinition) {
            const oldDef = node.data.customNodeDefinition as any;
            logger.debug('[CreativeNodeFlow] Updating custom node instance:', {
              nodeId: node.id,
              definitionId: updatedDef.id,
              oldName: oldDef.name,
              newName: updatedDef.name
            });
            
            return {
              ...node,
              data: {
                ...node.data,
                customNodeDefinition: updatedDef,
                // Preserve the handlers - these maintain the connections and context
                onOutput: node.data.onOutput,
                onReceiveInput: node.data.onReceiveInput
              }
            };
          }
        }
        return node;
      })
    );
    
    markFlowDirty(); // direct setNodes bypasses onNodesChange
    logger.info('[CreativeNodeFlow] Reloaded custom node definitions and updated all instances');
  }, [setNodes, markFlowDirty]);

  // Helper function to get connector type from node
  const getConnectorType = useCallback((node: Node, handleId: string | null, handleType: 'source' | 'target'): ConnectorType => {
    const nodeType = node.type || 'unknown';
    
    // Special handling for nodes with multiple inputs of different types
    if (handleType === 'target' && handleId) {
      // Creative Director (agentPrompt): text input (top) + image input (bottom)
      if (nodeType === 'agentPrompt') {
        if (handleId === 'input-text') return 'text';
        if (handleId === 'input-image') return 'image';
      }
      
      // Art Director (imagePrompt): text input (top) + image input (bottom)
      if (nodeType === 'imagePrompt') {
        if (handleId === 'input-text') return 'text';
        if (handleId === 'input-image') return 'image';
      }
      
      // GPT Image 1.5 (gptImage): text input (top) + image input (bottom)
      if (nodeType === 'gptImage') {
        if (handleId === 'input-text') return 'text';
        if (handleId === 'input-image') return 'image';
      }
      
      // FLUX Image (fluxImage): text input (top) + image input (bottom)
      if (nodeType === 'fluxImage') {
        if (handleId === 'input-text') return 'text';
        if (handleId === 'input-image') return 'image';
      }

      // Kling Image (klingImage): text input (top) + reference-image input
      // (bottom, up to 10). Without this the input-image handle falls through
      // to the nodeTypeMapping default below and reports 'text', so an image
      // edge is typed/coloured as text and the auto-snap picks the wrong handle.
      if (nodeType === 'klingImage') {
        if (handleId === 'input-text') return 'text';
        if (handleId === 'input-image') return 'image';
      }

      // Fal.ai video nodes: text input (top) + optional image input (bottom)
      const falAIVideoTypes = ['klingVideo', 'kling26Video', 'kling25Video', 'klingO3Video', 'wanVideo', 'wan26Video', 'wanAnimateVideo', 'ltxVideo', 'ltxFastVideo', 'minimaxVideo', 'pixverseVideo', 'hunyuanVideo', 'klingAvatar', 'veoVideo', 'grokVideo', 'seedance25Video', 'seedance2Video', 'minimaxH3Video'];
      if (falAIVideoTypes.includes(nodeType)) {
        if (handleId === 'input-text') return 'text';
        if (handleId === 'input-image') return 'image';
        // VeoVideo first-last-frame mode has two image inputs
        if (handleId === 'input-image-first') return 'image';
        if (handleId === 'input-image-last') return 'image';
      }
    }
    
    // Default mapping based on node type
    const nodeTypeMapping: Record<string, { source: ConnectorType; target: ConnectorType }> = {
      startingPrompt: { source: 'text', target: 'text' },
      agentPrompt: { source: 'text', target: 'text' }, // Default to text if handleId not specified
      chatGPT: { source: 'text', target: 'text' },
      claude: { source: 'text', target: 'text' },
      kimi: { source: 'text', target: 'text' },
      geminiText: { source: 'text', target: 'text' },
      grokText: { source: 'text', target: 'text' },
      imagePrompt: { source: 'image', target: 'text' }, // Default to text if handleId not specified
      gptImage: { source: 'image', target: 'text' }, // GPT Image 1.5
      fluxImage: { source: 'image', target: 'text' }, // FLUX Image
      zImageTurbo: { source: 'image', target: 'text' },
      qwenImage: { source: 'image', target: 'text' },
      qwenImageEdit: { source: 'image', target: 'image' }, // Edit node: acceptsMediaTypes is ['image']
      seedream: { source: 'image', target: 'text' },
      recraftV3: { source: 'image', target: 'text' },
      grokImage: { source: 'image', target: 'text' },
      glmImage: { source: 'image', target: 'text' },
      // Fallback only — the explicit input-text / input-image resolution for
      // klingImage happens in the multi-input block above.
      klingImage: { source: 'image', target: 'text' },
      // Fal.ai video nodes - T2V + I2V
      klingVideo: { source: 'video', target: 'any' },    // T2V + I2V + FFLF
      kling26Video: { source: 'video', target: 'any' },  // T2V + I2V
      kling25Video: { source: 'video', target: 'any' },  // T2V + I2V
      klingO3Video: { source: 'video', target: 'any' },  // T2V + I2V
      wanVideo: { source: 'video', target: 'any' },      // T2V + I2V
      wan26Video: { source: 'video', target: 'any' },    // T2V + I2V
      wanAnimateVideo: { source: 'video', target: 'any' }, // T2V + I2V
      ltxVideo: { source: 'video', target: 'any' },      // T2V + I2V
      minimaxVideo: { source: 'video', target: 'any' },  // T2V + I2V
      veoVideo: { source: 'video', target: 'any' },      // T2V + I2V
      geminiOmni: { source: 'video', target: 'any' },    // T2V + I2V (Google direct)
      grokVideo: { source: 'video', target: 'any' },      // T2V + I2V
      seedance2Video: { source: 'video', target: 'any' }, // T2V + I2V
      seedance25Video: { source: 'video', target: 'any' }, // T2V + I2V + FFLF
      minimaxH3Video: { source: 'video', target: 'any' }, // T2V + I2V + FFLF
      // Fal.ai video nodes - T2V only
      ltxFastVideo: { source: 'video', target: 'text' }, // T2V only
      pixverseVideo: { source: 'video', target: 'text' }, // T2V only
      hunyuanVideo: { source: 'video', target: 'text' },  // T2V only
      // Fal.ai video nodes - I2V only
      klingAvatar: { source: 'video', target: 'image' },  // I2V only (lip sync)
      imagePanel: { source: 'image', target: 'image' },
      inpaint: { source: 'image', target: 'image' },
      imageScale: { source: 'image', target: 'image' },
      documentPanel: { source: 'text', target: 'text' },
      dictation: { source: 'text', target: 'text' },
    };

    // Special handling for Output nodes - connector type matches content type
    if (nodeType === 'customOutput' || nodeType === 'universalOutput') {
      const contentType = node.data?.type as string | undefined;
      // If the node has content, use that type; otherwise default to 'any'
      if (contentType && ['text', 'image', 'video', 'audio'].includes(contentType)) {
        return contentType as ConnectorType;
      }
      return 'any';
    }

    const mapping = nodeTypeMapping[nodeType];
    
    if (!mapping) {
      return 'any'; // Default to 'any' for unknown nodes
    }
    
    return handleType === 'source' ? mapping.source : mapping.target;
  }, []);

  // Check if a node type is a generator (produces media like images/videos)
  // Uses the registry to dynamically get all generator types
  const isGeneratorNodeType = useCallback((nodeType: string | undefined): boolean => {
    return isGeneratorNode(nodeType);
  }, []);

  // Normalize legacy/default target handle IDs against the handles the target
  // node actually renders. Shares resolveTargetHandle with the load-time
  // migration so the two can't disagree about what a handle id means.
  //
  // Unlike the migration this never drops an edge: it runs on every nodes/edges
  // change, including the instant a setting is mid-flight, so an unresolvable
  // handle is left untouched rather than deleted.
  const normalizeTargetHandleForNode = useCallback((targetNode: Node, targetHandle: string | null | undefined): string | null => {
    if (!targetHandle) return null;
    const { keep, targetHandle: resolved } = resolveTargetHandle(
      targetNode.type || '',
      targetNode.data,
      targetHandle,
    );
    return keep ? resolved : targetHandle;
  }, []);

  // Sanitize already-loaded edges to prevent ReactFlow handle-id spam for legacy "input" handles.
  useEffect(() => {
    if (nodes.length === 0 || edges.length === 0) return;

    let changed = false;
    const normalizedEdges = edges.flatMap((edge) => {
      const targetNode = nodes.find((node) => node.id === edge.target);
      if (!targetNode) return edge;

      const sourceNode = nodes.find((node) => node.id === edge.source);
      const expectedEdgeType = sourceNode
        ? getConnectorType(sourceNode, edge.sourceHandle ?? null, 'source')
        : (edge.type as ConnectorType) || 'any';
      const expectedEdgeColor = EDGE_COLOR_MAP[expectedEdgeType] || EDGE_COLOR_MAP.any;

      const targetRegistration = getNodeByReactFlowType(targetNode.type || '');
      if (targetRegistration?.definition?.hasInputHandle === false) {
        // hasInputHandle:false can mean either truly no inputs, or "uses dynamic
        // labeled handles" (e.g., VideoStitch's per-clip handles). Only drop the
        // edge if no input handles are actually exposed for this node.
        const dynamicHandles = getInputHandlesForNodeType(targetNode.type || '', targetNode.data);
        if (dynamicHandles.length === 0) {
          changed = true;
          return [];
        }
      }

      const normalizedTargetHandle = normalizeTargetHandleForNode(targetNode, edge.targetHandle);
      const currentTargetHandle = edge.targetHandle ?? null;
      const currentStroke = (edge.style as React.CSSProperties)?.stroke;
      const currentStrokeWidth = (edge.style as React.CSSProperties)?.strokeWidth;

      const needsUpdate =
        normalizedTargetHandle !== currentTargetHandle ||
        edge.type !== expectedEdgeType ||
        currentStroke !== expectedEdgeColor ||
        currentStrokeWidth !== 3;

      if (!needsUpdate) {
        return edge;
      }

      changed = true;
      return {
        ...edge,
        targetHandle: normalizedTargetHandle,
        type: expectedEdgeType,
        style: {
          ...edge.style,
          stroke: expectedEdgeColor,
          strokeWidth: 3,
        },
      };
    });

    if (changed) {
      setEdges(normalizedEdges);
    }
  }, [nodes, edges, normalizeTargetHandleForNode, setEdges, getConnectorType]);

  // Helper function to validate connection between two connectors
  const validateConnection = useCallback((sourceNode: Node, targetNode: Node, connection: Connection): boolean => {
    const sourceType = getConnectorType(sourceNode, connection.sourceHandle, 'source');
    const targetNodeType = targetNode.type || 'unknown';

    logger.debug('[validateConnection]', {
      source: sourceNode.type,
      sourceType,
      target: targetNodeType,
      targetHandle: connection.targetHandle
    });

    // Block passive-to-passive connections: two containers/sinks/outputs with no
    // transformer between them just mirrors content with no useful work.
    // Silently rejected — the visual lack of an edge is enough feedback and a
    // toast would fire on every incidental near-miss during drag or
    // multi-target auto-connect, which is noisy and confusing.
    if (isPassiveNode(sourceNode.type) && isPassiveNode(targetNodeType)) {
      logger.debug('[validateConnection] Rejected passive→passive connection', {
        source: sourceNode.type,
        target: targetNodeType,
      });
      return false;
    }

    // Use the registry to check if the target node accepts the source type
    // findCompatibleInputHandle returns null if no compatible handle exists
    // Pass targetNode.data for mode-dependent handle detection (e.g., VEO3 first-last-frame)
    const compatibleHandle = findCompatibleInputHandle(targetNodeType, sourceType, targetNode.data);
    const normalizedCompatibleHandle = compatibleHandle
      ? normalizeTargetHandleForNode(targetNode, compatibleHandle)
      : null;
    
    if (!compatibleHandle) {
      logger.debug('[validateConnection] No compatible handle found - rejecting connection');
      return false;
    }
    
    // If a specific target handle is specified, verify it matches
    if (
      connection.targetHandle &&
      connection.targetHandle !== compatibleHandle &&
      connection.targetHandle !== normalizedCompatibleHandle
    ) {
      // Check if the specified handle accepts this type (support handles like input-image-first/last)
      const specifiedHandleType = connection.targetHandle.replace('input-', '').split('-')[0];
      if (specifiedHandleType !== sourceType && specifiedHandleType !== 'any' && sourceType !== 'any') {
        logger.debug('[validateConnection] Specified handle type mismatch', {
          specifiedHandle: connection.targetHandle,
          specifiedType: specifiedHandleType,
          sourceType
        });
        return false;
      }
    }
    
    return true;
  }, [getConnectorType, normalizeTargetHandleForNode]);

  // Callback for ReactFlow to validate connections during drag
  // This prevents invalid connections from visually snapping
  const isValidConnection = useCallback((connection: Connection | Edge): boolean => {
    // Type guard: Edge has 'id', Connection does not
    if ('id' in connection) {
      // It's an Edge - we only validate new connections
      return true;
    }
    if (!connection.source || !connection.target) return false;
    
    const sourceNode = nodesRef.current.find(n => n.id === connection.source);
    const targetNode = nodesRef.current.find(n => n.id === connection.target);
    
    if (!sourceNode || !targetNode) return false;
    
    return validateConnection(sourceNode, targetNode, connection);
  }, [validateConnection]);

  // Helper function to get the highest z-index from existing nodes
  const getHighestZIndex = useCallback((): number => {
    const currentNodes = nodesRef.current;
    let maxZ = 0;
    currentNodes.forEach(node => {
      if (node.zIndex && node.zIndex > maxZ) {
        maxZ = node.zIndex;
      }
    });
    return maxZ + 1;
  }, []);

  // Store handler functions in a ref to maintain stable references
  const nodeHandlerCache = useRef<Map<string, { onOutput: (data: OutputData) => void; onReceiveInput: (handler: InputHandlerCallback) => void }>>(new Map());

  // Keep a ref to avoid circular dependency
  const handleNodeOutputRef = useRef<((data: OutputData) => void) | null>(null);

  /**
   * Creates and caches handlers for a node
   * Called ONCE per node at creation time
   */
  const createNodeHandlers = useCallback((nodeId: string) => {
    // Check cache first
    if (nodeHandlerCache.current.has(nodeId)) {
      return nodeHandlerCache.current.get(nodeId)!;
    }
    
    // Create new handlers
    const handlers = {
      onOutput: (data: OutputData) => handleNodeOutputRef.current?.(data),
      onReceiveInput: (handler: InputHandlerCallback) => {
        nodeInputHandlers.current.set(nodeId, handler);
      }
    };
    
    // Cache for cleanup
    nodeHandlerCache.current.set(nodeId, handlers);
    
    return handlers;
  }, []);

  // Insert a Image Container node between two generator nodes and create edges
  const insertMediaContainerBetween = useCallback((
    sourceNode: Node,
    targetNode: Node,
    sourceHandle: string | null,
  ) => {
    const NODE_GAP = 100;
    const mediaNodeId = `output-${Date.now()}`;
    // Spawn at the same size as generation-created output nodes (registry-driven)
    const { width: mediaNodeWidth, height: mediaNodeHeight } = getOutputNodeDimensions('image');

    const sourceNodeWidth =
      (sourceNode.measured?.width as number) ||
      (sourceNode.style?.width as number) ||
      UI_DIMENSIONS.NODE_MIN_WIDTH;

    const sourceRightEdge = sourceNode.position.x + sourceNodeWidth;
    const targetLeftEdge = targetNode.position.x;
    const currentGap = targetLeftEdge - sourceRightEdge;

    const totalSpaceNeeded = NODE_GAP + mediaNodeWidth + NODE_GAP;
    const needsToMoveTarget = currentGap < totalSpaceNeeded;

    const mediaX = sourceRightEdge + NODE_GAP;
    const mediaY = (sourceNode.position.y + targetNode.position.y) / 2;

    const newTargetX = needsToMoveTarget
      ? mediaX + mediaNodeWidth + NODE_GAP
      : targetLeftEdge;

    const mediaNode = {
      id: mediaNodeId,
      type: 'customOutput',
      position: { x: mediaX, y: mediaY },
      zIndex: getHighestZIndex(),
      style: { width: mediaNodeWidth, height: mediaNodeHeight },
      data: {
        definition: OUTPUT_NODE_DEF,
        content: '',
        type: 'image',
        outputsMediaType: 'image',
        ...createNodeHandlers(mediaNodeId),
      },
    };

    const sourceType = getConnectorType(sourceNode, sourceHandle, 'source');

    recordUserAction('Connect nodes');
    setNodes((nds) => {
      const updated = needsToMoveTarget
        ? nds.map((node) =>
            node.id === targetNode.id
              ? { ...node, position: { ...node.position, x: newTargetX } }
              : node
          )
        : nds;
      return [...updated, mediaNode];
    });

    setEdges((eds) => [
      ...eds,
      {
        id: `${sourceNode.id}-${mediaNodeId}`,
        source: sourceNode.id,
        sourceHandle,
        target: mediaNodeId,
        targetHandle: null,
        type: sourceType,
        animated: true,
        style: { stroke: EDGE_COLOR_MAP[sourceType] || EDGE_COLOR_MAP.any, strokeWidth: 3 },
      },
      {
        id: `${mediaNodeId}-${targetNode.id}`,
        source: mediaNodeId,
        sourceHandle: null,
        target: targetNode.id,
        targetHandle: findCompatibleInputHandle(targetNode.type || '', 'image', targetNode.data) || null,
        type: 'image' as any,
        animated: true,
        style: { stroke: EDGE_COLOR_MAP.image, strokeWidth: 3 },
      },
    ]);

    markFlowDirty(); // direct setNodes/setEdges bypass on*Change
  }, [setNodes, setEdges, createNodeHandlers, getHighestZIndex, getConnectorType, markFlowDirty]);

  // Import Knod handler - must be after createNodeHandlers
  const handleImportKnod = useCallback(async () => {
    // Create a file input and trigger it
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.knod,.json,.zip';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      try {
        const result = await knodExportService.importFromFile(file);
        
        // Update nodes with proper handlers
        const updatedNodes = result.nodes.map(node => {
          const handlers = createNodeHandlers(node.id);
          return {
            ...node,
            data: {
              ...node.data,
              ...handlers,
            },
          };
        });

        setNodes(updatedNodes);
        setEdges(result.edges);
        flowHistory.clear(); // a different graph: old steps must not apply to it
        saveSessionsRef.current?.replaceContent(); // ...and its saves must not write this one
        setCurrentFlowName(result.flowName);
        currentFlowNameRef.current = result.flowName;
        
        // Reset flow ID since this is a new import
        setCurrentFlowId(null);
        
        // Apply viewport if available
        if (result.viewport) {
          setViewport(result.viewport);
          // Note: duration animation is handled by ReactFlow internally
        }

        // If there are media files, show a message about them
        if (result.mediaFiles && result.mediaFiles.size > 0) {
          toastService.success(`Knod imported with ${result.mediaFiles.size} media files`);
          // TODO: Upload media files to R2 storage and update node URLs
        } else {
          toastService.success('Knod imported successfully');
        }
      } catch (error) {
        logger.error('[CreativeNodeFlow] Import failed:', error);
        toastService.error('Failed to import Knod');
      }
    };
    input.click();
  }, [setNodes, setEdges, setCurrentFlowName, setCurrentFlowId, setViewport, createNodeHandlers, flowHistory]);

  // Handle node output events - optimized to reduce re-renders
  const handleNodeOutput = useCallback((outputData: OutputData) => {
    const { nodeId, content, context, type } = outputData;

    // Find edges that originate from this node
    const currentEdges = edgesRef.current;
    const currentNodes = nodesRef.current;
    const outgoingEdges = currentEdges.filter(edge => edge.source === nodeId);

    // Send data to connected target nodes
    outgoingEdges.forEach(edge => {
      const inputHandler = nodeInputHandlers.current.get(edge.target);
      if (inputHandler) {
        inputHandler({ content, context, type });
      }
    });

    // Auto-create output node if none exists and it's not already an output node
    if (outgoingEdges.length === 0) {
      const sourceNode = currentNodes.find(n => n.id === nodeId);

      // Don't auto-create if the source is already an output node or if we're already creating one
      if (sourceNode && sourceNode.type !== 'customOutput') {
        // Check if there's already an auto-output for this node
        const existingAutoOutput = currentNodes.find(n =>
          n.id.startsWith(`auto-output-${nodeId}`) ||
          currentEdges.some(edge => edge.source === nodeId && edge.target === n.id && n.type === 'customOutput')
        );

        if (!existingAutoOutput) {
          const newOutputId = `auto-output-${nodeId}-${Date.now()}`;
          
          // Calculate position using source node's actual measured dimensions
          // Priority: measured > computed > explicit width > type-specific default > general default
          let sourceWidth = sourceNode.measured?.width || (sourceNode as any).computed?.width || sourceNode.width;
          
          // If still no width, use type-specific defaults
          if (!sourceWidth) {
            if (sourceNode.type === 'startingPrompt' || sourceNode.type === 'agentPrompt') {
              sourceWidth = 448; // Starting Prompt and Writer width
            } else {
              sourceWidth = UI_DIMENSIONS.NODE_MIN_WIDTH;
            }
          }
          
          console.log('[Auto-Output] Source node:', {
            id: nodeId,
            type: sourceNode.type,
            position: sourceNode.position,
            width: sourceNode.width,
            measuredWidth: sourceNode.measured?.width,
            computedWidth: (sourceNode as any).computed?.width,
            usedWidth: sourceWidth
          });
          
          const spacing = 50;
          
          // Position to the right of the source node, accounting for its actual size
          // Position is in flow coordinates (not affected by zoom/pan)
          const position = {
            x: sourceNode.position.x + sourceWidth + spacing,
            y: sourceNode.position.y
          };
          
          // Create handlers for the new output node
          const handlers = createNodeHandlers(newOutputId);
          
          // Map content type to connector type for edge color (shared EDGE_COLOR_MAP)
          const edgeType = type || 'any';
          const edgeColor = EDGE_COLOR_MAP[edgeType] || EDGE_COLOR_MAP['any'];
          const outputDims = getOutputNodeDimensions(type);

          const newOutputNode: any = {
            id: newOutputId,
            position,
            type: 'customOutput',
            width: outputDims.width,
            height: outputDims.height,
            zIndex: getHighestZIndex(),
            data: {
              content,
              context,
              type,
              onReceiveInput: handlers.onReceiveInput,
              onOutput: handlers.onOutput
            },
          };

          const newEdge: Edge = {
            id: `${nodeId}-${newOutputId}`,
            source: nodeId,
            target: newOutputId,
            type: edgeType,
            animated: true,
            style: { stroke: edgeColor, strokeWidth: 3 }
          };

          setNodes(nodes => [...nodes, newOutputNode]);
          setEdges(edges => [...edges, newEdge]);
          markFlowDirty(); // direct setNodes/setEdges bypass on*Change

          // Immediately send data to the new output node
          setTimeout(() => {
            const inputHandler = nodeInputHandlers.current.get(newOutputId);
            if (inputHandler) {
              inputHandler({ content, context, type });
            }
          }, 100);
        }
      }
    }
  }, [getHighestZIndex, createNodeHandlers, markFlowDirty]);

  // Update ref after handleNodeOutput is defined
  useEffect(() => {
    handleNodeOutputRef.current = handleNodeOutput;
  }, [handleNodeOutput]);

  // Apply animation classes to nodes (separate from handler injection)
  const animatedNodes = useMemo(() => {
    return nodes.map(node => {
      const classes: string[] = [];
      if (animatedNodeIds.has(node.id)) classes.push('react-flow__node-animated');
      if (deletingNodeIds.has(node.id)) classes.push('react-flow__node-deleting');
      const newClassName = classes.join(' ');
      
      // Only update if className actually changed
      if (node.className === newClassName) {
        return node;
      }
      
      return { ...node, className: newClassName };
    });
  }, [nodes, animatedNodeIds, deletingNodeIds]);

  // Cleanup handler cache for removed nodes
  useEffect(() => {
    const currentNodeIds = new Set(nodes.map(n => n.id));
    const cachedNodeIds = Array.from(nodeHandlerCache.current.keys());
    
    cachedNodeIds.forEach(nodeId => {
      if (!currentNodeIds.has(nodeId)) {
        nodeHandlerCache.current.delete(nodeId);
        nodeInputHandlers.current.delete(nodeId);
      }
    });
  }, [nodes]);

  // Helper to add nodes with animation.
  // Lives in its own hook so the "adding a node marks the flow dirty" rule is a
  // property of the add operation (and is unit-testable) instead of something each
  // of the six call sites has to remember. See src/hooks/useAddNodeWithAnimation.ts.
  const addNodeWithAnimation = useAddNodeWithAnimation({
    setNodes,
    setAnimatedNodeIds,
    createNodeHandlers,
    getHighestZIndex,
    markDirty: markFlowDirty,
    recordHistory: recordUserAction,
  });

  const onConnect: OnConnect = useCallback(
    (params: Connection) => {
      console.log('[CreativeNodeFlow] Connection attempt:', params);
      
      // Validate connection types
      const sourceNode = nodes.find(n => n.id === params.source);
      const targetNode = nodes.find(n => n.id === params.target);
      
      if (!sourceNode || !targetNode) {
        console.warn('[CreativeNodeFlow] Source or target node not found');
        alertService.error('Connection failed: Node not found');
        return;
      }
      
      // Check if both nodes are generators - if so, insert a Image Container between them
      if (isGeneratorNodeType(sourceNode.type) && isGeneratorNodeType(targetNode.type)) {
        console.log('[CreativeNodeFlow] Generator-to-generator connection detected, inserting Image Container');
        insertMediaContainerBetween(sourceNode, targetNode, params.sourceHandle);
        setIsConnecting(false);
        return;
      }
      
      // Get source type for auto-snapping and edge styling
      const sourceType = getConnectorType(sourceNode, params.sourceHandle, 'source');
      
      // Check if connection is compatible - if not, try to find a compatible handle (auto-snap)
      let finalParams = params;
      if (!validateConnection(sourceNode, targetNode, params)) {
        const targetType = getConnectorType(targetNode, params.targetHandle, 'target');
        console.log('[CreativeNodeFlow] Connection incompatible, attempting auto-snap:', { sourceType, targetType });
        
        // Try to find a compatible handle on the target node
        // Pass targetNode.data for mode-dependent handle detection (e.g., VEO3 first-last-frame)
        const compatibleHandle = findCompatibleInputHandle(targetNode.type || '', sourceType, targetNode.data);
        
        if (compatibleHandle) {
          // Found a compatible handle - redirect the connection
          console.log('[CreativeNodeFlow] Auto-snapping to compatible handle:', compatibleHandle);
          finalParams = {
            ...params,
            targetHandle: normalizeTargetHandleForNode(targetNode, compatibleHandle),
          };
          
          // Verify the redirected connection is valid
          if (!validateConnection(sourceNode, targetNode, finalParams)) {
            console.warn('[CreativeNodeFlow] Auto-snap failed validation');
            alertService.warning(`Cannot connect ${sourceType} output to this node`);
            setIsConnecting(false);
            return;
          }
        } else {
          // No compatible handle found
          console.warn('[CreativeNodeFlow] Incompatible connection, no compatible handle found:', { sourceType, targetType });
          alertService.warning(`Cannot connect ${sourceType} output to ${targetType} input`);
          setIsConnecting(false);
          return;
        }
      }
      
      // Get edge type and color based on source connector type (shared EDGE_COLOR_MAP)
      const edgeType = sourceType;

      setIsConnecting(false); // Connection was successful
      const normalizedFinalParams = {
        ...finalParams,
        targetHandle: normalizeTargetHandleForNode(targetNode, finalParams.targetHandle),
      };

      recordUserAction('Connect nodes');
      setEdges((eds) => addEdge({
        ...normalizedFinalParams,
        id: `${normalizedFinalParams.source}-${normalizedFinalParams.target}${normalizedFinalParams.targetHandle ? `-${normalizedFinalParams.targetHandle}` : ''}`,
        type: edgeType,
        animated: true,
        style: { stroke: EDGE_COLOR_MAP[edgeType] || EDGE_COLOR_MAP.any, strokeWidth: 3 }
      }, eds));

      // onConnect writes edges directly; onEdgesChange never fires for it.
      markFlowDirty();

      // After creating the connection, check if source node has existing output data
      // and immediately transmit it to the target node
      setTimeout(() => {
        const sourceNode = nodes.find(n => n.id === normalizedFinalParams.source);
        if (sourceNode && sourceNode.data) {
          // Check if source node has existing content to transmit
          let { content, context, type } = sourceNode.data as any;
          
          // Special handling for Image Container nodes
          if (sourceNode.type === 'imagePanel' && (sourceNode.data as any).imageUrl) {
            const imageUrl = (sourceNode.data as any).imageUrl;
            content = imageUrl;
            type = 'image';
            // Build context if not already present
            if (!context) {
              const mimeType = typeof imageUrl === 'string' && imageUrl.startsWith('data:')
                ? imageUrl.split(';')[0].split(':')[1] || 'image/png'
                : 'image/png';
              context = {
                messages: [
                  {
                    role: 'user' as const,
                    content: [
                      { type: 'text' as const, text: 'Image from Image Container' },
                      { type: 'image' as const, imageUrl, mimeType }
                    ]
                  }
                ]
              };
            }
          }
          // Special handling for Text Container nodes
          else if (sourceNode.type === 'documentPanel' && (sourceNode.data as any).content) {
            const documentContent = (sourceNode.data as any).content;
            content = documentContent;
            type = 'text';
            if (!context) {
              context = {
                messages: [
                  {
                    role: 'user' as const,
                    content: `Document: ${(sourceNode.data as any).fileName || 'Untitled'}\n\n${documentContent}`
                  }
                ]
              };
            }
          }
          
          if (content || context) {
            const targetHandler = nodeInputHandlers.current.get(normalizedFinalParams.target!);
            if (targetHandler) {
              logger.debug(`[onConnect] Transmitting existing data from ${normalizedFinalParams.source} to ${normalizedFinalParams.target}:`, {
                content: content ? (typeof content === 'string' ? content.substring(0, 50) + '...' : 'Non-string content') : 'No content',
                hasContext: !!context,
                type
              });
              targetHandler({ content, context, type, nodeId: normalizedFinalParams.source });
            }
          }
        }
      }, 100);
    },
    [nodes, validateConnection, getConnectorType, findCompatibleInputHandle, normalizeTargetHandleForNode, insertMediaContainerBetween, markFlowDirty],
  );

  // Use ReactFlow's built-in coordinate transformation (more reliable)
  const screenToFlowPosition = useCallback((screenX: number, screenY: number): { x: number; y: number } => {
    return reactFlowScreenToFlowPosition({ x: screenX, y: screenY });
  }, [reactFlowScreenToFlowPosition]);


  // Handle connection end (when user releases connection without connecting)
  const onConnectEnd = useCallback(
    (event: MouseEvent | TouchEvent, connectionState?: any) => {
      console.log('[CreativeNodeFlow] onConnectEnd triggered', {
        target: event.target,
        connectionState,
        eventType: event.type,
        isConnecting
      });

      // Handle both mouse and touch events
      let clientX: number, clientY: number;
      if (event instanceof MouseEvent) {
        clientX = event.clientX;
        clientY = event.clientY;
      } else {
        // TouchEvent
        const touch = event.changedTouches[0];
        clientX = touch.clientX;
        clientY = touch.clientY;
      }

      // Use a small delay to check if a connection was actually made
      setTimeout(() => {
        if (isConnecting) {
          // Connection was started but not completed - show menu
          console.log('[CreativeNodeFlow] Connection failed, showing context menu');
          
          const flowPosition = screenToFlowPosition(clientX, clientY);

          // Calculate menu position with viewport boundary checking
          const menuWidth = 200; // approximate menu width
          const menuHeight = 400; // approximate menu height
          const padding = 8;
          
          let x = clientX;
          let y = clientY;
          
          // Check if menu would go off the right edge
          if (x + menuWidth > window.innerWidth - padding) {
            x = window.innerWidth - menuWidth - padding;
          }
          
          // Check if menu would go off the bottom edge
          if (y + menuHeight > window.innerHeight - padding) {
            y = window.innerHeight - menuHeight - padding;
          }
          
          // Ensure menu doesn't go off top or left edge
          if (x < padding) x = padding;
          if (y < padding) y = padding;

          setContextMenu({
            x,
            y,
            flowX: flowPosition.x,
            flowY: flowPosition.y,
          });
          
          setIsConnecting(false); // Reset the state
        }
      }, 50); // Small delay to allow onConnect to fire first if connection is successful
    },
    [screenToFlowPosition, isConnecting]
  );



  // Handle keyboard events for deletion, copy, and paste
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    // Check if we're in a text input/textarea - most operations should skip
    const target = event.target as HTMLElement;
    const isInTextInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;

    // Ctrl/Cmd+Z, Ctrl/Cmd+Shift+Z and Ctrl+Y are handled by useFlowHistory's
    // own document listener (text fields keep their native undo).
    if (getHistoryShortcut(event)) return;

    // Handle Shift+Space to toggle Iris floating panel
    if (event.shiftKey && event.code === 'Space') {
      event.preventDefault();
      if (irisMinimized) {
        setIrisMinimized(false);
        setIrisOpen(true);
      } else {
        setIrisOpen(prev => !prev);
      }
      return;
    }

    // Handle Ctrl+Shift+Enter to run flow
    if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'Enter') {
      event.preventDefault();
      if (!isExecuting) {
        runFullFlow();
      }
      return;
    }

    // Handle Ctrl+Enter (without Shift) to run selected nodes
    if ((event.ctrlKey || event.metaKey) && !event.shiftKey && event.key === 'Enter' && !isInTextInput) {
      event.preventDefault();
      if (!isExecuting) {
        const selectedNodes = nodes.filter(node => node.selected);
        if (selectedNodes.length >= 1) {
          // Run selected nodes (works for 1 or more)
          const selectedIds = selectedNodes.map(n => n.id);
          runSelectedNodes(selectedIds);
        }
      }
      return;
    }

    // Handle Ctrl+C to copy selected nodes
    if ((event.ctrlKey || event.metaKey) && event.key === 'c' && !isInTextInput) {
      const selectedNodes = nodes.filter(node => node.selected);
      if (selectedNodes.length > 0) {
        event.preventDefault(); // Prevent default copy behavior
        
        // Also copy edges between selected nodes
        const selectedNodeIds = new Set(selectedNodes.map(n => n.id));
        const selectedEdges = edges.filter(
          edge => selectedNodeIds.has(edge.source) && selectedNodeIds.has(edge.target)
        );
        
        // Store in internal clipboard
        nodeClipboard.current = { 
          nodes: selectedNodes.map(n => ({ ...n })), 
          edges: selectedEdges.map(e => ({ ...e })) 
        };
        
        // Also write to system clipboard with a marker so paste knows nodes were copied
        // This clears any previous image/file from the system clipboard
        const clipboardData = JSON.stringify({
          type: 'knods-nodes',
          nodeCount: selectedNodes.length,
          edgeCount: selectedEdges.length,
          timestamp: Date.now()
        });
        navigator.clipboard.writeText(clipboardData).catch(err => {
          logger.debug('Failed to write to system clipboard:', err);
        });
        
        logger.debug(`Copied ${selectedNodes.length} nodes and ${selectedEdges.length} edges to clipboard`);
        toastService.success(`Copied ${selectedNodes.length} node${selectedNodes.length > 1 ? 's' : ''}`);
      }
      return;
    }

    // Handle Escape to cancel execution
    if (event.key === 'Escape' && isExecuting) {
      event.preventDefault();
      cancelExecution();
      return;
    }

    // Handle Delete/Backspace
    if (event.key === 'Delete' || event.key === 'Backspace') {
      // Get selected nodes and edges
      const selectedNodes = nodes.filter(node => node.selected);
      const selectedEdges = edges.filter(edge => edge.selected);

      if (selectedNodes.length > 0 || selectedEdges.length > 0) {
        recordUserAction(
          selectedNodes.length === 0
            ? (selectedEdges.length === 1 ? 'Delete connection' : `Delete ${selectedEdges.length} connections`)
            : (selectedNodes.length === 1 ? 'Delete node' : `Delete ${selectedNodes.length} nodes`)
        );
      }

      if (selectedNodes.length > 0) {
        // Delete selected nodes and their connections
        const nodeIdsToDelete = selectedNodes.map(node => node.id);

        setNodes(currentNodes =>
          currentNodes.filter(node => !nodeIdsToDelete.includes(node.id))
        );

        setEdges(currentEdges =>
          currentEdges.filter(edge =>
            !nodeIdsToDelete.includes(edge.source) &&
            !nodeIdsToDelete.includes(edge.target)
          )
        );

        // Clear input handlers for deleted nodes
        nodeIdsToDelete.forEach(id => {
          nodeInputHandlers.current.delete(id);
        });

        markFlowDirty(); // direct setNodes/setEdges bypass on*Change
      }

      if (selectedEdges.length > 0) {
        // Delete selected edges
        const edgeIdsToDelete = selectedEdges.map(edge => edge.id);
        setEdges(currentEdges =>
          currentEdges.filter(edge => !edgeIdsToDelete.includes(edge.id))
        );
        markFlowDirty(); // direct setEdges bypasses onEdgesChange
      }
    }

    // Handle Ctrl+V paste
    if ((event.ctrlKey || event.metaKey) && event.key === 'v' && !isInTextInput) {
      event.preventDefault();
      
      // Helper function to paste nodes from internal clipboard
      const pasteNodesFromClipboard = () => {
        if (nodeClipboard.current.nodes.length === 0) {
          return;
        }
        
        const clipboardNodes = nodeClipboard.current.nodes;
        const clipboardEdges = nodeClipboard.current.edges;
        
        // Calculate paste position
        // If mouse is on canvas, use mouse position; otherwise use viewport center
        let pastePosition: { x: number; y: number };
        
        if (mousePositionRef.current) {
          // Paste at mouse cursor location
          pastePosition = reactFlowScreenToFlowPosition(mousePositionRef.current);
        } else {
          // Fallback: use viewport center
          const viewport = getViewport();
          const viewportCenter = {
            x: (window.innerWidth / 2 - viewport.x) / viewport.zoom,
            y: (window.innerHeight / 2 - viewport.y) / viewport.zoom
          };
          pastePosition = viewportCenter;
        }
        
        // Calculate the center of the copied nodes to paste them centered at cursor
        const nodesBounds = clipboardNodes.reduce(
          (acc, node) => ({
            minX: Math.min(acc.minX, node.position.x),
            minY: Math.min(acc.minY, node.position.y),
            maxX: Math.max(acc.maxX, node.position.x),
            maxY: Math.max(acc.maxY, node.position.y)
          }),
          { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity }
        );
        const nodesCenter = {
          x: (nodesBounds.minX + nodesBounds.maxX) / 2,
          y: (nodesBounds.minY + nodesBounds.maxY) / 2
        };
        
        // Calculate offset for pasted nodes (paste at cursor/center, offset from original center)
        const timestamp = Date.now();
        
        // Create ID mapping for new nodes
        const idMapping: Record<string, string> = {};
        
        // Create new nodes with offset positions and new IDs
        const newNodes: Node[] = clipboardNodes.map((node) => {
          const newId = `${node.type}-${timestamp}-${Math.random().toString(36).substr(2, 9)}`;
          idMapping[node.id] = newId;
          
          // Calculate node position relative to the paste position
          const offsetFromCenter = {
            x: node.position.x - nodesCenter.x,
            y: node.position.y - nodesCenter.y
          };
          
          return {
            ...node,
            id: newId,
            position: {
              x: pastePosition.x + offsetFromCenter.x,
              y: pastePosition.y + offsetFromCenter.y
            },
            selected: true, // Select pasted nodes
            data: {
              // A copy inherits no run state: a node copied mid-generation must
              // not paste as "generating" forever.
              ...stripRunState(node.data as Record<string, unknown>),
              // Recreate handlers for the new node
              ...createNodeHandlers(newId)
            }
          };
        });
        
        // Create new edges with updated source/target IDs
        const newEdges: Edge[] = clipboardEdges.map((edge) => ({
          ...edge,
          id: `e-${idMapping[edge.source] || edge.source}-${idMapping[edge.target] || edge.target}-${timestamp}`,
          source: idMapping[edge.source] || edge.source,
          target: idMapping[edge.target] || edge.target,
          selected: false
        }));
        
        // Deselect existing nodes and add new ones
        recordUserAction(newNodes.length === 1 ? 'Paste node' : `Paste ${newNodes.length} nodes`);
        setNodes(currentNodes => [
          ...currentNodes.map(n => ({ ...n, selected: false })),
          ...newNodes
        ]);
        
        if (newEdges.length > 0) {
          setEdges(currentEdges => [...currentEdges, ...newEdges]);
        }

        // Paste writes nodes/edges directly, so on*Change never fires for it.
        markFlowDirty();

        // Animate the new nodes
        setAnimatedNodeIds(prev => {
          const next = new Set(prev);
          newNodes.forEach(n => next.add(n.id));
          return next;
        });
        
        logger.debug(`Pasted ${newNodes.length} nodes and ${newEdges.length} edges`);
        toastService.success(`Pasted ${newNodes.length} node${newNodes.length > 1 ? 's' : ''}`);
      };

      // Helper to check for images/videos in clipboard and paste them
      const checkAndPasteMedia = () => {
        navigator.clipboard.read().then(async (clipboardItems) => {
          let foundMedia = false;
          
          for (const clipboardItem of clipboardItems) {
            for (const type of clipboardItem.types) {
              // Handle video paste
            if (type.startsWith('video/')) {
              foundMedia = true;
              try {
                const blob = await clipboardItem.getType(type);
                const fileName = `pasted-video-${Date.now()}.${type.split('/')[1] || 'mp4'}`;
                const file = new File([blob], fileName, { type });
                
                console.log('[CreativeNodeFlow] Pasting video:', fileName, type);
                
                // Create local URL for immediate preview
                const localUrl = URL.createObjectURL(blob);
                const nodeId = `output-${Date.now()}`;
                
                // Create output node at center of viewport immediately
                const reactFlowWrapper = reactFlowWrapperRef.current;
                if (reactFlowWrapper) {
                  const bounds = reactFlowWrapper.getBoundingClientRect();
                  const centerX = bounds.width / 2;
                  const centerY = bounds.height / 2;
                  
                  const flowPosition = reactFlowScreenToFlowPosition({
                    x: bounds.left + centerX,
                    y: bounds.top + centerY
                  });
                  
                  const outputDims = getOutputNodeDimensions('video');
                  const HEADER_HEIGHT = 44;

                  const newNode: any = {
                    id: nodeId,
                    type: 'universalOutput',
                    position: {
                      x: flowPosition.x - (outputDims.width / 2),
                      y: flowPosition.y - (HEADER_HEIGHT / 2)
                    },
                    width: outputDims.width,
                    height: outputDims.height,
                    zIndex: getHighestZIndex(),
                    data: {
                      pages: [{ type: 'video' as const, content: localUrl, timestamp: new Date().toISOString() }],
                      currentPageIndex: 0,
                      output: localUrl,
                      videoUrl: localUrl,
                      type: 'video',
                      outputsMediaType: 'video',
                      fileName,
                      isUploading: true, // Show loading indicator
                    }
                  };
                  
                  console.log('[CreativeNodeFlow] Adding pasted video output node with local URL:', newNode);
                  addNodeWithAnimation(newNode);
                  toastService.info(`Uploading "${fileName}"...`);
                  
                  // Upload to R2 in background
                  const uploadOptions = {
                    scope: 'flow',
                    flowId: currentFlowId || undefined,
                    skipDeduplication: true,
                  };
                  
                  fileStorageService.uploadFile(file, uploadOptions)
                    .then(fileInfo => {
                      console.log('[CreativeNodeFlow] Pasted video uploaded:', fileInfo);
                      // Update node with R2 URL
                      setNodes(nodes => nodes.map(n => 
                        n.id === nodeId ? {
                          ...n,
                          data: {
                            ...n.data,
                            pages: [{ type: 'video' as const, content: fileInfo.url, timestamp: new Date().toISOString() }],
                            output: fileInfo.url,
                            videoUrl: fileInfo.url,
                            fileId: fileInfo.id,
                            isUploading: false,
                          }
                        } : n
                      ));
                      // The node was marked dirty when it was added, but that save may
                      // already have run with the blob: URL — which is dead on reload.
                      // Mark again so the R2 URL is what actually persists.
                      markFlowDirty();
                      // Clean up local URL
                      URL.revokeObjectURL(localUrl);
                      toastService.success(`Video "${fileName}" uploaded`);
                    })
                    .catch(uploadError => {
                      console.error('[CreativeNodeFlow] Pasted video upload failed:', uploadError);
                      // Keep local URL but mark as failed
                      setNodes(nodes => nodes.map(n => 
                        n.id === nodeId ? {
                          ...n,
                          data: { ...n.data, isUploading: false, uploadFailed: true }
                        } : n
                      ));
                      markFlowDirty(); // direct setNodes bypasses onNodesChange
                      toastService.error(`Failed to upload "${fileName}"`);
                    });
                }
              } catch (err) {
                console.error('[CreativeNodeFlow] Failed to paste video:', err);
                alertService.error('Failed to paste video');
              }
              break;
            }
            
            if (type.startsWith('image/')) {
              foundMedia = true;
              clipboardItem.getType(type).then(blob => {
                // Convert blob to data URL for immediate display
                const reader = new FileReader();
                reader.onload = async (e) => {
                  const dataUrl = e.target?.result as string;
                  const fileName = `pasted-image-${Date.now()}.${type.split('/')[1] || 'png'}`;
                  const fileType = type;
                  
                  // Get image dimensions for aspect ratio locking
                  const getImageDimensions = (): Promise<{ width: number; height: number }> => {
                    return new Promise((resolve) => {
                      const img = new Image();
                      img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
                      img.onerror = () => resolve({ width: 0, height: 0 });
                      img.src = dataUrl;
                    });
                  };
                  
                  const dimensions = await getImageDimensions();

                  // Helper to create node data with upload status
                  const createNodeData = (imageUrl: string, isUploading: boolean, fileId?: string) => ({
                    imageUrl,
                    content: imageUrl,
                    type: 'image',
                    context: {
                      messages: [{
                        role: 'user' as const,
                        content: [
                          { type: 'text' as const, text: 'User uploaded an image' },
                          { type: 'image' as const, imageUrl, mimeType: fileType || 'image/png' }
                        ]
                      }]
                    },
                    isUploading,
                    fileId,
                    fileName,
                    fileType,
                    // Include dimensions for aspect ratio locking
                    preloadedWidth: dimensions.width,
                    preloadedHeight: dimensions.height
                  });

                  // Upload to R2 in background and return the stored URL
                  const uploadToR2 = async (): Promise<{ id: string; url: string } | null> => {
                    try {
                      await fileStorageService.init();
                      const file = new File([blob], fileName, { type: fileType });
                      // Upload to flow-scoped storage (Flow Assets)
                      // Skip deduplication for pasted images to improve upload speed
                      const uploadOptions = {
                        scope: 'flow',
                        flowId: currentFlowId || undefined,
                        skipDeduplication: true,
                      };
                      const storedFile = await fileStorageService.uploadFile(file, uploadOptions);
                      logger.info(`Pasted image saved to Flow Assets: ${fileName} (ID: ${storedFile.id}, URL: ${storedFile.url?.substring(0, 50)}...)`);
                      return { id: storedFile.id, url: storedFile.url };
                    } catch (error) {
                      logger.error('Failed to save pasted image to Flow Assets:', error);
                      return null;
                    }
                  };

                  // Check if an Image Container is selected
                  const selectedImagePanels = nodes.filter(
                    node => node.selected && node.type === 'imagePanel'
                  );

                  let targetNodeId: string;

                  if (selectedImagePanels.length > 0) {
                    // Update the first selected Image Container immediately with data URL
                    targetNodeId = selectedImagePanels[0].id;

                    recordUserAction('Paste image', { touchesResults: [targetNodeId] });
                    setNodes(currentNodes =>
                      currentNodes.map(node =>
                        node.id === targetNodeId
                          ? {
                              ...node,
                              data: {
                                ...node.data,
                                ...createNodeData(dataUrl, true) // Show with loading state
                              }
                            }
                          : node
                      )
                    );
                    // This branch adds no node (it fills an existing Image Container),
                    // so nothing else marks the flow dirty — without this the pasted
                    // image is gone on reload.
                    markFlowDirty();
                  } else {
                    // Create a new Image Container immediately with data URL
                    const reactFlowWrapper = reactFlowWrapperRef.current;
                    if (reactFlowWrapper) {
                      const bounds = reactFlowWrapper.getBoundingClientRect();
                      const centerX = bounds.width / 2;
                      const centerY = bounds.height / 2;

                      const flowPosition = reactFlowScreenToFlowPosition({
                        x: bounds.left + centerX,
                        y: bounds.top + centerY
                      });

                      const PANEL_WIDTH = 554; // Match gallery drop width
                      const HEADER_HEIGHT = 44;

                      targetNodeId = uniqueNodeId('imagePanel');

                      // Calculate height based on image aspect ratio (like gallery drop)
                      const aspectRatio = dimensions.width / dimensions.height;
                      const nodeWidth = 554; // Match gallery drop width
                      const nodeHeight = Math.round(nodeWidth / aspectRatio) + 100; // Add padding for node chrome

                      const newNode: any = {
                        id: targetNodeId,
                        position: {
                          x: flowPosition.x - (PANEL_WIDTH / 2),
                          y: flowPosition.y - (HEADER_HEIGHT / 2)
                        },
                        type: 'imagePanel',
                        width: nodeWidth,
                        height: nodeHeight,
                        zIndex: getHighestZIndex(),
                        data: {
                          ...createNodeData(dataUrl, true), // Show with loading state
                          ...createNodeHandlers(targetNodeId)
                        }
                      };

                      addNodeWithAnimation(newNode);
                    } else {
                      return; // Can't create node without wrapper
                    }
                  }

                  // Now upload to R2 in background and update node when complete
                  uploadToR2().then(result => {
                    const r2Url = result?.url || dataUrl;
                    const r2FileId = result?.id;

                    setNodes(currentNodes =>
                      currentNodes.map(node =>
                        node.id === targetNodeId
                          ? {
                              ...node,
                              data: {
                                ...node.data,
                                ...createNodeData(r2Url, false, r2FileId) // Update with R2 URL, loading done
                              }
                            }
                          : node
                      )
                    );

                    // Persist the R2 URL, not the data: URL the earlier save captured.
                    markFlowDirty();

                    // Trigger output only after R2 URL is available (for better API compatibility)
                    const hasConnections = edgesRef.current.some(edge => edge.source === targetNodeId);
                    if (hasConnections) {
                      const currentNode = nodesRef.current.find(n => n.id === targetNodeId);
                      const onOutput = (currentNode?.data as any)?.onOutput;
                      if (onOutput && typeof onOutput === 'function') {
                        onOutput({
                          nodeId: targetNodeId,
                          content: r2Url,
                          type: 'image',
                          context: createNodeData(r2Url, false, r2FileId).context
                        });
                      }
                    }
                  });
                }
                reader.readAsDataURL(blob);
              });
              break;
            }
          }
        }
        
        // If no image or video found in system clipboard, try pasting nodes from internal clipboard
        if (!foundMedia) {
          pasteNodesFromClipboard();
        }
      }).catch(err => {
        // Clipboard access denied or failed - try pasting nodes as fallback
        logger.debug('No image in clipboard or clipboard access denied:', err);
        pasteNodesFromClipboard();
      });
      };

      // First, check if the clipboard contains our node marker (text with knods-nodes type)
      // This takes priority over any images that might also be in the clipboard
      navigator.clipboard.readText().then(clipboardText => {
        if (clipboardText) {
          try {
            const parsed = JSON.parse(clipboardText);
            if (parsed.type === 'knods-nodes' && nodeClipboard.current.nodes.length > 0) {
              // Clipboard contains our node marker, paste nodes from internal clipboard
              pasteNodesFromClipboard();
              return;
            }
          } catch {
            // Not JSON or not our marker, continue to check for images
          }
        }
        // No node marker found, check for images/videos
        checkAndPasteMedia();
      }).catch(() => {
        // Clipboard read failed, check for images/videos
        checkAndPasteMedia();
      });
    }

    // Handle Shift+1 to fit view to all nodes (use event.code to detect physical key regardless of Shift)
    if (event.code === 'Digit1' && event.shiftKey && !isInTextInput) {
      event.preventDefault();
      fitView({ padding: 0.2, duration: 300 });
      return;
    }

    // Handle 1 (without Shift) to reset zoom to 100%
    if (event.code === 'Digit1' && !event.shiftKey && !isInTextInput && !event.ctrlKey && !event.metaKey) {
      event.preventDefault();
      resetZoomToOneCentered();
      return;
    }
  }, [nodes, edges, reactFlowScreenToFlowPosition, fitView, getHighestZIndex, createNodeHandlers, addNodeWithAnimation, isExecuting, runFullFlow, runSelectedNodes, cancelExecution, setNodes, setEdges, setAnimatedNodeIds, currentFlowId, resetZoomToOneCentered, markFlowDirty]);

  const getMenuPosition = useCallback((clientX: number, clientY: number, menuHeight: number = 400): { x: number; y: number } => {
    const menuWidth = 200;
    const padding = 8;

    let x = clientX;
    let y = clientY;

    if (x + menuWidth > window.innerWidth - padding) {
      x = window.innerWidth - menuWidth - padding;
    }

    if (y + menuHeight > window.innerHeight - padding) {
      y = window.innerHeight - menuHeight - padding;
    }

    if (x < padding) x = padding;
    if (y < padding) y = padding;

    return { x, y };
  }, []);

  const wouldCreateCycle = useCallback((sourceId: string, targetId: string, currentEdges: Edge[]): boolean => {
    const adjacency = new Map<string, string[]>();

    currentEdges.forEach((edge) => {
      if (!adjacency.has(edge.source)) {
        adjacency.set(edge.source, []);
      }
      adjacency.get(edge.source)!.push(edge.target);
    });

    const stack = [targetId];
    const visited = new Set<string>();

    while (stack.length > 0) {
      const nodeId = stack.pop()!;
      if (nodeId === sourceId) {
        return true;
      }
      if (visited.has(nodeId)) {
        continue;
      }
      visited.add(nodeId);

      const neighbors = adjacency.get(nodeId) || [];
      neighbors.forEach((neighbor) => {
        if (!visited.has(neighbor)) {
          stack.push(neighbor);
        }
      });
    }

    return false;
  }, []);

  // Right-click context menu handler
  const handlePaneContextMenu = useCallback((event: React.MouseEvent | MouseEvent) => {
    event.preventDefault();
    const selectedNodes = nodesRef.current.filter((node) => node.selected);
    const flowPosition = screenToFlowPosition(event.clientX, event.clientY);
    const { x, y } = getMenuPosition(event.clientX, event.clientY, selectedNodes.length >= 2 ? 120 : 400);
    
    setContextMenu({
      x, // Screen coordinates for menu positioning
      y,
      flowX: flowPosition.x, // Flow coordinates for node creation
      flowY: flowPosition.y,
      mode: selectedNodes.length >= 2 ? 'multi-select' : 'canvas',
      selectedNodeIds: selectedNodes.map((node) => node.id),
    });
  }, [getMenuPosition, screenToFlowPosition]);

  const handleNodeContextMenu = useCallback((event: React.MouseEvent, node: Node) => {
    event.preventDefault();

    // Utility container nodes (Output, ImagePanel, DocumentPanel) have their own
    // media context menu via PagedOutput/ImagePanel — don't show canvas context
    // menu for them.
    const reg = getNodeByReactFlowType(node.type || '');
    if (reg && reg.category === 'utility') {
      return;
    }

    const selectedNodes = nodesRef.current.filter((currentNode) => currentNode.selected);

    // Multi-select: show connect/disconnect menu
    if (selectedNodes.length >= 2) {
      const flowPosition = screenToFlowPosition(event.clientX, event.clientY);
      const { x, y } = getMenuPosition(event.clientX, event.clientY, 120);
      setContextMenu({
        x, y,
        flowX: flowPosition.x, flowY: flowPosition.y,
        mode: 'multi-select',
        selectedNodeIds: selectedNodes.map((n) => n.id),
      });
      return;
    }

    // Single node: check if it's a generator (switchable)
    const isGenerator = reg && (reg.category === 'text' || reg.category === 'image' || reg.category === 'video' || reg.category === 'audio');

    const flowPosition = screenToFlowPosition(event.clientX, event.clientY);
    const { x, y } = getMenuPosition(event.clientX, event.clientY, isGenerator ? 200 : 400);

    setContextMenu({
      x, y,
      flowX: flowPosition.x, flowY: flowPosition.y,
      mode: isGenerator ? 'node' : 'canvas',
      nodeId: node.id,
      selectedNodeIds: [node.id],
    });
  }, [getMenuPosition, screenToFlowPosition]);

  const handleCanvasContextMenuCapture = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const selectedNodes = nodesRef.current.filter((node) => node.selected);
    if (selectedNodes.length < 2) {
      return;
    }

    const target = event.target as HTMLElement;
    const clickedSelectedNode = Boolean(target.closest('.react-flow__node.selected'));
    const clickedSelectionOverlay = Boolean(
      target.closest('.react-flow__selection, .react-flow__nodesselection, .react-flow__nodesselection-rect, .react-flow__selectionpane')
    );

    if (!clickedSelectedNode && !clickedSelectionOverlay) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const flowPosition = screenToFlowPosition(event.clientX, event.clientY);
    const { x, y } = getMenuPosition(event.clientX, event.clientY, 120);

    setContextMenu({
      x,
      y,
      flowX: flowPosition.x,
      flowY: flowPosition.y,
      mode: 'multi-select',
      selectedNodeIds: selectedNodes.map((node) => node.id),
    });
  }, [getMenuPosition, screenToFlowPosition]);

  // Handle file context from FilePanel
  const handleFileContext = useCallback((contexts: any) => {
    logger.debug('[CreativeNodeFlow] Received file contexts from FilePanel:', contexts);
    setSelectedFileContexts(contexts);

    // Optionally auto-inject context into selected prompt nodes
    const selectedPromptNodes = nodes.filter(node =>
      node.selected &&
      (node.type === 'startingPrompt' || node.type === 'agentPrompt')
    );

    logger.debug('[CreativeNodeFlow] Selected prompt nodes:', selectedPromptNodes.length);

    if (selectedPromptNodes.length > 0) {
      logger.debug('[CreativeNodeFlow] Injecting file contexts into nodes:',
        selectedPromptNodes.map(n => n.id)
      );

      // Update nodes with file context
      setNodes(currentNodes =>
        currentNodes.map(node => {
          if (selectedPromptNodes.some(selected => selected.id === node.id)) {
            logger.debug(`[CreativeNodeFlow] Adding file contexts to node ${node.id}`);
            return {
              ...node,
              data: {
                ...node.data,
                fileContexts: contexts
              }
            };
          }
          return node;
        })
      );
      // The context the user just paid an analysis for lives only in node data —
      // without this it is dropped on reload and they pay again.
      markFlowDirty();
    } else {
  logger.warn('[CreativeNodeFlow] No prompt nodes selected. Please select a Starting Prompt or Creative Director node before sending files.');
  alertService.warning('Please select a Starting Prompt or Creative Director node first, then click "Send to Prompt"');
    }
  }, [nodes, setNodes, markFlowDirty]);

  // Where assets placed from FilePanel land: the centre of the canvas the
  // palette overlay (the open Assets panel) does not cover, plus a left clamp so
  // no new node starts underneath it.
  const getAssetPlacementFrame = useCallback(() => {
    const reactFlowBounds = reactFlowWrapperRef.current?.getBoundingClientRect();
    if (!reactFlowBounds) return null;
    return computeAssetPlacementFrame(
      reactFlowBounds,
      nodePaletteRef.current?.getOverlayRect() ?? null,
      reactFlowScreenToFlowPosition
    );
  }, [reactFlowScreenToFlowPosition]);

  // A placement is laid out with the sizes nodes ASK for, but they render at a
  // different height: `.react-flow__node` has `min-height: 448px; height: auto
  // !important`, so an Image Container asking for 300 renders 448, and taller
  // content grows further. React Flow's own `measured` cannot be used for a
  // node added at runtime here: it is never filled in for them (no
  // `dimensions` change reaches onNodesChange and the internal node keeps
  // `measured: {}`), so the rendered size is read from the node's element.
  // offsetWidth/offsetHeight are layout px, i.e. flow units at any zoom.
  const renderedNode = useCallback((node: Node): Node => {
    const el = reactFlowWrapperRef.current?.querySelector<HTMLElement>(
      `.react-flow__node[data-id="${CSS.escape(node.id)}"]`
    );
    if (el?.offsetWidth && el.offsetHeight) {
      return { ...node, measured: { width: el.offsetWidth, height: el.offsetHeight } };
    }
    return node;
  }, []);

  // Boxes of every node already on the canvas, at their rendered size where it
  // is on screen, so placed assets land beside them rather than on top of them.
  const getPlacementObstacles = useCallback(
    () => obstacleBoxes(nodesRef.current.map(renderedNode)),
    [renderedNode]
  );

  // After a placement, the effect below re-packs the new nodes with their
  // rendered sizes and moves them off anything they overlap, each time one of
  // them resizes (a ResizeObserver on their elements). It stops as soon as the
  // user drags or deletes one of them, and after a short window, so a late
  // image load still counts but a batch never moves under the user later.
  const pendingAssetLayoutRef = useRef<{
    ids: string[];
    anchor: FlowPoint;
    mode: AssetAnchorMode;
    minX: number | null;
    until: number;
    observer: ResizeObserver | null;
    observed: Set<Element>;
  } | null>(null);
  const [assetResizeTick, setAssetResizeTick] = useState(0);

  const stopAssetLayout = useCallback(() => {
    pendingAssetLayoutRef.current?.observer?.disconnect();
    pendingAssetLayoutRef.current = null;
  }, []);

  const trackAssetPlacement = useCallback((ids: string[], anchor: FlowPoint, mode: AssetAnchorMode, minX: number | null) => {
    stopAssetLayout();
    if (ids.length === 0) return;
    const observer = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => setAssetResizeTick(t => t + 1))
      : null;
    pendingAssetLayoutRef.current = { ids, anchor, mode, minX, until: Date.now() + 4000, observer, observed: new Set() };
    window.setTimeout(() => {
      if (pendingAssetLayoutRef.current?.ids === ids) stopAssetLayout();
    }, 4000);
  }, [stopAssetLayout]);

  useEffect(() => stopAssetLayout, [stopAssetLayout]);

  // If avoiding existing nodes pushed a placement out of the uncovered part of
  // the view, pan so it is centred there; the user should see what they placed.
  const revealPlacedNodes = useCallback((placed: Node[]) => {
    const bounds = reactFlowWrapperRef.current?.getBoundingClientRect();
    if (!bounds || placed.length === 0) return;
    const topLeft = reactFlowScreenToFlowPosition({ x: bounds.left, y: bounds.top });
    const bottomRight = reactFlowScreenToFlowPosition({ x: bounds.right, y: bounds.bottom });
    const left = getAssetPlacementFrame()?.minX ?? topLeft.x;
    const shift = revealShift(placed.map(nodeBox), {
      x: left,
      y: topLeft.y,
      width: bottomRight.x - left,
      height: bottomRight.y - topLeft.y,
    });
    if (!shift) return;
    const { x, y, zoom } = getViewport();
    // Not animated: the node updates that follow a placement cancel a running
    // viewport transition, which then never arrives (measured live).
    setViewport({ x: x - shift.x * zoom, y: y - shift.y * zoom, zoom }, { duration: 0 });
  }, [reactFlowScreenToFlowPosition, getAssetPlacementFrame, getViewport, setViewport]);

  useLayoutEffect(() => {
    const pending = pendingAssetLayoutRef.current;
    if (!pending) return;
    if (Date.now() > pending.until) {
      stopAssetLayout();
      return;
    }
    const batch = pending.ids.map(id => nodes.find(n => n.id === id));
    // None yet: the add has not been committed. Some: the user deleted one.
    if (batch.every(n => !n)) return;
    if (batch.some(n => !n || n.dragging)) {
      stopAssetLayout();
      return;
    }
    // Watch each new node's element as soon as React Flow has rendered it.
    for (const id of pending.ids) {
      const el = reactFlowWrapperRef.current?.querySelector(`.react-flow__node[data-id="${CSS.escape(id)}"]`);
      if (el && !pending.observed.has(el)) {
        pending.observed.add(el);
        pending.observer?.observe(el);
      }
    }
    const placed = (batch as Node[]).map(renderedNode);
    if (!placed.every(isNodeMeasured)) {
      // React Flow mounts node elements a frame after the state change; look again then.
      requestAnimationFrame(() => setAssetResizeTick(t => t + 1));
      return;
    }

    const ids = new Set(pending.ids);
    const positions = relayoutAssetBatch(placed, pending.anchor, {
      anchor: pending.mode,
      minX: pending.minX,
      obstacles: obstacleBoxes(nodes.map(renderedNode), ids),
    });
    const moved = placed.some(n => {
      const p = positions.get(n.id)!;
      return Math.abs(p.x - n.position.x) > 0.5 || Math.abs(p.y - n.position.y) > 0.5;
    });
    if (!moved) return;
    setNodes(current => current.map(n => (ids.has(n.id) ? { ...n, position: positions.get(n.id)! } : n)));
    markFlowDirty(); // direct setNodes bypasses onNodesChange
    revealPlacedNodes(placed.map(n => ({ ...n, position: positions.get(n.id)! })));
  }, [nodes, assetResizeTick, renderedNode, stopAssetLayout, setNodes, markFlowDirty, revealPlacedNodes]);

  // Handle adding a file to the stage from FilePanel (lightbox, card, context menu).
  // The Assets panel deliberately stays open afterwards so several can be placed.
  const handleAddFileToStage = useCallback((file: { id: string; name: string; type: string; url?: string; previewUrl?: string }) => {
    const frame = getAssetPlacementFrame();
    if (!frame) return;

    const newNode = buildAssetStageNodeAt(file, frame.center, uniqueNodeId, frame.minX, getPlacementObstacles());
    if (newNode) {
      // Unique key: never merges with another placement, but keeps the step
      // live while the placement re-layout settles the node's final position.
      recordUserAction('Place asset', { coalesceKey: `place:${newNode.id}` });
      addNodeWithAnimation(newNode);
      trackAssetPlacement([newNode.id], frame.center, 'header', frame.minX);
      revealPlacedNodes([newNode]);
    } else {
      alertService.info(`File type ${file.type} added. Drag to canvas to use.`);
    }
  }, [getAssetPlacementFrame, addNodeWithAnimation, getPlacementObstacles, trackAssetPlacement, revealPlacedNodes]);

  // Place a multi-selection from FilePanel: same nodes as a single placement,
  // laid out as a grid around the uncovered canvas centre, clear of existing nodes.
  const handleAddFilesToStage = useCallback((files: { id: string; name: string; type: string; url?: string; previewUrl?: string }[]) => {
    const frame = getAssetPlacementFrame();
    if (!frame) return;

    const { nodes: newNodes, skipped } = buildAssetStageGrid(files, frame.center, uniqueNodeId, frame.minX, getPlacementObstacles());
    if (newNodes.length > 0) {
      recordUserAction(
        newNodes.length === 1 ? 'Place asset' : `Place ${newNodes.length} assets`,
        { coalesceKey: `place:${newNodes[0].id}` }
      );
    }
    newNodes.forEach(node => addNodeWithAnimation(node));
    trackAssetPlacement(newNodes.map(n => n.id), frame.center, 'center', frame.minX);
    revealPlacedNodes(newNodes);
    if (skipped.length > 0) {
      alertService.info(`${skipped.length} selected file${skipped.length === 1 ? '' : 's'} can't be placed on the canvas and ${skipped.length === 1 ? 'was' : 'were'} skipped.`);
    }
  }, [getAssetPlacementFrame, addNodeWithAnimation, getPlacementObstacles, trackAssetPlacement, revealPlacedNodes]);

  // Handlers for output node context menu (fullscreen lightbox)
  const handleOutputSendToDevice = useCallback((url: string, type: string) => {
    // Create a minimal asset object for SendToDeviceModal.
    // Audio must be declared honestly: sending an .mp3 labelled image/png made
    // the phone landing page build an <img> around it, fire onerror and show the
    // error panel with no download button.
    const meta = type === 'video'
      ? { ext: 'mp4', mime: 'video/mp4' }
      : type === 'audio'
        ? { ext: 'mp3', mime: 'audio/mpeg' }
        : { ext: 'png', mime: 'image/png' };
    setSendToDeviceAsset({
      id: `output-${Date.now()}`,
      name: `output.${meta.ext}`,
      type: meta.mime,
      size: 0,
      url: url,
      previewUrl: url
    });
  }, []);

  const handleOutputSetFlowThumbnail = useCallback((url: string) => {
    setCurrentFlowThumbnail(url);
    toastService.success('Knod thumbnail set');
    autoSave.triggerSave();
  }, [autoSave]);

  const handleOutputDelete = useCallback((nodeId: string, pageIndex: number) => {
    recordUserAction('Delete output', { touchesResults: [nodeId] });
    setNodes(prevNodes => {
      const node = prevNodes.find(n => n.id === nodeId);
      if (!node) return prevNodes;
      
      const pages = (node.data as any).pages as Array<any> || [];
      
      if (pages.length <= 1) {
        // If only one page, remove the entire node
        return prevNodes.filter(n => n.id !== nodeId);
      }
      
      // Remove the page at pageIndex
      const newPages = pages.filter((_: any, idx: number) => idx !== pageIndex);
      const newIndex = Math.min(pageIndex, newPages.length - 1);
      
      return prevNodes.map(n => {
        if (n.id !== nodeId) return n;
        return {
          ...n,
          data: {
            ...n.data,
            pages: newPages,
            currentPageIndex: newIndex,
            content: newPages[newIndex]?.content || '',
            type: newPages[newIndex]?.type || 'text'
          }
        };
      });
    });
    
    // Also remove any edges connected to this node if it was deleted
    setEdges(prevEdges => {
      const nodeExists = nodes.some(n => n.id === nodeId);
      if (!nodeExists) {
        return prevEdges.filter(e => e.source !== nodeId && e.target !== nodeId);
      }
      return prevEdges;
    });
    
    markFlowDirty(); // direct setNodes/setEdges bypass on*Change
    toastService.success('Output deleted');
  }, [setNodes, setEdges, nodes, markFlowDirty]);

  // Inject context menu handlers into output nodes
  // Using refs to store handler references to avoid infinite loops
  const outputHandlersRef = useRef({
    onSendToDevice: handleOutputSendToDevice,
    onSetFlowThumbnail: handleOutputSetFlowThumbnail,
    onDeleteOutput: handleOutputDelete
  });
  
  useEffect(() => {
    outputHandlersRef.current = {
      onSendToDevice: handleOutputSendToDevice,
      onSetFlowThumbnail: handleOutputSetFlowThumbnail,
      onDeleteOutput: handleOutputDelete
    };
  }, [handleOutputSendToDevice, handleOutputSetFlowThumbnail, handleOutputDelete]);

  // Inject handlers into output nodes when they're created
  useEffect(() => {
    let hasUpdates = false;
    const updatedNodes = nodes.map(node => {
      if (node.type === 'customOutput' || node.type === 'universalOutput') {
        const nodeData = node.data as Record<string, unknown>;
        // Check if handlers need to be injected
        if (!nodeData.onSendToDevice || !nodeData.onSetFlowThumbnail || !nodeData.onDeleteOutput) {
          hasUpdates = true;
          const nodeId = node.id;
          return {
            ...node,
            data: {
              ...nodeData,
              onSendToDevice: outputHandlersRef.current.onSendToDevice,
              onSetFlowThumbnail: outputHandlersRef.current.onSetFlowThumbnail,
              onDeleteOutput: (pageIndex: number) => outputHandlersRef.current.onDeleteOutput(nodeId, pageIndex)
            }
          };
        }
      }
      // Also inject handlers into imagePanel nodes for context menu
      if (node.type === 'imagePanel') {
        const nodeData = node.data as Record<string, unknown>;
        if (!nodeData.onSendToDevice || !nodeData.onSetFlowThumbnail) {
          hasUpdates = true;
          return {
            ...node,
            data: {
              ...nodeData,
              onSendToDevice: outputHandlersRef.current.onSendToDevice,
              onSetFlowThumbnail: outputHandlersRef.current.onSetFlowThumbnail
            }
          };
        }
      }
      return node;
    });
    
    if (hasUpdates) {
      setNodes(updatedNodes);
    }
  }, [nodes, setNodes]);

  // Check which nodes use a specific asset - called before deletion
  const handleCheckAssetUsage = useCallback((fileId: string, fileUrl: string): string[] => {
    const affectedNodeIds: string[] = [];
    
    for (const node of nodes) {
      const nodeData = node.data as Record<string, unknown>;
      
      // Check imagePanel nodes
      if (node.type === 'imagePanel') {
        if (nodeData.fileId === fileId || nodeData.imageUrl === fileUrl) {
          affectedNodeIds.push(node.id);
        }
      }
      
      // Check customOutput nodes (video, audio, images)
      if (node.type === 'customOutput') {
        const pages = nodeData.pages as Array<{ content?: string; type?: string }> | undefined;
        if (pages) {
          const hasMatchingContent = pages.some(page => page.content === fileUrl);
          if (hasMatchingContent || nodeData.fileId === fileId) {
            affectedNodeIds.push(node.id);
          }
        }
      }
      
      // Check universalOutput nodes
      if (node.type === 'universalOutput') {
        const pages = nodeData.pages as Array<{ content?: string }> | undefined;
        if (pages) {
          const hasMatchingContent = pages.some(page => page.content === fileUrl);
          if (hasMatchingContent) {
            affectedNodeIds.push(node.id);
          }
        }
      }
      
      // Check documentPanel nodes
      if (node.type === 'documentPanel') {
        if (nodeData.fileId === fileId || nodeData.fileUrl === fileUrl) {
          affectedNodeIds.push(node.id);
        }
      }
    }
    
    return affectedNodeIds;
  }, [nodes]);

  // Remove one or more assets from stage nodes - intelligently handles multi-page nodes
  // For nodes with multiple pages: removes only the matching page(s)
  // For single-page nodes or nodes where all pages match: deletes the entire node
  // Takes a list of urls so a bulk asset delete resolves the stage in ONE pass.
  const handleRemoveAssetsFromStage = useCallback((nodeIds: string[], fileUrls: string[]) => {
    if (nodeIds.length === 0) return;

    const nodeIdSet = new Set(nodeIds);
    // Empty urls are dropped, not matched: a node flagged only by fileId still
    // falls through to full removal below, exactly as it did before.
    const urlSet = new Set(fileUrls.filter(Boolean));

    const nodesToRemove = new Set<string>();
    let pagesRemoved = 0;

    // Computed against the current `nodes` rather than inside a functional
    // updater: the edge cleanup below needs the removed ids synchronously, and
    // a queued updater has not run yet at that point.
    const nextNodes = nodes.map(node => {
      if (!nodeIdSet.has(node.id)) return node;

      const nodeData = node.data as Record<string, unknown>;

      // Handle multi-page nodes (customOutput, universalOutput)
      if (node.type === 'customOutput' || node.type === 'universalOutput') {
        const pages = nodeData.pages as Array<{ content?: string; type?: string; timestamp?: string }> | undefined;

        if (pages && pages.length > 0) {
          // Filter out pages that match any of the deleted file URLs
          const remainingPages = pages.filter(page => !(page.content && urlSet.has(page.content)));

          if (remainingPages.length === 0) {
            // All pages matched - mark node for deletion
            nodesToRemove.add(node.id);
            return node;
          } else if (remainingPages.length < pages.length) {
            // Some pages removed - update the node with remaining pages
            pagesRemoved += pages.length - remainingPages.length;
            const currentIndex = (nodeData.currentPageIndex as number) || 0;
            // Adjust currentPageIndex if needed
            const newIndex = Math.min(currentIndex, remainingPages.length - 1);

            return {
              ...node,
              data: {
                ...nodeData,
                pages: remainingPages,
                currentPageIndex: newIndex,
                // Update content to reflect current page
                content: remainingPages[newIndex]?.content || ''
              }
            };
          }
        }
      }

      // For non-paged nodes (imagePanel, documentPanel) or single-page nodes - mark for deletion
      nodesToRemove.add(node.id);
      return node;
    }).filter(n => !nodesToRemove.has(n.id));

    setNodes(nextNodes);

    // Also remove any edges connected to deleted nodes
    if (nodesToRemove.size > 0) {
      setEdges(prev => prev.filter(e =>
        !nodesToRemove.has(e.source) && !nodesToRemove.has(e.target)
      ));
    }

    // This edits the canvas through setNodes/setEdges directly rather than
    // through onNodesChange, so nothing else marks the flow dirty - without
    // this the removed nodes come back on the next load, still pointing at
    // assets that no longer exist.
    if (nodesToRemove.size > 0 || pagesRemoved > 0) {
      autoSave.markDirty();
    }

    // Log what happened
    if (nodesToRemove.size > 0 && pagesRemoved > 0) {
      logger.info(`[Assets] Removed ${nodesToRemove.size} node(s) and ${pagesRemoved} page(s) from other nodes`);
    } else if (nodesToRemove.size > 0) {
      logger.info(`[Assets] Removed ${nodesToRemove.size} node(s) from stage:`, Array.from(nodesToRemove));
    } else if (pagesRemoved > 0) {
      logger.info(`[Assets] Removed ${pagesRemoved} page(s) from nodes`);
    }
  }, [nodes, setNodes, setEdges, autoSave]);

  // Single-asset variant kept for the existing one-file delete flow
  const handleRemoveNodesFromStage = useCallback((nodeIds: string[], fileUrl: string) => {
    handleRemoveAssetsFromStage(nodeIds, [fileUrl]);
  }, [handleRemoveAssetsFromStage]);

  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
    setActiveSubmenu(null);
    setHandleContext(null);
  }, []);

  // Switch a node's type in-place, preserving ID, edges, prompt, and aspect ratio
  const switchNodeType = useCallback((nodeId: string, newPascalType: string) => {
    const reg = getNodeRegistration(newPascalType);
    if (!reg) return;

    recordUserAction('Switch node type', { touchesResults: [nodeId] });
    setNodes(prev => prev.map(n => {
      if (n.id !== nodeId) return n;
      const oldData = n.data as any;
      const newInitial = reg.initialData || {};
      return {
        ...n,
        type: reg.camelCaseType,
        data: {
          ...createNodeHandlers(n.id),
          ...newInitial,
          prompt: oldData.prompt ?? '',
          parameters: {
            ...(newInitial.parameters || {}),
            // Preserve aspect ratio from old node if it was set
            ...(oldData.parameters?.aspectRatio ? { aspectRatio: oldData.parameters.aspectRatio } : {}),
          },
          brandVoiceId: oldData.brandVoiceId,
        }
      };
    }));

    // Re-validate incoming edges — remove any whose targetHandle is incompatible
    setEdges(prev => prev.filter(e => {
      if (e.target !== nodeId) return true;
      // If edge has a specific target handle, check it still exists on the new node type
      if (e.targetHandle) {
        const handles = getInputHandlesForNodeType(reg.camelCaseType);
        return handles.some(h => h.id === e.targetHandle);
      }
      return true;
    }));

    markFlowDirty(); // direct setNodes/setEdges bypass on*Change
    closeContextMenu();
  }, [setNodes, setEdges, createNodeHandlers, closeContextMenu, markFlowDirty]);

  // Close context menu when clicking outside of it
  const handleDocumentClick = useCallback((event: MouseEvent) => {
    if (!contextMenu) return;

    // Check if click is outside the context menu
    const target = event.target as HTMLElement;
    const contextMenuElement = target.closest('.context-menu');

    if (!contextMenuElement) {
      closeContextMenu();
    }
  }, [contextMenu, closeContextMenu]);

  // Find non-overlapping position for new node
  const findNonOverlappingPosition = useCallback((baseX: number, baseY: number, direction: 'right' | 'left' = 'right', nodeWidth = 448, nodeHeight = 448, sourceWidth?: number): { x: number; y: number } => {
    const spacing = 100;
    // Use source node's actual width for placement offset, fall back to new node width
    const effectiveSourceWidth = sourceWidth ?? nodeWidth;

    let offsetX = direction === 'right' ? effectiveSourceWidth + spacing : -(nodeWidth + spacing);
    let offsetY = 0;

    let attempts = 0;
    const maxAttempts = 20;

    while (attempts < maxAttempts) {
      const testX = baseX + offsetX;
      const testY = baseY + offsetY;

      // Check for overlap with existing nodes using their actual dimensions
      const hasOverlap = nodes.some(node => {
        const existingW = (node.measured?.width as number) || (node.style?.width as number) || 448;
        const existingH = (node.measured?.height as number) || (node.style?.height as number) || 448;

        return (
          testX < node.position.x + existingW + spacing &&
          testX + nodeWidth > node.position.x - spacing &&
          testY < node.position.y + existingH + spacing &&
          testY + nodeHeight > node.position.y - spacing
        );
      });

      if (!hasOverlap) {
        return { x: testX, y: testY };
      }

      // Try different positions
      if (attempts < 5) {
        offsetY += 100; // Try below
      } else if (attempts < 10) {
        offsetY -= 200; // Try above
      } else {
        offsetX += direction === 'right' ? 100 : -100; // Try further away
        offsetY = 0;
      }

      attempts++;
    }

    // Fallback position if no good spot found
    return { x: baseX + offsetX, y: baseY + offsetY };
  }, [nodes]);

  // Context menu node creation handlers
  const createNodeFromMenu = useCallback((nodeType: CustomNodeType) => {
    if (!contextMenu) return;

    const newId = `${nodeType}-${Date.now()}`;

    let position: { x: number; y: number };
    let autoConnect = false;
    let sourceNodeId: string | null = null;
    let isSourceHandle = false;

    // Get actual dimensions from registry
    const { width, height } = getNodeDimensions(nodeType);
    const HEADER_HEIGHT = 44;

    // Check if this was triggered from a handle double-click/right-click
    if (handleContext) {
      const sourceNode = nodes.find(n => n.id === handleContext.nodeId);
      if (sourceNode) {
        sourceNodeId = handleContext.nodeId;
        isSourceHandle = handleContext.isSource;

        // Position new node relative to source node using actual dimensions
        const direction: 'right' | 'left' = isSourceHandle ? 'right' : 'left';
        const sourceW = (sourceNode.measured?.width as number) || (sourceNode.style?.width as number) || 448;
        position = findNonOverlappingPosition(
          sourceNode.position.x,
          sourceNode.position.y,
          direction,
          width,
          height,
          sourceW
        );
        autoConnect = true;
      } else {
        // Fallback to menu position - center horizontally, align header middle vertically
        position = {
          x: contextMenu.flowX - (width / 2),
          y: contextMenu.flowY - (HEADER_HEIGHT / 2)
        };
      }
    } else {
      // Use flow coordinates from context menu - center horizontally, align header middle vertically
      position = {
        x: contextMenu.flowX - (width / 2),
        y: contextMenu.flowY - (HEADER_HEIGHT / 2)
      };
    }
    const nodeData: any = {
      ...createNodeHandlers(newId),
      brandVoiceId: currentBrandVoiceId,
      ...getInitialNodeData(nodeType),
    };

    const newNode: any = {
      id: newId,
      position,
      type: nodeType,
      width,
      height,
      zIndex: getHighestZIndex(),
      data: nodeData
    };

    addNodeWithAnimation(newNode);

    // Auto-connect if created from handle double-click/right-click
    if (autoConnect && sourceNodeId) {
      const sourceNode = nodes.find(n => n.id === sourceNodeId);

      // Check if both are generators — insert media container between them
      const sourceIsGenerator = isGeneratorNodeType(sourceNode?.type);
      const newIsGenerator = isGeneratorNodeType(nodeType);

      if (sourceIsGenerator && newIsGenerator && sourceNode) {
        const newNodeRef = { id: newId, type: nodeType, position, data: nodeData } as Node;
        if (isSourceHandle) {
          // Source handle clicked: existing → [media container] → new
          insertMediaContainerBetween(sourceNode, newNodeRef, handleContext?.handleId || null);
        } else {
          // Target handle clicked: new → [media container] → existing
          insertMediaContainerBetween(newNodeRef, sourceNode, null);
        }
      } else {
        // Normal edge creation
        const connectorType = sourceNode
          ? getConnectorType(sourceNode, handleContext?.handleId || null, isSourceHandle ? 'source' : 'target')
          : 'any';

        let sourceId: string;
        let targetId: string;

        if (isSourceHandle) {
          sourceId = sourceNodeId;
          targetId = newId;
        } else {
          sourceId = newId;
          targetId = sourceNodeId;
        }

        const sourceHandle = isSourceHandle ? (handleContext?.handleId || null) : null;
        const targetHandle = isSourceHandle
          ? (findCompatibleInputHandle(nodeType, connectorType, {}) || null)
          : (handleContext?.handleId || null);

        // Validate before creating the edge — the auto-connect path bypasses
        // ReactFlow's isValidConnection callback, so any illegal pairing (e.g.
        // Output -> Output, ImagePanel -> Output) would otherwise sneak through.
        const edgeSourceNode = nodes.find(n => n.id === sourceId)
          || (sourceId === newId ? (newNode as Node) : undefined);
        const edgeTargetNode = nodes.find(n => n.id === targetId)
          || (targetId === newId ? (newNode as Node) : undefined);
        if (edgeSourceNode && edgeTargetNode) {
          const valid = validateConnection(
            edgeSourceNode,
            edgeTargetNode,
            { source: sourceId, target: targetId, sourceHandle, targetHandle }
          );
          if (!valid) {
            closeContextMenu();
            setHandleContext(null);
            return;
          }
        }

        const newEdge: Edge = {
          id: `${sourceId}-${targetId}`,
          source: sourceId,
          target: targetId,
          sourceHandle,
          targetHandle,
          type: connectorType,
          animated: true,
          style: { stroke: EDGE_COLOR_MAP[connectorType] || EDGE_COLOR_MAP.any, strokeWidth: 3 }
        };

        setEdges(edges => [...edges, newEdge]);
        markFlowDirty(); // direct setEdges bypasses onEdgesChange
      }
    }

    closeContextMenu();
    setHandleContext(null); // Clear handle context
  }, [contextMenu, setEdges, createNodeHandlers, closeContextMenu, handleContext, nodes, findNonOverlappingPosition, getHighestZIndex, addNodeWithAnimation, currentBrandVoiceId, getConnectorType, isGeneratorNodeType, insertMediaContainerBetween, validateConnection, markFlowDirty]);

  // Custom node creation handler
  // @ts-expect-error TS6133 - Kept for future custom node menu support
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _createCustomNodeFromMenu = useCallback((definition: CustomNodeDefinition) => {
    if (!contextMenu) return;

    const newId = `custom-${definition.id}-${Date.now()}`;

    // Node dimensions for centering
    const NODE_WIDTH = 320;
    const HEADER_HEIGHT = 44;

    // Center the node at the click position
    const position = {
      x: contextMenu.flowX - (NODE_WIDTH / 2),
      y: contextMenu.flowY - (HEADER_HEIGHT / 2)
    };

    // Get the universal definition (if migrated)
    let universalDef = getUniversalDefinition(definition);
    
    // If not migrated, migrate it now
    if (!universalDef) {
      logger.warn('[CreativeNodeFlow] Custom node not migrated, migrating now:', definition.name);
      universalDef = convertCustomNodeToUniversal(definition);
    }
    
    // Initialize variables from prompt template
    const templateVars = promptTemplateUtils.extractVariables(definition.promptTemplate);
    const initialVariables = templateVars.reduce((acc, varName) => {
      acc[varName] = ''; // Empty string for each variable
      return acc;
    }, {} as Record<string, string>);

    // Initialize parameters for image/video models
    const initialParameters: any = {};
    if (definition.aspectRatio) {
      initialParameters.aspectRatio = definition.aspectRatio;
    }
    if (definition.durationSeconds) {
      initialParameters.duration = definition.durationSeconds;
    }

    const nodeData: any = {
      ...createNodeHandlers(newId),
      definition: universalDef,              // UniversalNode expects this
      customNodeDefinition: definition,
      variables: initialVariables,           // For PromptTemplate variable inputs
      parameters: initialParameters,         // For ParameterControls (aspectRatio, duration)
      additionalInstructions: '',
      lastResult: undefined,
      lastExecuted: undefined,
      // Store custom node metadata for header styling
      _customNodeIcon: definition.icon,
      _customNodeColor: definition.color,
    };

    const newNode: any = {
      id: newId,
      position,
      type: definition.id, // Use definition.id directly (already has 'custom-' prefix)
      width: 360,
      height: 280,
      zIndex: getHighestZIndex(),
      data: nodeData
    };

    addNodeWithAnimation(newNode);
    closeContextMenu();

    logger.info('[CreativeNodeFlow] Created custom node:', {
      name: definition.name,
      type: definition.id,
      migrated: !!universalDef,
      variables: Object.keys(initialVariables),
      parameters: Object.keys(initialParameters)
    });
  }, [contextMenu, createNodeHandlers, getHighestZIndex, addNodeWithAnimation, closeContextMenu]);

  // Reset function to clear all nodes and start fresh
  // @ts-expect-error TS6133 - Kept for future reset functionality
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _resetToInitialState = useCallback(() => {
    nodeInputHandlers.current.clear();
    setNodes(initialNodes);
    setEdges(initialEdges);
    closeContextMenu();
  }, [setNodes, setEdges, closeContextMenu]);

  // Add keyboard event listener and document click listener
  useEffect(() => {
    const handleGlobalKeyDown = (event: KeyboardEvent) => {
      // Only handle if no input is focused
      if (!(event.target as HTMLElement).closest('input, textarea, select')) {
        handleKeyDown(event);
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    document.addEventListener('mousedown', handleDocumentClick);

    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown);
      document.removeEventListener('mousedown', handleDocumentClick);
    };
  }, [handleKeyDown, handleDocumentClick]);

  // Add double-click and right-click listeners to handles
  useEffect(() => {
    const handleHandleInteraction = (event: MouseEvent) => {
      const handle = (event.target as HTMLElement).closest('.react-flow__handle');
      if (!handle) return;
      event.preventDefault();
      event.stopPropagation();

      // Get handle information — find the parent node wrapper, not the handle itself
      const nodeId = handle.closest('.react-flow__node')?.getAttribute('data-id');
      const isSource = handle.classList.contains('react-flow__handle-source');
      const handleId = handle.getAttribute('data-handleid');

      if (!nodeId) return;

      // Check if handle is already connected
      const isConnected = edges.some(edge => {
        if (isSource) {
          return edge.source === nodeId;
        } else {
          return edge.target === nodeId;
        }
      });

      // Only show menu for unconnected handles
      if (!isConnected) {
        // Store handle context for auto-connection
        setHandleContext({
          nodeId,
          handleType: isSource ? 'source' : 'target',
          handleId: handleId || '',
          isSource
        });

        // Show context menu at handle position (convert screen → flow coordinates)
        const rect = handle.getBoundingClientRect();
        const screenX = rect.right + 10;
        const screenY = rect.top;
        const flowPos = screenToFlowPosition(screenX, screenY);
        setContextMenu({
          x: screenX,
          y: screenY,
          flowX: flowPos.x,
          flowY: flowPos.y,
          nodeId,
          handleType: isSource ? 'source' : 'target',
          handleId: handleId || undefined
        });
      }
    };

    // Add both double-click and right-click listeners to all handles
    const addHandleListeners = () => {
      const handles = document.querySelectorAll('.react-flow__handle');
      handles.forEach(handle => {
        handle.addEventListener('dblclick', handleHandleInteraction as EventListener);
        handle.addEventListener('contextmenu', handleHandleInteraction as EventListener);
      });
    };

    // Remove listeners
    const removeHandleListeners = () => {
      const handles = document.querySelectorAll('.react-flow__handle');
      handles.forEach(handle => {
        handle.removeEventListener('dblclick', handleHandleInteraction as EventListener);
        handle.removeEventListener('contextmenu', handleHandleInteraction as EventListener);
      });
    };

    // Add listeners after a small delay to ensure handles are rendered
    const timeoutId = setTimeout(addHandleListeners, 100);

    return () => {
      clearTimeout(timeoutId);
      removeHandleListeners();
    };
  }, [edges, setContextMenu, setHandleContext, screenToFlowPosition]);

  // Handle edge deletion (delete key or backspace)
  const onEdgesDelete = useCallback((edgesToDelete: Edge[]) => {
    console.log('[CreativeNodeFlow] Edges deleted:', edgesToDelete);
    
    // Clear context from target nodes when edges are deleted
    edgesToDelete.forEach(edge => {
      const targetNode = nodes.find(n => n.id === edge.target);
      if (targetNode) {
        logger.debug('[onEdgesDelete] Clearing context from target node:', edge.target);
        
        // Clear the context from the target node's data
        setNodes((nodes) =>
          nodes.map((node) => {
            if (node.id === edge.target) {
              // Get remaining edges after deletion
              const remainingEdges = edges.filter(e => 
                !edgesToDelete.includes(e) && e.target === node.id
              );
              
              // If no more incoming edges, clear all context
              if (remainingEdges.length === 0) {
                logger.debug('[onEdgesDelete] No more incoming edges, clearing all context');
                return {
                  ...node,
                  data: {
                    ...node.data,
                    context: null,
                    // Don't clear prompt or other user-entered data
                  }
                };
              }
            }
            return node;
          })
        );
      }
    });
    
    // Note: No alert needed - edge deletion is obvious from visual feedback
  }, [nodes, edges, setNodes]);

  const handleConnectSelectedNodes = useCallback(() => {
    const minHorizontalSeparation = UI_DIMENSIONS.NODE_MIN_WIDTH / 2;
    const selectedNodes = nodesRef.current
      .filter((node) => node.selected)
      .sort((a, b) => {
        if (a.position.x !== b.position.x) {
          return a.position.x - b.position.x;
        }
        return a.position.y - b.position.y;
      });

    if (selectedNodes.length < 2) {
      alertService.info('Select at least 2 nodes to connect.');
      closeContextMenu();
      return;
    }

    const existingEdges = edgesRef.current;
    const nextEdges = [...existingEdges];
    const newEdges: Edge[] = [];
    const newMediaNodes: Node[] = [];
    const nodesToMove: { id: string; newX: number }[] = [];
    let logicalConnections = 0;
    let skippedIncompatible = 0;
    let skippedAlreadyConnected = 0;
    let skippedCycle = 0;
    let skippedHorizontalSpacing = 0;

    // Use same gap as auto-arrange
    const NODE_GAP = 100;
    // Spawn at the same size as generation-created output nodes (registry-driven)
    const { width: MEDIA_NODE_WIDTH, height: MEDIA_NODE_HEIGHT } = getOutputNodeDimensions('image');

    for (let sourceIndex = 0; sourceIndex < selectedNodes.length; sourceIndex++) {
      const sourceNode = selectedNodes[sourceIndex];
      const sourceType = getConnectorType(sourceNode, null, 'source');

      for (let targetIndex = sourceIndex + 1; targetIndex < selectedNodes.length; targetIndex++) {
        const targetNode = selectedNodes[targetIndex];
        const horizontalSeparation = targetNode.position.x - sourceNode.position.x;

        if (horizontalSeparation < minHorizontalSeparation) {
          skippedHorizontalSpacing++;
          continue;
        }

        // Generator-to-generator: insert a Image Container between them
        if (isGeneratorNode(sourceNode.type) && isGeneratorNode(targetNode.type)) {
          const alreadyConnected = nextEdges.some(
            (edge) => edge.source === sourceNode.id && edge.target === targetNode.id
          );
          if (alreadyConnected) {
            skippedAlreadyConnected++;
            continue;
          }
          if (wouldCreateCycle(sourceNode.id, targetNode.id, nextEdges)) {
            skippedCycle++;
            continue;
          }

          const mediaNodeId = `output-${Date.now()}-${sourceIndex}-${targetIndex}`;
          const sourceNodeWidth =
            (sourceNode.measured?.width as number) ||
            (sourceNode.style?.width as number) ||
            UI_DIMENSIONS.NODE_MIN_WIDTH;
          const sourceRightEdge = sourceNode.position.x + sourceNodeWidth;
          const targetLeftEdge = targetNode.position.x;
          const totalSpaceNeeded = NODE_GAP + MEDIA_NODE_WIDTH + NODE_GAP;
          const needsToMoveTarget = (targetLeftEdge - sourceRightEdge) < totalSpaceNeeded;
          const mediaX = sourceRightEdge + NODE_GAP;
          const mediaY = (sourceNode.position.y + targetNode.position.y) / 2;
          const newTargetX = needsToMoveTarget ? mediaX + MEDIA_NODE_WIDTH + NODE_GAP : targetLeftEdge;

          newMediaNodes.push({
            id: mediaNodeId,
            type: 'customOutput',
            position: { x: mediaX, y: mediaY },
            zIndex: getHighestZIndex(),
            style: { width: MEDIA_NODE_WIDTH, height: MEDIA_NODE_HEIGHT },
            data: {
              definition: OUTPUT_NODE_DEF,
              content: '',
              type: 'image',
              outputsMediaType: 'image',
              ...createNodeHandlers(mediaNodeId),
            },
          });

          if (needsToMoveTarget) {
            nodesToMove.push({ id: targetNode.id, newX: newTargetX });
          }

          const e1: Edge = {
            id: `${sourceNode.id}-${mediaNodeId}`,
            source: sourceNode.id,
            target: mediaNodeId,
            sourceHandle: null,
            targetHandle: null,
            type: sourceType,
            animated: true,
            style: { stroke: EDGE_COLOR_MAP[sourceType] || EDGE_COLOR_MAP.any, strokeWidth: 3 },
          };
          const imageHandle = findCompatibleInputHandle(targetNode.type || '', 'image', targetNode.data);
          const e2: Edge = {
            id: `${mediaNodeId}-${targetNode.id}`,
            source: mediaNodeId,
            target: targetNode.id,
            sourceHandle: null,
            targetHandle: imageHandle || null,
            type: 'image',
            animated: true,
            style: { stroke: EDGE_COLOR_MAP.image, strokeWidth: 3 },
          };
          nextEdges.push(e1, e2);
          newEdges.push(e1, e2);
          logicalConnections++;
          continue;
        }

        const compatibleHandle = findCompatibleInputHandle(targetNode.type || '', sourceType, targetNode.data);
        const normalizedCompatibleHandle = compatibleHandle
          ? normalizeTargetHandleForNode(targetNode, compatibleHandle)
          : null;

        if (!compatibleHandle) {
          skippedIncompatible++;
          continue;
        }

        const connection: Connection = {
          source: sourceNode.id,
          target: targetNode.id,
          sourceHandle: null,
          targetHandle: normalizedCompatibleHandle,
        };

        if (!validateConnection(sourceNode, targetNode, connection)) {
          skippedIncompatible++;
          continue;
        }

        const alreadyConnected = nextEdges.some((edge) => {
          return edge.source === sourceNode.id && edge.target === targetNode.id && (edge.targetHandle || null) === normalizedCompatibleHandle;
        });

        if (alreadyConnected) {
          skippedAlreadyConnected++;
          continue;
        }

        if (wouldCreateCycle(sourceNode.id, targetNode.id, nextEdges)) {
          skippedCycle++;
          continue;
        }

        const edgeType = sourceType;
        const edgeColor = EDGE_COLOR_MAP[edgeType] || EDGE_COLOR_MAP.any;
        const targetHandleSuffix = normalizedCompatibleHandle || 'default';
        const newEdge: Edge = {
          id: `${sourceNode.id}-${targetNode.id}-${targetHandleSuffix}`,
          source: sourceNode.id,
          target: targetNode.id,
          sourceHandle: null,
          targetHandle: normalizedCompatibleHandle,
          type: edgeType,
          animated: true,
          style: { stroke: edgeColor, strokeWidth: 3 },
        };

        nextEdges.push(newEdge);
        newEdges.push(newEdge);
        logicalConnections++;
      }
    }

    if (logicalConnections > 0) {
      recordUserAction(`Connect ${logicalConnections} link${logicalConnections === 1 ? '' : 's'}`);
      if (newMediaNodes.length > 0 || nodesToMove.length > 0) {
        setNodes((curr) => {
          const moved = curr.map((n) => {
            const move = nodesToMove.find((m) => m.id === n.id);
            return move ? { ...n, position: { ...n.position, x: move.newX } } : n;
          });
          return [...moved, ...newMediaNodes];
        });
      }
      setEdges((prevEdges) => [...prevEdges, ...newEdges]);
      markFlowDirty(); // direct setNodes/setEdges bypass on*Change
      toastService.success(`Connected ${logicalConnections} node link${logicalConnections === 1 ? '' : 's'}.`);
    } else if (skippedHorizontalSpacing > 0 && skippedIncompatible === 0 && skippedAlreadyConnected === 0 && skippedCycle === 0) {
      alertService.info('No connections made. Move target nodes further right to connect them.');
    } else if (skippedAlreadyConnected > 0 && skippedIncompatible === 0 && skippedCycle === 0) {
      alertService.info('Selected nodes are already connected.');
    } else if (skippedCycle > 0 && skippedIncompatible === 0 && skippedAlreadyConnected === 0) {
      alertService.info('Connections were skipped to avoid loops.');
    } else {
      alertService.info('No legal non-looping connections found for the selected nodes.');
    }

    closeContextMenu();
  }, [closeContextMenu, getConnectorType, setEdges, setNodes, validateConnection, wouldCreateCycle, normalizeTargetHandleForNode, createNodeHandlers, getHighestZIndex, markFlowDirty]);

  const handleDisconnectSelectedNodes = useCallback(() => {
    const selectedNodeIds = new Set(
      nodesRef.current
        .filter((node) => node.selected)
        .map((node) => node.id)
    );

    if (selectedNodeIds.size < 2) {
      alertService.info('Select at least 2 nodes to disconnect.');
      closeContextMenu();
      return;
    }

    const edgesToRemove = edgesRef.current.filter((edge) => {
      return selectedNodeIds.has(edge.source) || selectedNodeIds.has(edge.target);
    });

    if (edgesToRemove.length === 0) {
      alertService.info('No connections found for selected nodes.');
      closeContextMenu();
      return;
    }

    recordUserAction(`Disconnect ${edgesToRemove.length} link${edgesToRemove.length === 1 ? '' : 's'}`);
    onEdgesDelete(edgesToRemove);
    const edgesToRemoveIds = new Set(edgesToRemove.map((edge) => edge.id));
    setEdges((prevEdges) => prevEdges.filter((edge) => !edgesToRemoveIds.has(edge.id)));
    markFlowDirty(); // direct setEdges bypasses onEdgesChange
    toastService.success(`Disconnected ${edgesToRemove.length} link${edgesToRemove.length === 1 ? '' : 's'}.`);
    closeContextMenu();
  }, [closeContextMenu, onEdgesDelete, setEdges, markFlowDirty]);

  // ============================================================================
  // Save/Load Flow Handlers
  // ============================================================================

  // Toggle auto-save setting (currently not exposed in UI)

  // Quick save - saves directly, auto-generating name if needed
  const handleQuickSave = useCallback(async () => {
    // What this save covers: edits made while it is on the wire are NOT saved
    // by it and must stay dirty (see useAutoSave.markSavedUpTo).
    const rev = autoSave.snapshotRevision();
    const session = saveSessionsRef.current!.current;
    try {
      // Generate flow name if not set
      let flowName = currentFlowName && currentFlowName.trim() ? currentFlowName : '';
      if (!flowName) {
        const now = new Date();
        const dateStr = now.toISOString().split('T')[0]; // YYYY-MM-DD
        const timeStr = now.toTimeString().split(' ')[0].replace(/:/g, '-'); // HH-MM-SS
        flowName = `flow-${dateStr}-${timeStr}`;
        setCurrentFlowName(flowName);
      }

      const viewport = getViewport();
      const flow = flowSerializationService.serializeFlow(
        nodes,
        edges,
        viewport,
        {
          // Same row as this canvas's autosave, even while its first one is in flight.
          id: saveSessionsRef.current!.reserveId(session, currentFlowId),
          name: flowName,
          description: currentFlowDescription,
          tags: currentFlowTags,
          brandVoiceId: currentBrandVoiceId,
          projectId: currentFlowProjectId
        },
        false // Not a template
      );

      await flowStorageService.saveFlow(flow);
      autoSave.markSavedUpTo(rev);

      // Another flow was opened while this saved: the editor's state is no
      // longer this canvas's, so do not write this canvas's id/name into it.
      if (session !== saveSessionsRef.current!.current) {
        toastService.success(`Knod "${flowName}" saved successfully!`);
        return;
      }
      // A minted id is this canvas's own row, not a different flow.
      saveSessionsRef.current!.recordSaved(session, currentFlowId, flow.metadata.id, true);
      setCurrentFlowId(flow.metadata.id);
      setCurrentFlowName(flow.metadata.name);
      setCurrentFlowDescription(flow.metadata.description || '');
      setCurrentFlowTags(flow.metadata.tags || []);
      
      // Update URL to reflect the saved knod
      navigate(`/knod/${flow.metadata.id}`);
      
      // (Marked saved up to `rev` right after the write.)
      
      toastService.success(`Knod "${flowName}" saved successfully!`);
    } catch (error) {
      logger.error('Failed to save flow:', error);
      // "We're having trouble saving your work. Please try again in a moment."
      // is actively misleading for a cap refusal — there is no moment at which
      // trying again works. Explain it instead.
      if (explainIfFlowLimit(error)) return;
      toastService.error(friendlyErrors.saveFailed);
    }
  }, [currentFlowName, currentFlowId, currentFlowDescription, currentFlowTags, currentBrandVoiceId, nodes, edges, getViewport, autoSave, setCurrentFlowId, setCurrentFlowName, setCurrentFlowDescription, setCurrentFlowTags, navigate, explainIfFlowLimit]);

  // Handle Ctrl+S / Cmd+S keyboard shortcut for quick save
  useEffect(() => {
    const handleSaveShortcut = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 's') {
        event.preventDefault();
        handleQuickSave();
      }
    };

    document.addEventListener('keydown', handleSaveShortcut);
    return () => {
      document.removeEventListener('keydown', handleSaveShortcut);
    };
  }, [handleQuickSave]);

  // ============================================================================
  // Publish: inspect current flow + auto-add missing inputs/outputs
  // ============================================================================

  /** Inspect the LIVE editor state for the publish modal preview. */
  const inspectForPublish = useCallback((): InspectionResult => {
    return inspectFlowForPublish(nodes, edges);
  }, [nodes, edges]);

  /**
   * Apply auto-add suggestions to the live editor state, then trigger a save
   * so the new nodes/edges are in the DB before the publish modal re-inspects.
   */
  const handleAutoAddPublishIO = useCallback(async (): Promise<InspectionResult> => {
    const inspection = inspectFlowForPublish(nodes, edges);
    if (inspection.fixableCount === 0) return inspection;

    const patch = buildAutoAddPatch(nodes, inspection);
    recordUserAction('Add publish inputs/outputs');

    // 1. Add new panel nodes (with full handler bindings via createNodeHandlers)
    if (patch.nodesToAdd.length > 0) {
      setNodes((prev) => [
        ...prev,
        ...patch.nodesToAdd.map((n) => ({
          ...n,
          data: {
            ...createNodeHandlers(n.id),
            ...n.data,
          },
          zIndex: getHighestZIndex(),
        })),
      ]);
    }

    // 2. Add edges
    if (patch.edgesToAdd.length > 0) {
      setEdges((prev) => [...prev, ...patch.edgesToAdd]);
    }

    // 3. Apply per-node data updates (clear prompts, set apiOutput flags)
    if (patch.nodeUpdates.length > 0) {
      const updateMap = new Map(patch.nodeUpdates.map((u) => [u.nodeId, u.data]));
      setNodes((prev) =>
        prev.map((n) => {
          const upd = updateMap.get(n.id);
          if (!upd) return n;
          return { ...n, data: { ...n.data, ...upd } };
        }),
      );
    }

    autoSave.markDirty();
    toastService.success(patch.summary);

    // Re-inspect after the React Flow state updates (next tick).
    // We can't read the new state synchronously inside the same callback,
    // so we project the patch onto the snapshot we already have.
    const projectedNodes = nodes.map((n) => {
      const upd = patch.nodeUpdates.find((u) => u.nodeId === n.id)?.data;
      return upd ? { ...n, data: { ...n.data, ...upd } } : n;
    });
    const projectedNodesWithAdded = [...projectedNodes, ...patch.nodesToAdd];
    const projectedEdges = [...edges, ...patch.edgesToAdd];
    return inspectFlowForPublish(projectedNodesWithAdded, projectedEdges);
  }, [nodes, edges, setNodes, setEdges, createNodeHandlers, getHighestZIndex, autoSave]);

  /**
   * Update the label that appears for a publish input/output.
   *
   * Writes to BOTH `data.label` AND the api*Label field so:
   *   - The editor's node title sees it (reads `data.label`)
   *   - The publish schema sees it whether or not the node has the apiInput
   *     /apiOutput flag set (the schema prefers api*Label, falls back to label)
   *
   * Then forces an immediate save (bypassing the auto-save enabled check)
   * so the label survives a page refresh — relying on `markDirty` alone
   * silently failed when auto-save was disabled in user settings.
   */
  const handleUpdatePublishLabel = useCallback(
    (kind: 'input' | 'output', nodeId: string, label: string): InspectionResult => {
      const trimmed = label.trim();
      const patch = kind === 'input'
        ? { label: trimmed, apiInputLabel: trimmed }
        : { label: trimmed, apiOutputLabel: trimmed };

      recordUserAction('Rename', { coalesceKey: `publish-label:${nodeId}` });
      setNodes((prev) =>
        prev.map((n) => (n.id === nodeId ? { ...n, data: { ...n.data, ...patch } } : n)),
      );

      // Force a save so the label persists across refreshes regardless of
      // whether the user has auto-save enabled.
      autoSave.triggerSave(true).catch((err) => {
        console.error('[handleUpdatePublishLabel] save failed:', err);
      });

      // Project the change onto a fresh inspection so the modal can show
      // the updated label immediately (setNodes is async w.r.t. state read).
      const projectedNodes = nodes.map((n) =>
        n.id === nodeId ? { ...n, data: { ...n.data, ...patch } } : n,
      );
      return inspectFlowForPublish(projectedNodes, edges);
    },
    [nodes, edges, setNodes, autoSave],
  );

  const handleSaveFlow = useCallback(async (data: any) => {
    // See handleQuickSave: only what was changed before this point is saved.
    const rev = autoSave.snapshotRevision();
    const session = saveSessionsRef.current!.current;
    try {
      const viewport = getViewport();
      const flow = flowSerializationService.serializeFlow(
        nodes,
        edges,
        viewport,
        {
          // TODO(templates): "save as template" reuses the flow's id, so a second template from the same knod overwrites the first.
          id: saveSessionsRef.current!.reserveId(session, currentFlowId),
          name: data.name,
          description: data.description,
          tags: data.tags,
          brandVoiceId: currentBrandVoiceId,
          // Preserve project association on full saves too (skip when
          // saving as a template — templates are project-agnostic).
          projectId: data.saveAsTemplate ? null : currentFlowProjectId
        },
        data.saveAsTemplate
      );

      if (data.saveAsTemplate && data.templateCategory) {
        const template = {
          ...flow,
          isTemplate: true as const,
          category: data.templateCategory
        };
        await flowStorageService.saveTemplate(template);
        toastService.success(`Template "${data.name}" saved`);
        // Only the TEMPLATE row was written: the flow's own edits stay unsaved
        // (dirty), so no markSavedUpTo here.
        if (session !== saveSessionsRef.current!.current) return;
        saveSessionsRef.current!.recordSaved(session, currentFlowId, flow.metadata.id, true);
      } else {
        await flowStorageService.saveFlow(flow);
        autoSave.markSavedUpTo(rev); // the flow row holds the graph as of `rev`
        if (session !== saveSessionsRef.current!.current) {
          // Another flow was opened meanwhile; leave the editor's state alone.
          toastService.success(`Knod "${data.name}" saved`);
          return;
        }
        saveSessionsRef.current!.recordSaved(session, currentFlowId, flow.metadata.id, true);
        // Persist current knod ID for page reload restoration
        flowStorageService.setCurrentFlowId(flow.metadata.id);
        
        // Update URL to reflect the saved knod
        navigate(`/knod/${flow.metadata.id}`, { replace: true });
        
        toastService.success(`Knod "${data.name}" saved`);
      }

      setCurrentFlowId(flow.metadata.id);
      setCurrentFlowName(flow.metadata.name);
      setCurrentFlowDescription(flow.metadata.description || '');
      setCurrentFlowTags(flow.metadata.tags || []);
    } catch (error) {
      logger.error('Failed to save flow:', error);
      // Save-as-new from the dialog is a creation like any other. See
      // handleQuickSave for why the generic message is not good enough here.
      if (explainIfFlowLimit(error)) return;
      toastService.error(friendlyErrors.saveFailed);
    }
  }, [nodes, edges, currentFlowId, getViewport, autoSave, explainIfFlowLimit]);

  // Handle "Update Template" - save current flow state to the loaded template
  const handleUpdateTemplate = useCallback(async () => {
    if (!currentTemplateId) {
      toastService.error('No template loaded to update');
      return;
    }

    try {
      const viewport = getViewport();
      const flow = flowSerializationService.serializeFlow(
        nodes,
        edges,
        viewport,
        {
          id: currentTemplateId, // Use the original template ID
          name: currentFlowName,
          description: currentFlowDescription,
          tags: currentFlowTags,
        },
        true // isTemplate
      );

      const template = {
        ...flow,
        isTemplate: true as const,
        category: currentTemplateCategory || 'Other'
      };

      await flowStorageService.saveTemplate(template);
      toastService.success(`Template "${currentFlowName}" updated`);
      // Only the template row was written; the flow's saved state is untouched.
    } catch (error) {
      logger.error('Failed to update template:', error);
      toastService.error(friendlyErrors.saveFailed);
    }
  }, [nodes, edges, currentTemplateId, currentFlowName, currentFlowDescription, currentFlowTags, currentTemplateCategory, getViewport, autoSave]);

  const handleLoadFlow = useCallback(async (flow: any) => {
    // A dashboard summary's `state` is empty because the listing projection
    // omits the column, not because the knod is. Opening one would show a blank
    // canvas and the next auto-save would persist it over the real graph, so
    // refuse loudly — callers must fetch the flow with loadFlow() first.
    if (flow?.metadata?.isSummary) {
      logger.error('[handleLoadFlow] Refusing to open a dashboard summary:', flow.metadata.id);
      toastService.error(friendlyErrors.loadFailed);
      return;
    }

    try {
      const state = flowSerializationService.deserializeFlow(flow);
      
      // FIX: Normalize custom node types (handle legacy underscore format)
      const normalizedNodes = state.nodes.map(original => {
        // Legacy Veo model/settings first (see migrateNodeTypes), then the type map.
        const node = migrateLegacyVeoNode(original);
        // If node type contains underscore and starts with "custom_", convert to hyphen format
        if (node.type && node.type.includes('_') && node.type.startsWith('custom_')) {
          const correctedType = node.type.replace('custom_custom-', 'custom-');
          logger.info(`[LoadFlow] Correcting node type: ${node.type} → ${correctedType}`);
          return {
            ...node,
            type: correctedType
          };
        }
        // Migrate legacy node types
        const newType = LEGACY_NODE_TYPE_MAP[node.type || ''];
        if (newType) {
          logger.info(`[LoadFlow] Migrating node type: ${node.type} → ${newType}`);
          return { ...node, type: newType };
        }
        // Migrate deprecated Flux model selections
        const nodeData = node.data as Record<string, unknown>;
        if (node.type === 'fluxImage' && nodeData?.selectedModel) {
          const deprecatedFluxModels = [
            // Flux 1 Kontext series
            'flux-1-kontext-pro', 'flux-1-kontext-max', 'flux-1-kontext-dev',
            // Flux 1.1 series (old defaults)
            'flux-1.1-ultra', 'flux-1.1-pro',
            // Other deprecated models
            'flux-kontext-pro', 'flux-realism', 'ideogram-v3',
            // Removed Flux 2 models
            'flux-2-flex'
          ];
          if (deprecatedFluxModels.includes(nodeData.selectedModel as string)) {
            // Migrate to flux-2-max (was previously flux-2-pro, which now points to
            // the real BFL FLUX.2 [pro] mid-tier — a different model).
            logger.info(`[LoadFlow] Migrating deprecated Flux model: ${nodeData.selectedModel} → flux-2-max`);
            return {
              ...node,
              data: {
                ...nodeData,
                selectedModel: 'flux-2-max'
              }
            };
          }
          // Jun 2026: Knods's 'flux-2-pro' historically pointed to fal-ai/flux-2-max
          // (the highest-quality tier). We renamed it to 'flux-2-max' to free the
          // 'flux-2-pro' slot for the real BFL FLUX.2 [pro] mid-tier. Migrate stored
          // values so existing flows keep producing the same output.
          // Heuristic: an old saved value of 'flux-2-pro' means "Max". The new
          // 'flux-2-pro' is only written by clients that have this code, and they
          // can't be older than this migration, so this is safe to apply broadly.
          // (Older saved flows never had access to the real BFL FLUX.2 [pro] option.)
          if (nodeData.selectedModel === 'flux-2-pro') {
            const savedAt = (node.data as Record<string, unknown>)?.fluxModelMigratedAt;
            if (!savedAt) {
              logger.info('[LoadFlow] Renaming legacy FLUX model: flux-2-pro → flux-2-max (Knods alias swap)');
              return {
                ...node,
                data: {
                  ...nodeData,
                  selectedModel: 'flux-2-max',
                  fluxModelMigratedAt: '2026-06-rename',
                }
              };
            }
          }
        }
        return node;
      });
      
      // Restore image URLs from FileStorageService for ImagePanel nodes
      const restoredNodes = await Promise.all(normalizedNodes.map(async (node) => {
        const nodeData = node.data as Record<string, unknown>;
        if (node.type === 'imagePanel' && nodeData?.fileId) {
          try {
            await fileStorageService.init();
            const fileData = await fileStorageService.getFileData(nodeData.fileId as string);
            const imageUrl = URL.createObjectURL(fileData);
            
            logger.info(`Restored image for node ${node.id}: ${nodeData.fileName} (ID: ${nodeData.fileId})`);
            
            // Build context for the restored image
            const imageContext = {
              messages: [
                {
                  role: 'user' as const,
                  content: [
                    {
                      type: 'text' as const,
                      text: 'User uploaded an image'
                    },
                    {
                      type: 'image' as const,
                      imageUrl: imageUrl,
                      mimeType: node.data.fileType || 'image/png'
                    }
                  ]
                }
              ]
            };
            
            return {
              ...node,
              data: {
                ...node.data,
                imageUrl,
                content: imageUrl,
                context: imageContext
              }
            };
          } catch (error) {
            logger.error(`Failed to restore image for node ${node.id}:`, error);
            // Return node without image restoration
            return node;
          }
        }
        return node;
      }));
      
      // Re-attach handlers to loaded nodes and reset transient states
      const nodesWithHandlers = restoredNodes.map(node => {
        const handlers = createNodeHandlers(node.id);
        const nodeData = node.data as Record<string, unknown>;
        
        // For custom nodes, restore the definition property
        let additionalData = {};
        if (node.type?.startsWith('custom-') && nodeData?.customNodeDefinition) {
          const customDef = nodeData.customNodeDefinition as unknown as CustomNodeDefinition;
          let universalDef = getUniversalDefinition(customDef);
          
          // If not migrated, convert it now
          if (!universalDef) {
            logger.warn('[handleLoadFlow] Custom node not migrated, converting:', customDef.name);
            universalDef = convertCustomNodeToUniversal(customDef);
          }
          
          additionalData = { definition: universalDef };
        }
        
        return {
          ...node,
          data: {
            ...node.data,
            ...additionalData,
            // Reset transient processing states that should not persist
            streamingInProgress: false,
            isProcessing: false,
            onOutput: handlers.onOutput,
            onReceiveInput: handlers.onReceiveInput
          }
        };
      });
      
      // These handlers do their own inline legacy-type migration rather than
      // calling migrateNodeTypes(), so they need the dimension backstop
      // explicitly. Without it the Load-dialog and template paths still hand
      // React Flow unsized nodes. See utils/nodeDimensionRepair.ts.
      setNodes(repairNodeDimensionsAll(nodesWithHandlers));
      flowHistory.clear(); // a different graph: old steps must not apply to it
      saveSessionsRef.current?.replaceContent(); // ...and its saves must not write this one
      setEdges(state.edges);
      
      setCurrentFlowId(flow.metadata.id);
      setCurrentFlowName(flow.metadata.name);
      setCurrentFlowDescription(flow.metadata.description || '');
      setCurrentFlowTags(flow.metadata.tags || []);
      setCurrentFlowThumbnail(flow.metadata.thumbnail);
      setCurrentBrandVoiceId(flow.metadata.brandVoiceId);
      setCurrentFlowProjectId(flow.metadata.projectId ?? null);

      // Restore viewport position and zoom
      if (state.viewport) {
        setPendingViewport(state.viewport);
      }
      
      // Clear template tracking (this is a regular flow, not a template)
      setCurrentTemplateId(null);
      setCurrentTemplateCategory(undefined);
      
      // Persist current knod ID for page reload restoration
      flowStorageService.setCurrentFlowId(flow.metadata.id);
      
      // Update URL to reflect the loaded knod
      navigate(`/knod/${flow.metadata.id}`, { replace: true });
      
      toastService.success(`Knod "${flow.metadata.name}" loaded`);
    } catch (error) {
      logger.error('Failed to load knod:', error);
      toastService.error(friendlyErrors.loadFailed);
    }
  }, [setNodes, setEdges, createNodeHandlers, navigate, flowHistory]);

  const handleLoadTemplate = useCallback(async (template: any) => {
    try {
      const newFlow = flowSerializationService.instantiateTemplate(template);
      const state = flowSerializationService.deserializeFlow(newFlow);
      
      // Restore image URLs from FileStorageService for ImagePanel nodes
      const restoredNodes = await Promise.all(state.nodes.map((n) => migrateLegacyVeoNode(n)).map(async (node) => {
        const nodeData = node.data as Record<string, unknown>;
        if (node.type === 'imagePanel' && nodeData?.fileId) {
          try {
            await fileStorageService.init();
            const fileData = await fileStorageService.getFileData(nodeData.fileId as string);
            const imageUrl = URL.createObjectURL(fileData);
            
            logger.info(`Restored image for template node ${node.id}: ${nodeData.fileName} (ID: ${nodeData.fileId})`);
            
            // Build context for the restored image
            const imageContext = {
              messages: [
                {
                  role: 'user' as const,
                  content: [
                    {
                      type: 'text' as const,
                      text: 'User uploaded an image'
                    },
                    {
                      type: 'image' as const,
                      imageUrl: imageUrl,
                      mimeType: node.data.fileType || 'image/png'
                    }
                  ]
                }
              ]
            };
            
            return {
              ...node,
              data: {
                ...node.data,
                imageUrl,
                content: imageUrl,
                context: imageContext
              }
            };
          } catch (error) {
            logger.error(`Failed to restore image for template node ${node.id}:`, error);
            return node;
          }
        }
        return node;
      }));
      
      // Re-attach handlers to loaded nodes and reset transient states
      const nodesWithHandlers = restoredNodes.map(node => {
        const handlers = createNodeHandlers(node.id);
        const nodeData = node.data as Record<string, unknown>;
        
        // For custom nodes, restore the definition property
        let additionalData = {};
        if (node.type?.startsWith('custom-') && nodeData?.customNodeDefinition) {
          const customDef = nodeData.customNodeDefinition as unknown as CustomNodeDefinition;
          let universalDef = getUniversalDefinition(customDef);
          
          // If not migrated, convert it now
          if (!universalDef) {
            logger.warn('[handleLoadTemplate] Custom node not migrated, converting:', customDef.name);
            universalDef = convertCustomNodeToUniversal(customDef);
          }
          
          additionalData = { definition: universalDef };
        }
        
        return {
          ...node,
          data: {
            ...node.data,
            ...additionalData,
            // Reset transient processing states that should not persist
            streamingInProgress: false,
            isProcessing: false,
            onOutput: handlers.onOutput,
            onReceiveInput: handlers.onReceiveInput
          }
        };
      });
      
      // These handlers do their own inline legacy-type migration rather than
      // calling migrateNodeTypes(), so they need the dimension backstop
      // explicitly. Without it the Load-dialog and template paths still hand
      // React Flow unsized nodes. See utils/nodeDimensionRepair.ts.
      setNodes(repairNodeDimensionsAll(nodesWithHandlers));
      flowHistory.clear(); // a different graph: old steps must not apply to it
      saveSessionsRef.current?.replaceContent(); // ...and its saves must not write this one
      setEdges(state.edges);
      
      setCurrentFlowId(newFlow.metadata.id);
      setCurrentFlowName(newFlow.metadata.name);
      setCurrentFlowDescription(newFlow.metadata.description || '');
      setCurrentFlowTags(newFlow.metadata.tags || []);
      
      // Track original template for "Update Template" functionality
      setCurrentTemplateId(template.metadata.id);
      setCurrentTemplateCategory(template.category);
      
      toastService.success(`Template "${template.metadata.name}" loaded`);
    } catch (error) {
      logger.error('Failed to load template:', error);
      toastService.error(friendlyErrors.templateLoadFailed);
    }
  }, [setNodes, setEdges, flowHistory]);

  // Handle "Run Template" - load and immediately execute
  const handleRunTemplate = useCallback(async (template: any) => {
    try {
      // First load the template
      const newFlow = flowSerializationService.instantiateTemplate(template);
      const state = flowSerializationService.deserializeFlow(newFlow);
      
      // Restore image URLs from FileStorageService for ImagePanel nodes
      const restoredNodes = await Promise.all(state.nodes.map((n) => migrateLegacyVeoNode(n)).map(async (node) => {
        const nodeData = node.data as Record<string, unknown>;
        if (node.type === 'imagePanel' && nodeData?.fileId) {
          try {
            await fileStorageService.init();
            const fileData = await fileStorageService.getFileData(nodeData.fileId as string);
            const imageUrl = URL.createObjectURL(fileData);
            
            const imageContext = {
              messages: [
                {
                  role: 'user' as const,
                  content: [
                    { type: 'text' as const, text: 'User uploaded an image' },
                    { type: 'image' as const, imageUrl: imageUrl, mimeType: node.data.fileType || 'image/png' }
                  ]
                }
              ]
            };
            
            return {
              ...node,
              data: { ...node.data, imageUrl, content: imageUrl, context: imageContext }
            };
          } catch (error) {
            logger.error(`Failed to restore image for template node ${node.id}:`, error);
            return node;
          }
        }
        return node;
      }));
      
      // Re-attach handlers to loaded nodes and reset transient states
      const nodesWithHandlers = restoredNodes.map(node => {
        const handlers = createNodeHandlers(node.id);
        const nodeData = node.data as Record<string, unknown>;
        
        let additionalData = {};
        if (node.type?.startsWith('custom-') && nodeData?.customNodeDefinition) {
          const customDef = nodeData.customNodeDefinition as unknown as CustomNodeDefinition;
          let universalDef = getUniversalDefinition(customDef);
          if (!universalDef) {
            universalDef = convertCustomNodeToUniversal(customDef);
          }
          additionalData = { definition: universalDef };
        }
        
        return {
          ...node,
          data: {
            ...node.data,
            ...additionalData,
            // Reset transient processing states that should not persist
            streamingInProgress: false,
            isProcessing: false,
            onOutput: handlers.onOutput,
            onReceiveInput: handlers.onReceiveInput
          }
        };
      });
      
      // These handlers do their own inline legacy-type migration rather than
      // calling migrateNodeTypes(), so they need the dimension backstop
      // explicitly. Without it the Load-dialog and template paths still hand
      // React Flow unsized nodes. See utils/nodeDimensionRepair.ts.
      setNodes(repairNodeDimensionsAll(nodesWithHandlers));
      flowHistory.clear(); // a different graph: old steps must not apply to it
      saveSessionsRef.current?.replaceContent(); // ...and its saves must not write this one
      setEdges(state.edges);
      
      setCurrentFlowId(newFlow.metadata.id);
      setCurrentFlowName(newFlow.metadata.name);
      setCurrentFlowDescription(newFlow.metadata.description || '');
      setCurrentFlowTags(newFlow.metadata.tags || []);
      
      toastService.success(`Running template "${template.metadata.name}"...`);
      
      // Run the flow after a short delay to allow React to update
      setTimeout(() => {
        runFullFlow();
      }, 100);
    } catch (error) {
      logger.error('Failed to run template:', error);
      toastService.error(friendlyErrors.operationFailed);
    }
  }, [setNodes, setEdges, createNodeHandlers, runFullFlow, flowHistory]);

  // Currently not exposed in UI
  // const handleNewFlow = useCallback(async () => {
  //   if (nodes.length > 0 || edges.length > 0) {
  //     const confirmed = await confirmDialogService.confirm({
  //       title: 'Start New Flow?',
  //       message: 'Starting a new flow will clear the current canvas. Any unsaved changes will be lost.',
  //       confirmText: 'Start New Flow',
  //       cancelText: 'Cancel',
  //       type: 'warning'
  //     });
  //     
  //     if (!confirmed) {
  //       return;
  //     }
  //   }
  //   
  //   // Start with empty canvas
  //   setNodes([]);
  //   setEdges(initialEdges);
  //   setCurrentFlowId(null);
  //   setCurrentFlowName('');
  //   setCurrentFlowDescription('');
  //   setCurrentFlowTags([]);
  //   
  //   // Clear template tracking
  //   setCurrentTemplateId(null);
  //   setCurrentTemplateCategory(undefined);
  //   
  //   // Clear persisted current flow ID
  //   flowStorageService.clearCurrentFlowId();
  //   
  //   // Navigate to home (clear flow URL)
  //   navigate('/', { replace: true });
  //   
  //   toastService.info('New flow started');
  // }, [nodes, edges, setNodes, setEdges, navigate]);

  // Close the node palette submenu
  const closeNodePaletteSubmenu = useCallback(() => {
    nodePaletteRef.current?.closeSubmenu();
  }, []);

  const openNodePaletteCategory = useCallback((category: PaletteSection) => {
    nodePaletteRef.current?.openCategory(category);
  }, []);

  // Close both palette submenu and canvas context menu
  const closeAllMenus = useCallback(() => {
    closeContextMenu();
    closeNodePaletteSubmenu();
  }, [closeContextMenu, closeNodePaletteSubmenu]);

  // Handle node drag start - add class to hide other settings panels
  const handleNodeDragStart = useCallback(() => {
    closeNodePaletteSubmenu();
    document.body.classList.add('node-dragging');
  }, [closeNodePaletteSubmenu]);

  // Handle node drag stop - keep the dragged node at top z-index
  const handleNodeDragStop = useCallback((_event: React.MouseEvent, node: Node, nodes: Node[]) => {
    // Remove the dragging class
    document.body.classList.remove('node-dragging');
    
    // Get the highest z-index and assign it to the dragged node(s)
    const newZIndex = getHighestZIndex();
    const draggedNodeIds = nodes.length > 0 ? nodes.map(n => n.id) : [node.id];
    
    setNodes(prevNodes => prevNodes.map(n => {
      if (draggedNodeIds.includes(n.id)) {
        return { ...n, zIndex: newZIndex };
      }
      return n;
    }));
  }, [getHighestZIndex, setNodes]);

  // Handle node selection from palette (click)
  const handleNodePaletteSelect = useCallback((nodeType: string) => {
    // Get viewport center
    const viewport = getViewport();
    const centerX = (window.innerWidth / 2 - viewport.x) / viewport.zoom;
    const centerY = (window.innerHeight / 2 - viewport.y) / viewport.zoom;
    const paletteOffset = 140; // Half of palette width

    // When Iris emits multiple addNode actions they all arrive synchronously,
    // so React hasn't committed the new nodes yet and findNonOverlappingPosition
    // would see a stale snapshot. Instead we track placement ourselves:
    // within a 1-second window, each successive call steps 500px to the right.
    const now = Date.now();
    const SEQUENCE_WINDOW_MS = 1000;
    const STEP_PX = 500;

    let targetX: number;
    let targetY: number;

    if (irisPlacementRef.current && now < irisPlacementRef.current.expiresAt) {
      // Continue sequencing from the last placed node
      const nextIndex = irisPlacementRef.current.index + 1;
      targetX = irisPlacementRef.current.x + STEP_PX;
      targetY = irisPlacementRef.current.y;
      irisPlacementRef.current = { x: targetX, y: targetY, index: nextIndex, expiresAt: now + SEQUENCE_WINDOW_MS };
    } else {
      // First node in a new batch — place near viewport center
      targetX = centerX + paletteOffset;
      targetY = centerY;
      irisPlacementRef.current = { x: targetX, y: targetY, index: 0, expiresAt: now + SEQUENCE_WINDOW_MS };
    }

    createNodeAtPosition(nodeType as CustomNodeType, targetX, targetY);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getViewport]);

  // Handle node drag from palette
  const handleNodePaletteDragStart = useCallback((event: React.DragEvent, nodeType: string) => {
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.effectAllowed = 'move';
  }, []);

  // Helper function to create node at specific position
  const createNodeAtPosition = useCallback((nodeType: CustomNodeType, flowX: number, flowY: number, providedId?: string, initialData?: Record<string, unknown>): string => {
    const newId = providedId ?? `${nodeType}-${Date.now()}`;

    // Build node data from registry (single source of truth)
    const { width, height } = getNodeDimensions(nodeType);
    let nodeData: any = {
      ...createNodeHandlers(newId),
      brandVoiceId: currentBrandVoiceId,
      ...getInitialNodeData(nodeType),
    };

    // Merge caller-provided initialData overrides
    if (initialData) {
      nodeData = { ...nodeData, ...initialData };
    }

    // Calculate position to center the node horizontally and offset vertically
    const position = {
      x: flowX - (width / 2),
      y: flowY - height
    };

    const newNode: any = {
      id: newId,
      position,
      type: nodeType,
      width,
      height,
      zIndex: getHighestZIndex(),
      data: nodeData
    };

    addNodeWithAnimation(newNode);
    completeActionRef.current('add-node', { nodeType });
    return newId;
  }, [createNodeHandlers, getHighestZIndex, addNodeWithAnimation, currentBrandVoiceId, completeActionRef]);

  // Handle Iris "addFlow" action — create a fully-connected multi-node template at once
  const handleIrisAddFlow = useCallback((template: {
    nodes: Array<{ id: string; nodeType: string; initialData?: Record<string, unknown> }>;
    edges: Array<{ source: string; target: string }>;
  }) => {
    if (!template.nodes?.length) return;

    const viewport = getViewport();
    const centerX = (window.innerWidth / 2 - viewport.x) / viewport.zoom;
    const centerY = (window.innerHeight / 2 - viewport.y) / viewport.zoom;

    // 1. Pre-assign stable real IDs so we can build edges that reference them
    let counter = 0;
    const idMap: Record<string, string> = {};
    for (const n of template.nodes) {
      idMap[n.id] = `${n.nodeType.toLowerCase()}-iris-${Date.now()}-${counter++}`;
    }

    // 2. BFS to compute column depth (topological distance from any root)
    const inDeg: Record<string, number> = {};
    const outAdj: Record<string, string[]> = {};
    for (const n of template.nodes) { inDeg[n.id] = 0; outAdj[n.id] = []; }
    for (const e of (template.edges ?? [])) {
      outAdj[e.source]?.push(e.target);
      inDeg[e.target] = (inDeg[e.target] ?? 0) + 1;
    }
    const depth: Record<string, number> = {};
    const bfsQueue: string[] = template.nodes.filter(n => inDeg[n.id] === 0).map(n => n.id);
    for (const id of bfsQueue) depth[id] = 0;
    for (let i = 0; i < bfsQueue.length; i++) {
      const id = bfsQueue[i];
      for (const child of outAdj[id]) {
        const d = depth[id] + 1;
        if (depth[child] === undefined || depth[child] < d) {
          depth[child] = d;
          bfsQueue.push(child);
        }
      }
    }

    // 3. Group nodes by column depth
    const maxDepth = Math.max(0, ...Object.values(depth));
    const columns: string[][] = Array.from({ length: maxDepth + 1 }, () => []);
    for (const n of template.nodes) columns[depth[n.id] ?? 0].push(n.id);

    // 4. Compute canvas positions — centered on viewport, columns left → right
    const COL_SPACING = 540;
    const ROW_SPACING = 520;
    const NODE_W = 448;   // default node width (matches generator spawn size)
    const NODE_H = 448;   // default node height (matches generator spawn size)
    const COLLISION_PAD = 60;
    const totalW = maxDepth * COL_SPACING;
    const startX = centerX - totalW / 2 + 140;
    const posMap: Record<string, { x: number; y: number }> = {};
    for (let col = 0; col <= maxDepth; col++) {
      const colNodes = columns[col];
      const totalH = (colNodes.length - 1) * ROW_SPACING;
      for (let row = 0; row < colNodes.length; row++) {
        posMap[colNodes[row]] = {
          x: startX + col * COL_SPACING,
          y: centerY - totalH / 2 + row * ROW_SPACING,
        };
      }
    }

    // 4b. Collision detection — shift flow below existing nodes if overlapping
    const existingNodes = nodesRef.current;
    if (existingNodes.length > 0) {
      // Bounding box of planned new nodes
      const allPos = Object.values(posMap);
      const newMinX = Math.min(...allPos.map(p => p.x)) - COLLISION_PAD;
      const newMaxX = Math.max(...allPos.map(p => p.x)) + NODE_W + COLLISION_PAD;
      const newMinY = Math.min(...allPos.map(p => p.y)) - COLLISION_PAD;
      const newMaxY = Math.max(...allPos.map(p => p.y)) + NODE_H + COLLISION_PAD;
      // Find existing nodes whose bounding boxes overlap the planned area
      let lowestOverlapY = -Infinity;
      let hasCollision = false;
      for (const en of existingNodes) {
        const ex = en.position.x;
        const ey = en.position.y;
        const ew = (en.measured?.width ?? en.width ?? NODE_W);
        const eh = (en.measured?.height ?? en.height ?? NODE_H);

        // Check AABB overlap
        if (ex + ew > newMinX && ex < newMaxX && ey + eh > newMinY && ey < newMaxY) {
          hasCollision = true;
          lowestOverlapY = Math.max(lowestOverlapY, ey + eh);
        }
      }

      if (hasCollision) {
        // Shift all planned positions so the flow starts below the lowest colliding node
        const shiftY = lowestOverlapY + COLLISION_PAD - Math.min(...allPos.map(p => p.y));
        for (const id of Object.keys(posMap)) {
          posMap[id].y += shiftY;
        }

        // Pan viewport to center on the new flow position after nodes are created
        const shiftedPositions = Object.values(posMap);
        const flowCenterX = (Math.min(...shiftedPositions.map(p => p.x)) + Math.max(...shiftedPositions.map(p => p.x)) + NODE_W) / 2;
        const flowCenterY = (Math.min(...shiftedPositions.map(p => p.y)) + Math.max(...shiftedPositions.map(p => p.y)) + NODE_H) / 2;
        setTimeout(() => {
          const vp = getViewport();
          setViewport(
            {
              x: window.innerWidth / 2 - flowCenterX * vp.zoom,
              y: window.innerHeight / 2 - flowCenterY * vp.zoom,
              zoom: vp.zoom,
            },
            { duration: 500 }
          );
        }, 100);
      }
    }

    // 5. Create all nodes with pre-assigned IDs and initialData
    // One undo step for nodes + the edges wired a tick later.
    const irisHistoryKey = `iris-flow:${Object.values(idMap)[0]}`;
    recordUserAction('Add flow from Iris', { coalesceKey: irisHistoryKey, hold: true });
    if (!template.edges?.length) releaseUserAction(irisHistoryKey);
    for (const n of template.nodes) {
      const entry = Object.values(NODE_REGISTRY).find(
        r => r.type === n.nodeType || r.camelCaseType === n.nodeType
      );
      const camelType = (entry ? entry.camelCaseType : n.nodeType) as CustomNodeType;
      const pos = posMap[n.id];
      createNodeAtPosition(camelType, pos.x, pos.y, idMap[n.id], n.initialData);
    }

    // 6. Wire up edges (after a tick so React has committed the new nodes)
    if (template.edges?.length) {
      const newEdges: Edge[] = template.edges.map(e => {
        // Auto-resolve the correct target handle based on the source node's output type.
        // Without this, image outputs land on the text input handle (first/default handle).
        const sourceNodeType = template.nodes.find(n => n.id === e.source)?.nodeType ?? '';
        const targetNodeType = template.nodes.find(n => n.id === e.target)?.nodeType ?? '';

        const sourceEntry = Object.values(NODE_REGISTRY).find(
          r => r.type === sourceNodeType || r.camelCaseType === sourceNodeType
        );
        const targetEntry = Object.values(NODE_REGISTRY).find(
          r => r.type === targetNodeType || r.camelCaseType === targetNodeType
        );

        const sourceOutputType = (sourceEntry?.definition?.outputsMediaType as string) || 'any';
        const targetCamelType = targetEntry?.camelCaseType || '';
        const targetNodeData = template.nodes.find(n => n.id === e.target)?.initialData || {};
        const rawTargetHandle = findCompatibleInputHandle(targetCamelType, sourceOutputType, targetNodeData);
        const targetNodeForNormalization = {
          id: idMap[e.target],
          type: targetCamelType,
          data: targetNodeData,
        } as Node;
        const resolvedTargetHandle = rawTargetHandle
          ? (normalizeTargetHandleForNode(targetNodeForNormalization, rawTargetHandle) ?? undefined)
          : undefined;
        const sourceConnectorType = (['text', 'image', 'video', 'any'].includes(sourceOutputType)
          ? sourceOutputType
          : 'any') as ConnectorType;
        const edgeColor = EDGE_COLOR_MAP[sourceConnectorType] || EDGE_COLOR_MAP.any;

        return {
          id: `iris-e-${idMap[e.source]}-${idMap[e.target]}`,
          source: idMap[e.source],
          target: idMap[e.target],
          ...(resolvedTargetHandle ? { targetHandle: resolvedTargetHandle } : {}),
          type: sourceConnectorType,
          animated: false,
          style: { stroke: edgeColor, strokeWidth: 3 },
        };
      });
      setTimeout(() => {
        setEdges(prev => [...prev, ...newEdges]);
        markFlowDirty(); // direct setEdges bypasses onEdgesChange
        releaseUserAction(irisHistoryKey);
      }, 0);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getViewport, setViewport, setEdges, markFlowDirty]);

  // Handle Iris "updateNode" action — update the prompt on an existing node
  const handleIrisUpdateNode = useCallback((nodeId: string, prompt: string) => {
    recordUserAction('Edit prompt (Iris)');
    setNodes(nds => nds.map(n =>
      n.id === nodeId ? { ...n, data: { ...n.data, prompt } } : n
    ));
    markFlowDirty(); // direct setNodes bypasses onNodesChange
  }, [setNodes, markFlowDirty]);

  // Handle drop on canvas for creating image nodes and nodes from palette
  const handleDrop = useCallback(async (event: React.DragEvent) => {
    event.preventDefault();
    
    try {
      // Check if it's a node from the palette
      const nodeType = event.dataTransfer.getData('application/reactflow');
      if (nodeType) {
        const flowPosition = reactFlowScreenToFlowPosition({
          x: event.clientX,
          y: event.clientY,
        });
        createNodeAtPosition(nodeType as CustomNodeType, flowPosition.x, flowPosition.y);
        return;
      }
      
      // Check if it's a file drop from FilePanel (has application/json data) — must check BEFORE
      // dataTransfer.files because the browser auto-adds the dragged <img> thumbnail to files,
      // which would incorrectly trigger the filesystem file handler with a low-res thumbnail.
      const jsonData = event.dataTransfer.getData('application/json');
      if (jsonData) {
        const dragData = JSON.parse(jsonData);

        // If dragging a global file to stage, copy it to flow assets
        if (dragData.scope === 'global' && currentFlowId) {
          try {
            const response = await fetch(dragData.fileUrl);
            const blob = await response.blob();
            const file = new File([blob], dragData.fileName, { type: dragData.fileType });

            await fileStorageService.uploadFile(file, {
              scope: 'flow',
              flowId: currentFlowId,
              folder: 'global-copies'
            });

            logger.debug('[CreativeNodeFlow] Copied global file to flow assets:', dragData.fileName);
          } catch (error) {
            logger.error('[CreativeNodeFlow] Failed to copy global file to flow:', error);
          }
        }

        // Handle image file drop from FilePanel
        if (dragData.isImage && dragData.fileUrl) {
          const reactFlowBounds = reactFlowWrapperRef.current?.getBoundingClientRect();
          if (!reactFlowBounds) return;

          const img = new Image();
          img.onload = () => {
            const PANEL_WIDTH = 554;
            const HEADER_HEIGHT = 44;

            const rawPosition = reactFlowScreenToFlowPosition({
              x: event.clientX,
              y: event.clientY,
            });

            const position = {
              x: rawPosition.x - (PANEL_WIDTH / 2),
              y: rawPosition.y - (HEADER_HEIGHT / 2)
            };

            const aspectRatio = img.naturalWidth / img.naturalHeight;
            const nodeWidth = 554;
            const nodeHeight = Math.round(nodeWidth / aspectRatio) + 100;

            const newNode = {
              id: uniqueNodeId('imagePanel'),
              type: 'imagePanel',
              position,
              width: nodeWidth,
              height: nodeHeight,
              data: {
                imageUrl: dragData.fileUrl,
                fileName: dragData.fileName,
                label: `Image: ${dragData.fileName}`,
                preloadedWidth: img.naturalWidth,
                preloadedHeight: img.naturalHeight,
                fileId: dragData.fileId,
                fileType: dragData.fileType
              }
            };

            addNodeWithAnimation(newNode);
          };
          img.onerror = () => {
            console.error('[CreativeNodeFlow] Failed to preload image:', dragData.fileUrl);
            const PANEL_WIDTH = 420;
            const HEADER_HEIGHT = 44;
            const rawPosition = reactFlowScreenToFlowPosition({
              x: event.clientX,
              y: event.clientY,
            });
            const position = {
              x: rawPosition.x - (PANEL_WIDTH / 2),
              y: rawPosition.y - (HEADER_HEIGHT / 2)
            };
            const newNode = {
              id: uniqueNodeId('imagePanel'),
              type: 'imagePanel',
              position,
              width: 420,
              height: 300,
              data: {
                imageUrl: dragData.fileUrl,
                fileName: dragData.fileName,
                label: `Image: ${dragData.fileName}`,
                fileId: dragData.fileId,
                fileType: dragData.fileType
              }
            };
            addNodeWithAnimation(newNode);
          };
          img.src = dragData.fileUrl;
        }
        // Handle document file drop
        else if (dragData.isDocument && dragData.fileId) {
          console.log('[CreativeNodeFlow] Document drop - dragData:', dragData);

          const rawPosition = reactFlowScreenToFlowPosition({
            x: event.clientX,
            y: event.clientY,
          });

          const PANEL_WIDTH = 480;
          const HEADER_HEIGHT = 44;

          const position = {
            x: rawPosition.x - (PANEL_WIDTH / 2),
            y: rawPosition.y - (HEADER_HEIGHT / 2)
          };

          const documentContent = dragData.content?.markdown || dragData.content?.displayContent || dragData.content || '';

          const pages = [{
            content: documentContent,
            context: dragData.context || null,
            type: 'text' as const,
            timestamp: new Date().toISOString()
          }];

          const newNode = {
            id: `documentPanel-${Date.now()}`,
            type: 'documentPanel',
            position,
            width: 480,
            height: 600,
            data: {
              content: documentContent,
              fileName: dragData.fileName,
              label: `Document: ${dragData.fileName}`,
              fileId: dragData.fileId,
              fileType: dragData.fileType,
              pages,
              currentPageIndex: 0
            }
          };
          addNodeWithAnimation(newNode);
        }
        // Handle video file drop
        else if (dragData.isVideo && dragData.fileUrl) {
          const rawPosition = reactFlowScreenToFlowPosition({
            x: event.clientX,
            y: event.clientY,
          });

          const outputDims = getOutputNodeDimensions('video');
          const position = {
            x: rawPosition.x - (outputDims.width / 2),
            y: rawPosition.y - 22
          };

          const initialPage = {
            content: dragData.fileUrl,
            context: null,
            type: 'video' as const,
            timestamp: new Date().toISOString()
          };

          const newNode = {
            id: `customOutput-${Date.now()}`,
            type: 'universalOutput',
            position,
            width: outputDims.width,
            height: outputDims.height,
            data: {
              output: dragData.fileUrl,
              content: dragData.fileUrl,
              type: 'video',
              outputsMediaType: 'video',
              fileName: dragData.fileName,
              fileId: dragData.fileId,
              fileType: dragData.fileType,
              pages: [initialPage],
              currentPageIndex: 0
            }
          };
          addNodeWithAnimation(newNode);
        }
        return;
      }

      // Check if it's a file drop from the file system (not from FilePanel)
      if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
        const files = Array.from(event.dataTransfer.files);
        for (const file of files) {
          try {
            // Calculate position first
            const flowPosition = reactFlowScreenToFlowPosition({
              x: event.clientX,
              y: event.clientY,
            });
            
            // Handle .knod and .zip file imports
            const fileName = file.name.toLowerCase();
            if (fileName.endsWith('.knod') || fileName.endsWith('.zip') || (fileName.endsWith('.json') && file.type === 'application/json')) {
              try {
                const result = await knodExportService.importFromFile(file);
                
                // Update nodes with proper handlers
                const updatedNodes = result.nodes.map(node => {
                  const handlers = createNodeHandlers(node.id);
                  return {
                    ...node,
                    data: {
                      ...node.data,
                      ...handlers,
                    },
                  };
                });

                setNodes(updatedNodes);
                setEdges(result.edges);
                flowHistory.clear(); // a different graph: old steps must not apply to it
                saveSessionsRef.current?.replaceContent(); // ...and its saves must not write this one
                setCurrentFlowName(result.flowName);
                currentFlowNameRef.current = result.flowName;
                
                // Reset flow ID since this is a new import
                setCurrentFlowId(null);
                
                // Apply viewport if available
                if (result.viewport) {
                  setViewport(result.viewport);
                  // Note: duration animation is handled by ReactFlow internally
                }

                // If there are media files, show a message about them
                if (result.mediaFiles && result.mediaFiles.size > 0) {
                  toastService.success(`Knod imported with ${result.mediaFiles.size} media files`);
                } else {
                  toastService.success('Knod imported successfully');
                }
              } catch (error) {
                logger.error('[CreativeNodeFlow] Knod import from drop failed:', error);
                toastService.error('Failed to import Knod');
              }
              continue;
            }
            
            // For images: create node immediately with local URL for instant feedback
            if (file.type.startsWith('image/')) {
              const localUrl = URL.createObjectURL(file);
              const nodeId = uniqueNodeId('imagePanel');
              const PANEL_WIDTH = 554;
              const HEADER_HEIGHT = 44;
              
              // Create node immediately with loading state
              const img = new Image();
              img.onload = async () => {
                const position = {
                  x: flowPosition.x - (PANEL_WIDTH / 2),
                  y: flowPosition.y - (HEADER_HEIGHT / 2)
                };
                const aspectRatio = img.naturalWidth / img.naturalHeight;
                const nodeHeight = Math.round(PANEL_WIDTH / aspectRatio) + 100;
                
                // Create node with local URL immediately
                const newNode = {
                  id: nodeId,
                  type: 'imagePanel',
                  position,
                  width: PANEL_WIDTH,
                  height: nodeHeight,
                  data: {
                    imageUrl: localUrl,
                    fileName: file.name,
                    label: `Image: ${file.name}`,
                    preloadedWidth: img.naturalWidth,
                    preloadedHeight: img.naturalHeight,
                    isUploading: true, // Loading indicator
                  }
                };
                addNodeWithAnimation(newNode);
                
                // Upload in background
                try {
                  const uploadOptions: Record<string, any> = {
                    scope: 'flow',
                    flowId: currentFlowId || undefined,
                    folder: 'dropped-files'
                  };
                  const fileInfo = await fileStorageService.uploadFile(file, uploadOptions);
                  
                  // Update node with R2 URL
                  setNodes(nodes => nodes.map(n => 
                    n.id === nodeId ? {
                      ...n,
                      data: {
                        ...n.data,
                        imageUrl: fileInfo.url,
                        fileId: fileInfo.id,
                        fileType: file.type,
                        isUploading: false,
                      }
                    } : n
                  ));
                   // The node was marked dirty when it was added, but that save may already
                   // have run with the blob: URL — dead on reload. Mark again.
                   markFlowDirty();
                  
                  // Clean up local URL
                  URL.revokeObjectURL(localUrl);
                } catch (uploadError) {
                  console.error('[CreativeNodeFlow] Upload failed, keeping local URL:', uploadError);
                  // Keep local URL but mark as not uploading
                  setNodes(nodes => nodes.map(n => 
                    n.id === nodeId ? {
                      ...n,
                      data: { ...n.data, isUploading: false, uploadFailed: true }
                    } : n
                  ));
                   // direct setNodes bypasses onNodesChange
                   markFlowDirty();
                  alertService.error(friendlyErrors.uploadFailed);
                }
              };
              img.src = localUrl;
              continue;
            }
            
            // Handle video drop: create node immediately with local URL, upload in background
            if (file.type.startsWith('video/')) {
              console.log('[CreativeNodeFlow] Uploading dropped video:', file.name, file.type);
              
              // Create local URL for immediate preview
              const localUrl = URL.createObjectURL(file);
              const nodeId = `output-${Date.now()}`;
              const outputDims = getOutputNodeDimensions('video');
              const HEADER_HEIGHT = 44;
              const position = {
                x: flowPosition.x - (outputDims.width / 2),
                y: flowPosition.y - (HEADER_HEIGHT / 2)
              };

              // Create node immediately with loading state
              const newNode = {
                id: nodeId,
                type: 'universalOutput',
                position,
                width: outputDims.width,
                height: outputDims.height,
                data: {
                  pages: [{ type: 'video' as const, content: localUrl, timestamp: new Date().toISOString() }],
                  currentPageIndex: 0,
                  output: localUrl,
                  videoUrl: localUrl,
                  type: 'video',
                  outputsMediaType: 'video',
                  fileName: file.name,
                  isUploading: true, // Show loading indicator
                }
              };
              console.log('[CreativeNodeFlow] Adding video output node with local URL:', newNode);
              addNodeWithAnimation(newNode);
              toastService.info(`Uploading "${file.name}"...`);
              
              // Upload in background
              const uploadOptions: Record<string, any> = {
                scope: 'flow',
                flowId: currentFlowId || undefined,
                folder: 'dropped-files'
              };
              
              fileStorageService.uploadFile(file, uploadOptions)
                .then(fileInfo => {
                  console.log('[CreativeNodeFlow] Video uploaded successfully:', fileInfo);
                  // Update node with R2 URL
                  setNodes(nodes => nodes.map(n => 
                    n.id === nodeId ? {
                      ...n,
                      data: {
                        ...n.data,
                        pages: [{ type: 'video' as const, content: fileInfo.url, timestamp: new Date().toISOString() }],
                        output: fileInfo.url,
                        videoUrl: fileInfo.url,
                        fileId: fileInfo.id,
                        isUploading: false,
                      }
                    } : n
                  ));
                  // Persist the R2 URL, not the blob: URL the earlier save captured.
                  markFlowDirty();
                  // Clean up local URL
                  URL.revokeObjectURL(localUrl);
                  toastService.success(`Video "${file.name}" uploaded`);
                })
                .catch(uploadError => {
                  console.error('[CreativeNodeFlow] Video upload failed:', uploadError);
                  // Keep local URL but mark as failed
                  setNodes(nodes => nodes.map(n => 
                    n.id === nodeId ? {
                      ...n,
                      data: { ...n.data, isUploading: false, uploadFailed: true }
                    } : n
                  ));
                  // direct setNodes bypasses onNodesChange
                  markFlowDirty();
                  toastService.error(`Failed to upload "${file.name}"`);
                });
              continue;
            }
            
            // For text files: show toast and upload first
            if (file.type.startsWith('text/') || file.name.endsWith('.txt')) {
              console.log('[CreativeNodeFlow] Uploading dropped text file:', file.name, file.type);
              alertService.info(`Uploading ${file.name}...`);
              
              // Upload file to flow assets
              const uploadOptions: Record<string, any> = {
                scope: 'flow',
                flowId: currentFlowId || undefined,
                folder: 'dropped-files'
              };
              
              const fileInfo = await fileStorageService.uploadFile(file, uploadOptions);
              console.log('[CreativeNodeFlow] Text file uploaded successfully:', fileInfo);
              // Read text file and create Text Container node
              const text = await file.text();
              const PANEL_WIDTH = 480;
              const HEADER_HEIGHT = 44;
              const position = {
                x: flowPosition.x - (PANEL_WIDTH / 2),
                y: flowPosition.y - (HEADER_HEIGHT / 2)
              };
              
              const newNode = {
                id: `documentPanel-${Date.now()}`,
                type: 'documentPanel',
                position,
                width: 480,
                height: 600,
                data: {
                  content: text,
                  fileName: file.name,
                  fileId: fileInfo.id,
                  fileType: file.type,
                  pages: [{
                    content: text,
                    context: null,
                    type: 'text' as const,
                    timestamp: new Date().toISOString()
                  }],
                  currentPageIndex: 0
                }
              };
              addNodeWithAnimation(newNode);
            }
          } catch (error) {
            console.error('[CreativeNodeFlow] Error uploading dropped file:', error);
            alertService.error(`Failed to upload ${file.name}`);
          }
        }
        return;
      }
      
      // No more handlers needed — FilePanel JSON drops are handled above
    } catch (error) {
      console.error('[CreativeNodeFlow] Error handling drop:', error);
    }
  }, [reactFlowScreenToFlowPosition, addNodeWithAnimation, currentFlowId, setNodes, getHighestZIndex, flowHistory]);

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    // Accept both 'move' (from node palette) and 'copy' (from file panel)
    const types = event.dataTransfer.types;
    if (types.includes('application/reactflow')) {
      event.dataTransfer.dropEffect = 'move';
    } else if (types.includes('application/json')) {
      event.dataTransfer.dropEffect = 'copy';
    }
  }, []);

  // Track mouse position for paste-at-cursor functionality
  const handleMouseMove = useCallback((event: React.MouseEvent) => {
    mousePositionRef.current = {
      x: event.clientX,
      y: event.clientY
    };
  }, []);

  const handleMouseLeave = useCallback(() => {
    mousePositionRef.current = null;
  }, []);

  return (
    <AutoSaveProvider
      onMarkDirty={autoSave.markDirty}
      isDirty={autoSave.isDirty}
      status={autoSave.status}
    >
    <div 
      ref={reactFlowWrapperRef} 
      style={{ width: '100vw', height: '100vh' }}
      onContextMenuCapture={handleCanvasContextMenuCapture}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <ReactFlow
        nodes={animatedNodes}
        edges={edges}
        onlyRenderVisibleElements
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onConnectEnd={onConnectEnd}
        onEdgesDelete={onEdgesDelete}
        onPaneContextMenu={handlePaneContextMenu}
        onNodeContextMenu={handleNodeContextMenu}
        onPaneClick={closeAllMenus}
        onNodeClick={closeAllMenus}
        onNodeDoubleClick={handleNodeDoubleClick}
        onNodeDragStart={handleNodeDragStart}
        onNodeDragStop={handleNodeDragStop}
        onSelectionStart={closeAllMenus}
        onMoveStart={closeAllMenus}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        isValidConnection={isValidConnection}
        defaultEdgeOptions={{
          interactionWidth: isMobile ? 40 : 20,
        }}
        connectionRadius={isMobile ? 60 : 40}
        connectionLineComponent={SnappingConnectionLine}
        colorMode="dark"
        deleteKeyCode="Delete"
        selectionOnDrag={!isMobile}
        selectionMode={SelectionMode.Partial}
        selectionKeyCode={isMobile ? null : "Shift"}
        multiSelectionKeyCode={isMobile ? null : "Shift"}
        snapToGrid={snapToGridEnabled}
        snapGrid={snapGrid}
        minZoom={0.1}
        maxZoom={4}
        // REMOVED fitView - it was causing nodes to jump after creation
        // The viewport was being auto-adjusted every time nodes changed
        // fitView
        // fitViewOptions={{
        //   padding: 0.3,
        //   includeHiddenNodes: false,
        //   maxZoom: 1.0,
        //   minZoom: 1.0
        // }}
        onError={(id, message) => {
          // Suppress ResizeObserver errors
          if (message && message.includes('ResizeObserver')) {
            return;
          }
          logger.error('ReactFlow error:', id, message);
        }}
      >
<Panel position="bottom-right" className={`minimap-panel-wrapper${isMobile && minimapExpanded ? ' minimap-expanded' : ''}`}>
          <MiniMap
            pannable={true}
            zoomable={true}
            nodeColor={(node) => {
              const registration = getNodeByReactFlowType(node.type || '');
              const category = registration?.category;
              const nodeData = node.data as Record<string, unknown>;

              if (category === 'utility') {
                const pages = nodeData.pages as Array<{ type?: string; imageUrl?: string; videoUrl?: string; audioUrl?: string; text?: string }> | undefined;
                const imageUrl = nodeData.imageUrl as string | undefined;
                const videoUrl = nodeData.videoUrl as string | undefined;
                const audioUrl = nodeData.audioUrl as string | undefined;

                if (pages && pages.length > 0) {
                  const firstPage = pages[0];
                  if (firstPage.videoUrl) return '#FF4B42';
                  if (firstPage.imageUrl) return '#42C3D7';
                  if (firstPage.audioUrl) return '#10B981';
                  if (firstPage.text) return '#FED402';
                  if (firstPage.type === 'video') return '#FF4B42';
                  if (firstPage.type === 'image') return '#42C3D7';
                  if (firstPage.type === 'audio') return '#10B981';
                  if (firstPage.type === 'text') return '#FED402';
                }

                if (videoUrl) return '#FF4B42';
                if (imageUrl) return '#42C3D7';
                if (audioUrl) return '#10B981';
                return '#4A5568';
              }

              switch (category) {
                case 'text': return '#FED402';
                case 'image': return '#42C3D7';
                case 'video': return '#FF4B42';
                case 'audio': return '#10B981';
                default: return '#4A5568';
              }
            }}
          />

          {/* Mobile: control strip */}
          {isMobile && (
            <div className="minimap-control-strip">
              <button className="minimap-strip-btn" onClick={handleAutoArrange} title="Auto-arrange nodes">
                <AccountTreeIcon style={{ fontSize: '14px' }} />
                Arrange
              </button>
              <button className="minimap-strip-btn" onClick={() => fitView({ padding: 0.2, duration: 300 })} title="Fit view">
                <CenterFocusStrongIcon style={{ fontSize: '14px' }} />
                Fit
              </button>
              <button className="minimap-strip-btn minimap-toggle-btn" onClick={() => setMinimapExpanded(prev => !prev)} title={minimapExpanded ? 'Collapse minimap' : 'Expand minimap'}>
                {minimapExpanded ? <ExpandMoreIcon style={{ fontSize: '18px' }} /> : <ExpandLessIcon style={{ fontSize: '18px' }} />}
              </button>
            </div>
          )}

          {/* Desktop: absolute-positioned buttons */}
          {!isMobile && (
            <>
              <div className="minimap-auto-arrange-button" onClick={handleAutoArrange} title="Auto-arrange nodes">
                <AccountTreeIcon style={{ fontSize: '14px' }} />
              </div>
              <div className="minimap-reset-zoom-button" onClick={resetZoomToOneCentered} title="Reset zoom to 100% (Press 1)">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <text x="12" y="15" textAnchor="middle" fontSize="8" fontWeight="bold" fill="currentColor">100%</text>
                </svg>
              </div>
              <div className="minimap-fit-view-button" onClick={() => fitView({ padding: 0.2, duration: 300 })} title="Fit view to all nodes (Press Shift+1)">
                <svg width="14" height="14" viewBox="0 0 32 30" fill="currentColor">
                  <path d="M3.692 4.63c0-.53.4-.938.939-.938h5.215V0H4.708C2.13 0 0 2.054 0 4.63v5.216h3.692V4.631zM27.354 0h-5.2v3.692h5.17c.53 0 .984.4.984.939v5.215H32V4.631A4.624 4.624 0 0027.354 0zm.954 24.83c0 .532-.4.94-.939.94h-5.215v3.768h5.215c2.577 0 4.631-2.13 4.631-4.707v-5.139h-3.692v5.139zm-23.677.94c-.531 0-.939-.4-.939-.94v-5.138H0v5.139c0 2.577 2.13 4.707 4.708 4.707h5.138V25.77H4.631z" />
                </svg>
              </div>
            </>
          )}
        </Panel>
        
        {/* When snapping is on, the visible dot grid matches the snap grid so nodes
            visibly land on it (a mismatched gap makes snapping feel broken). */}
        <Background
          gap={snapToGridEnabled ? snapGridSize : 16}
          size={1}
          color={snapToGridEnabled ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.08)'}
        />

        {/* Execution Progress Overlay */}
        <ExecutionProgress />

        {/* NEW 3-BAR FLOATING TOOLBAR SYSTEM */}
        
        {/* Left Bar: Flow name + save status */}
        <FloatingToolbarLeft
          flowName={currentFlowName}
          onFlowNameChange={(newName) => {
            setCurrentFlowName(newName);
            currentFlowNameRef.current = newName;
            autoSave.triggerSave(true);
          }}
          autoSaveStatus={autoSave.status}
          lastSaved={autoSave.lastSaved}
          isDirty={autoSave.isDirty}
          leftPanelWidth={isMobile ? 0 : 96} // Width of node palette sidebar (0 on mobile: palette is at bottom)
          onExportWithMedia={handleExportWithMedia}
          onExportStructure={handleExportStructure}
          onImport={handleImportKnod}
          onPublish={() => setPublishModalOpen(true)}
          canPublish={!!currentFlowId}
          onBeforeNavigateHome={autoSave.triggerSave}
        />

        {/* Center Bar: Run button (idle) OR Selection toolbar (when nodes selected) */}
        <FloatingToolbarCenter
          onRunFlow={runFullFlow}
          totalFlowCredits={totalFlowCredits}
          isMobile={isMobile}
          onCopyNode={handleCopySelectedNode}
          onDeselectAll={handleDeselectAll}
          selectedEdgeCount={selectedEdgeCount}
        />

        {/* Right Bar: Credits + User Menu */}
        <FloatingToolbarRight
          onSaveAsTemplate={() => {
            setSaveDialogAsTemplate(true);
            setSaveDialogOpen(true);
          }}
          onUpdateTemplate={handleUpdateTemplate}
          hasLoadedTemplate={!!currentTemplateId}
        />

        {/* Node creation panel - hidden, will be replaced with right-click context menu */}
      </ReactFlow>

      {/* Node Palette - Left Panel with embedded Assets panel (includes upload zone) */}
      <NodePalette
        ref={nodePaletteRef}
        onNodeSelect={handleNodePaletteSelect}
        onNodeDragStart={handleNodePaletteDragStart}
        onBeforeNavigateHome={autoSave.triggerSave}
        assetsPanel={
          <FilePanel
            onFileContext={handleFileContext}
            onAddToStage={handleAddFileToStage}
            onAddManyToStage={handleAddFilesToStage}
            onDeleteAsset={handleCheckAssetUsage}
            onRemoveNodesFromStage={handleRemoveNodesFromStage}
            onRemoveAssetsFromStage={handleRemoveAssetsFromStage}
            onSetFlowThumbnail={(thumbnailUrl) => {
              setCurrentFlowThumbnail(thumbnailUrl);
              toastService.success('Knod thumbnail set');
              // Trigger a save to persist the thumbnail
              autoSave.triggerSave();
            }}
            isVisible={true}
            currentFlowId={currentFlowId}
            embedded
          />
        }
        onIrisOpen={() => { setIrisOpen(true); setIrisMinimized(false); }}
      />

      {/* FilePanel Toggle Button - Hidden, using internal collapse button instead */}
      {/* <button
        style={{
          position: 'fixed',
          top: '20px',
          right: filePanelVisible ? '380px' : '20px',
          width: '40px',
          height: '40px',
          background: 'var(--node-body-background)',
          border: '1px solid var(--node-border-color)',
          borderRadius: '8px',
          color: 'var(--color-text-primary)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '18px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
          zIndex: 1001,
          transition: 'all 0.3s ease'
        }}
        onClick={() => setFilePanelVisible(!filePanelVisible)}
        title={filePanelVisible ? 'Hide Files Panel' : 'Show Files Panel'}
        onMouseEnter={(e) => {
          e.target.style.background = 'var(--color-accent-primary-alpha)';
          e.target.style.borderColor = 'var(--color-accent-primary)';
        }}
        onMouseLeave={(e) => {
          e.target.style.background = 'var(--node-body-background)';
          e.target.style.borderColor = 'var(--node-border-color)';
        }}
      >
  {filePanelVisible ? <FolderIcon fontSize="small" /> : <FolderOpenOutlinedIcon fontSize="small" />}
      </button> */}

      {/* Right-click Context Menu */}
      {contextMenu && (
        <div
          className="context-menu context-menu--canvas"
          ref={(el) => {
            if (el) {
              const rect = el.getBoundingClientRect();
              const padding = 8;
              let newX = contextMenu.x;
              let newY = contextMenu.y;

              if (rect.right > window.innerWidth - padding) {
                newX = window.innerWidth - rect.width - padding;
              }
              if (rect.bottom > window.innerHeight - padding) {
                newY = window.innerHeight - rect.height - padding;
              }
              if (newX < padding) newX = padding;
              if (newY < padding) newY = padding;

              if (newX !== contextMenu.x || newY !== contextMenu.y) {
                el.style.left = `${newX}px`;
                el.style.top = `${newY}px`;
              }
            }
          }}
          style={{ top: contextMenu.y, left: contextMenu.x, position: 'fixed' }}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          {contextMenu.mode === 'multi-select' ? (
            <div className="context-menu__wrapper">
              <div className="context-menu__main">
                <button
                  className="context-menu__item context-menu__item--main"
                  onClick={handleConnectSelectedNodes}
                >
                  <span className="context-menu__label">Connect nodes</span>
                </button>
                <button
                  className="context-menu__item context-menu__item--main"
                  onClick={handleDisconnectSelectedNodes}
                >
                  <span className="context-menu__label">Disconnect all</span>
                </button>
              </div>
            </div>
          ) : contextMenu.mode === 'node' ? (() => {
            const targetNode = nodes.find(n => n.id === contextMenu.nodeId);
            if (!targetNode) return null;
            const nodeReg = getNodeByReactFlowType(targetNode.type || '');
            if (!nodeReg) return null;
            const category = nodeReg.category as 'text' | 'image' | 'video' | 'audio';
            const getNodeLabel = (r: NodeRegistration) => r.definition?.title || r.type;

            const SWITCH_CATEGORY_META: Record<'text' | 'image' | 'video' | 'audio', { color: string; icon: string }> = {
              text: { color: '#B38E00', icon: '/icons/text-figma.svg' },
              image: { color: '#00849C', icon: '/icons/image-figma.svg' },
              video: { color: '#C22C22', icon: '/icons/video-figma.svg' },
              audio: { color: '#0E9F6E', icon: '/icons/audio-figma.svg' },
            };
            const meta = SWITCH_CATEGORY_META[category];

            const alternatives = Object.values(NODE_REGISTRY)
              .filter(n =>
                n.category === category &&
                !n.hidden &&
                n.type !== nodeReg.type &&
                n.type !== 'WanAnimateVideo'
              )
              .sort((a, b) => getNodeLabel(a).localeCompare(getNodeLabel(b)));

            return (
              <div className="context-menu__wrapper">
                <div className="context-menu__main">
                  <button
                    className={clsx('context-menu__item', 'context-menu__item--main', 'has-submenu', {
                      'is-active': activeSubmenu === 'switch',
                    })}
                    onMouseEnter={() => setActiveSubmenu('switch')}
                    onFocus={() => setActiveSubmenu('switch')}
                  >
                    <span className="context-menu__label">Switch to</span>
                    <span className="context-menu__chevron">›</span>
                  </button>
                  <div className="context-menu__divider" />
                  <button
                    className="context-menu__item context-menu__item--main context-menu__item--danger"
                    onClick={() => {
                      const nodeId = contextMenu.nodeId!;
                      closeContextMenu();
                      recordUserAction('Delete node', { hold: true, coalesceKey: `ctx-delete:${nodeId}` });
                      setDeletingNodeIds(prev => new Set([...prev, nodeId]));
                      window.setTimeout(() => {
                        setNodes(prev => prev.filter(n => n.id !== nodeId));
                        setEdges(prev => prev.filter(e => e.source !== nodeId && e.target !== nodeId));
                        markFlowDirty(); // direct setNodes/setEdges bypass on*Change
                        releaseUserAction(`ctx-delete:${nodeId}`);
                        setDeletingNodeIds(prev => {
                          const next = new Set(prev);
                          next.delete(nodeId);
                          return next;
                        });
                      }, 200);
                    }}
                  >
                    <span className="context-menu__label">Delete</span>
                  </button>
                </div>
                {activeSubmenu === 'switch' && (
                  <div
                    className={clsx('context-menu__submenu', 'is-visible')}
                    onMouseEnter={() => setActiveSubmenu('switch')}
                    onMouseLeave={() => setActiveSubmenu(null)}
                  >
                    <div className="context-menu__submenu-items">
                      {alternatives.map(node => {
                        const logoPath = node.logoPath;
                        return (
                          <button
                            key={node.type}
                            className={clsx('context-menu__item', 'context-menu__item--submenu', `type-${category}`)}
                            onClick={() => switchNodeType(contextMenu.nodeId!, node.type)}
                          >
                            <span className="context-menu__item-icon" aria-hidden="true">
                              {logoPath ? (
                                <img src={logoPath} alt={getNodeLabel(node)} />
                              ) : (
                                <img src={meta.icon} alt={category} />
                              )}
                            </span>
                            <span
                              className="context-menu__label context-menu__label--accent"
                              style={{ color: meta.color }}
                            >
                              {getNodeLabel(node)}
                            </span>
                          </button>
                        );
                      })}
                      {alternatives.length === 0 && (
                        <div className="context-menu__empty">No alternatives available</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })() : (() => {
            const getNodeLabel = (reg: NodeRegistration) => reg.definition?.title || reg.type;

            // Determine handle media type for filtering when triggered from a handle
            const handleMediaType = handleContext ? (() => {
              const node = nodes.find(n => n.id === handleContext.nodeId);
              if (!node) return null;
              return getConnectorType(node, handleContext.handleId, handleContext.isSource ? 'source' : 'target');
            })() : null;

            // Filter nodes by compatibility with the handle's media type
            const isCompatible = (reg: NodeRegistration): boolean => {
              if (!handleMediaType || !handleContext) return true;
              const def = reg.definition;
              if (!def) return true;
              if (handleContext.isSource) {
                // Source handle clicked: show nodes that ACCEPT this output type
                const accepts = def.acceptsMediaTypes || [];
                return accepts.includes(handleMediaType) || accepts.includes('any') || handleMediaType === 'any';
              } else {
                // Target handle clicked: show nodes that OUTPUT what this input expects
                const outputs = def.outputsMediaType;
                return outputs === handleMediaType || outputs === 'any' || handleMediaType === 'any';
              }
            };

            // Include container/utility nodes when triggered from a handle
            const allCompatibleNodes = Object.values(NODE_REGISTRY)
              .filter((n) => !n.hidden && isCompatible(n));

            const textNodes = allCompatibleNodes
              .filter((n) => n.category === 'text')
              .sort((a, b) => getNodeLabel(a).localeCompare(getNodeLabel(b)));
            const imageNodes = allCompatibleNodes
              .filter((n) => n.category === 'image')
              .sort((a, b) => getNodeLabel(a).localeCompare(getNodeLabel(b)));
            const videoNodes = allCompatibleNodes
              .filter((n) => n.category === 'video')
              .sort((a, b) => getNodeLabel(a).localeCompare(getNodeLabel(b)));
            const audioNodes = allCompatibleNodes
              .filter((n) => n.category === 'audio')
              .sort((a, b) => getNodeLabel(a).localeCompare(getNodeLabel(b)));
            // Utility nodes (ImagePanel, DocumentPanel, Output) shown when from handle:
            // - Source handle (output): show containers with input handles (Output)
            // - Target handle (input): show containers with output handles (ImagePanel, DocumentPanel)
            // On a plain canvas right-click (no handle context), show all utility nodes.
            const utilityNodes = handleContext
              ? allCompatibleNodes.filter((n) => {
                  if (n.category !== 'utility') return false;
                  if (handleContext.isSource) {
                    return n.definition?.hasInputHandle !== false;
                  } else {
                    return n.definition?.hasOutputHandle !== false;
                  }
                }).sort((a, b) => getNodeLabel(a).localeCompare(getNodeLabel(b)))
              : allCompatibleNodes
                  .filter((n) => n.category === 'utility')
                  .sort((a, b) => getNodeLabel(a).localeCompare(getNodeLabel(b)));

            const imageModels = imageNodes.map((node) => ({
              key: node.camelCaseType,
              label: getNodeLabel(node),
              nodeType: node.camelCaseType,
            }));

            const videoModels = videoNodes.map((node) => ({
              key: node.camelCaseType,
              label: getNodeLabel(node),
              nodeType: node.camelCaseType,
            }));

            const audioModels = audioNodes.map((node) => ({
              key: node.camelCaseType,
              label: getNodeLabel(node),
              nodeType: node.camelCaseType,
            }));

            const textModels = textNodes.map((node) => ({
              key: node.camelCaseType,
              label: getNodeLabel(node),
              nodeType: node.camelCaseType,
            }));

            const utilityModels = utilityNodes.map((node) => ({
              key: node.camelCaseType,
              label: getNodeLabel(node),
              nodeType: node.camelCaseType,
            }));

            const mainMenuItems = [
              ...(textModels.length > 0 ? [{ key: 'text', label: 'Text', submenu: 'text' as SubmenuKey, type: 'text' as const }] : []),
              ...(imageModels.length > 0 ? [{ key: 'image', label: 'Image', submenu: 'image' as SubmenuKey, type: 'image' as const }] : []),
              ...(videoModels.length > 0 ? [{ key: 'video', label: 'Video', submenu: 'video' as SubmenuKey, type: 'video' as const }] : []),
              ...(audioModels.length > 0 ? [{ key: 'audio', label: 'Audio', submenu: 'audio' as SubmenuKey, type: 'audio' as const }] : []),
              ...(utilityModels.length > 0 ? [{ key: 'utility', label: 'Utility', submenu: 'utility' as SubmenuKey, type: 'utility' as const }] : []),
              ...(!handleContext ? [
                { key: 'divider-1', divider: true },
                { key: 'assets', label: 'Assets', onSelect: () => openNodePaletteCategory('assets') },
                { key: 'help', label: 'Help', onSelect: () => openNodePaletteCategory('help') },
              ] : []),
            ];

            const renderMainItem = (item: typeof mainMenuItems[number]) => {
              if (item.divider) {
                return <div key={item.key} className="context-menu__divider" />;
              }

              const hasSubmenu = Boolean(item.submenu);
              const isDisabled = Boolean((item as any).disabled);
              const isActive = activeSubmenu === item.submenu;
              const isTypeItem = item.type && (item.type === 'text' || item.type === 'image' || item.type === 'video' || item.type === 'audio' || item.type === 'utility');
              const meta = isTypeItem ? CATEGORY_META[item.type] : null;

              return (
                <button
                  key={item.key}
                  className={clsx('context-menu__item', 'context-menu__item--main', {
                    'is-disabled': isDisabled,
                    'has-submenu': hasSubmenu,
                    'is-active': isActive,
                    [`type-${item.type}`]: isTypeItem,
                  })}
                  onMouseEnter={() => item.submenu && setActiveSubmenu(item.submenu)}
                  onFocus={() => item.submenu && setActiveSubmenu(item.submenu)}
                  onClick={() => {
                    if (isDisabled) return;
                    if (item.onSelect) {
                      item.onSelect();
                      setActiveSubmenu(null);
                      closeContextMenu();
                      return;
                    }
                    if (!item.submenu) {
                      closeContextMenu();
                    }
                  }}
                >
                  {meta && (
                    <span className="context-menu__item-icon context-menu__item-icon--main" aria-hidden="true">
                      <img src={meta.icon} alt={meta.label} />
                    </span>
                  )}
                  <span className="context-menu__label" style={meta ? { color: meta.color } : undefined}>{item.label}</span>
                  {hasSubmenu && <span className="context-menu__chevron">›</span>}
                </button>
              );
            };

            const CATEGORY_META: Record<'text' | 'image' | 'video' | 'audio' | 'utility', { color: string; icon: string; label: string }> = {
              text: { color: '#B38E00', icon: '/icons/text-figma.svg', label: 'Text' },
              image: { color: '#00849C', icon: '/icons/image-figma.svg', label: 'Image' },
              video: { color: '#C22C22', icon: '/icons/video-figma.svg', label: 'Video' },
              audio: { color: '#0E9F6E', icon: '/icons/audio-figma.svg', label: 'Audio' },
              utility: { color: '#9CA3AF', icon: '/icons/image-figma.svg', label: 'Utility' },
            };

            const renderSubmenu = (
              key: 'text' | 'image' | 'video' | 'audio' | 'utility',
              items: { key: string; label: string; nodeType: string }[],
            ) => {
              const meta = CATEGORY_META[key];

              return (
                <div
                  key={key}
                  className={clsx('context-menu__submenu', { 'is-visible': activeSubmenu === key })}
                  onMouseEnter={() => setActiveSubmenu(key)}
                  onMouseLeave={() => setActiveSubmenu(null)}
                >
                  <div className="context-menu__submenu-items">
                    {items.map((item) => {
                      const node = getNodeByReactFlowType(item.nodeType);
                      const logoPath = node?.logoPath;

                      return (
                        <button
                          key={item.key}
                          className={clsx('context-menu__item', 'context-menu__item--submenu', `type-${key}`)}
                          onClick={() => {
                            createNodeFromMenu(item.nodeType as CustomNodeType);
                            closeContextMenu();
                          }}
                        >
                          <span
                            className="context-menu__item-icon"
                            aria-hidden="true"
                          >
                            {logoPath ? (
                              <img src={logoPath} alt={item.label} />
                            ) : (
                              <img src={meta.icon} alt={meta.label} />
                            )}
                          </span>
                          <span
                            className="context-menu__label context-menu__label--accent"
                            style={{ color: meta.color }}
                          >
                            {item.label}
                          </span>
                        </button>
                      );
                    })}
                    {items.length === 0 && (
                      <div className="context-menu__empty">No models available</div>
                    )}
                  </div>
                </div>
              );
            };

            return (
              <div className="context-menu__wrapper">
                <div className="context-menu__main">
                  {mainMenuItems.map(renderMainItem)}
                </div>
                {activeSubmenu === 'text' && renderSubmenu('text', textModels)}
                {activeSubmenu === 'image' && renderSubmenu('image', imageModels)}
                {activeSubmenu === 'video' && renderSubmenu('video', videoModels)}
                {activeSubmenu === 'audio' && renderSubmenu('audio', audioModels)}
                {activeSubmenu === 'utility' && renderSubmenu('utility', utilityModels)}
              </div>
            );
          })()}
        </div>
      )}

      {/* Save Flow Dialog */}
      <SaveFlowDialog
        open={saveDialogOpen}
        onClose={() => {
          setSaveDialogOpen(false);
          setSaveDialogAsTemplate(false);
        }}
        onSave={handleSaveFlow}
        currentFlowName={currentFlowName}
        currentFlowDescription={currentFlowDescription}
        currentFlowTags={currentFlowTags}
        isTemplate={saveDialogAsTemplate}
      />

      {/* Publish Modal — turn this Knod into a public form */}
      <PublishModal
        open={publishModalOpen}
        onClose={() => setPublishModalOpen(false)}
        flowId={currentFlowId}
        flowName={currentFlowName}
        inspect={inspectForPublish}
        onAutoAdd={handleAutoAddPublishIO}
        onUpdateLabel={handleUpdatePublishLabel}
        onSaveBeforePublish={handleQuickSave}
      />

      {/* Load Flow Dialog */}
      <LoadFlowDialog
        open={loadDialogOpen}
        onClose={() => setLoadDialogOpen(false)}
        onLoadFlow={handleLoadFlow}
        onLoadTemplate={handleLoadTemplate}
        onRunTemplate={handleRunTemplate}
        currentFlowId={currentFlowId}
        onCurrentFlowDeleted={() => {
          // The currently open flow was deleted - start a new flow
          setNodes([]);
          setEdges(initialEdges);
          setCurrentFlowId(null);
          flowHistory.clear();
          saveSessionsRef.current?.replaceContent();
          setCurrentFlowName('');
          setCurrentFlowDescription('');
          setCurrentFlowTags([]);
          flowStorageService.clearCurrentFlowId();
          toastService.info('Current knod was deleted. Starting new knod.');
        }}
      />

      {/* Custom Node Builder Modal */}
      <CustomNodeBuilder
        isOpen={customNodeBuilderOpen}
        onClose={() => {
          setCustomNodeBuilderOpen(false);
          setEditingCustomNode(undefined);
        }}
        onSave={handleCustomNodeBuilderSave}
        editingNode={editingCustomNode}
      />

      {/* Brand Voice Manager */}
      <BrandVoiceManager
        isOpen={brandVoiceManagerOpen}
        onClose={() => setBrandVoiceManagerOpen(false)}
        onSelectBrandVoice={(brandVoice) => {
          setCurrentBrandVoiceId(brandVoice.id);
        }}
        currentBrandVoiceId={currentBrandVoiceId}
      />

      {/* Iris Floating Panel */}
      <IrisFloatingPanel
        isOpen={irisOpen || irisMinimized}
        isMinimized={irisMinimized}
        onClose={() => { setIrisOpen(false); setIrisMinimized(false); }}
        onMinimize={() => setIrisMinimized(true)}
        onRestore={() => { setIrisMinimized(false); setIrisOpen(true); }}
        onAddFlow={handleIrisAddFlow}
        onUpdateNode={handleIrisUpdateNode}
        onAddNode={(nodeType) => {
          // Look up camelCase type in registry
          const entry = Object.values(NODE_REGISTRY).find(
            r => r.type === nodeType || r.camelCaseType === nodeType
          );
          const camelType = entry ? entry.camelCaseType : nodeType;
          handleNodePaletteSelect(camelType);
        }}
        canvasState={() => ({
          nodes: nodes.map(n => ({ id: n.id, type: n.type, data: n.data, selected: n.selected })),
          edges: edges.map(e => ({ source: e.source, target: e.target })),
        })}
      />

      {/* Send to Device Modal (for output node context menu) */}
      {sendToDeviceAsset && (
        <SendToDeviceModal
          asset={sendToDeviceAsset}
          onClose={() => setSendToDeviceAsset(null)}
        />
      )}

      {/* Toast Notification */}
      {toastData && (
        <Toast
          message={toastData.message}
          type={toastData.type}
          duration={toastData.duration}
          persona={toastData.persona}
          speakerName={toastData.speakerName}
          onClose={() => setToastData(null)}
        />
      )}

      {/* Confirm Dialog */}
      {confirmData && (
        <ConfirmDialog
          title={confirmData.title}
          message={confirmData.message}
          confirmText={confirmData.confirmText}
          cancelText={confirmData.cancelText}
          type={confirmData.type}
          onConfirm={() => {
            confirmData.callback(true);
            setConfirmData(null);
          }}
          onCancel={() => {
            confirmData.callback(false);
            setConfirmData(null);
          }}
        />
      )}

      {/* Prompt Limit Modal */}
      {promptLimitData && (
        <PromptLimitModal
          fullText={promptLimitData.fullText}
          limit={promptLimitData.limit}
          modelName={promptLimitData.modelName}
          contextChars={promptLimitData.contextChars}
          promptChars={promptLimitData.promptChars}
          onCondense={promptLimitData.onCondense}
          onResult={(result) => {
            promptLimitData.callback(result);
            setPromptLimitData(null);
          }}
        />
      )}

      {/* Update Required Modal - shown when app has been updated */}
      <UpdateRequiredModal
        open={updateRequired}
        onSaveAndRefresh={async () => {
          // Trigger auto-save, wait for it, then refresh
          await autoSave.triggerSave(true);
          window.location.reload();
        }}
        onRefreshOnly={() => {
          window.location.reload();
        }}
      />
    </div>
    </AutoSaveProvider>
  );
}

// Wrapper component that provides ReactFlow and Execution contexts
function CreativeNodeFlow() {
  return (
    <ReactFlowProvider>
      <ExecutionProvider>
        <TutorialProvider>
          <CreativeNodeFlowInner />
          <TutorialOverlay />
        </TutorialProvider>
      </ExecutionProvider>
    </ReactFlowProvider>
  );
}

export default React.memo(CreativeNodeFlow);
